import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getModuleTopbarColor } from './AppSidebar';
import {
    typesCourAPI, modalitesAPI, famillesOptionsAPI,
    coursAPI, coursVolumesAPI, programmeNationalAPI,
    enseignantsAPI, semestresConfigAPI, sousNiveauxAPI, importAPI,
    carryForwardAPI, ueAPI, parcoursPnAPI
} from '../services/api';
import ImportModal from './ImportModal';
import './Programme.css';

// ─── Couleurs statut conformité ───────────────────────────────
const STATUT_CONF = {
    conforme:    { bg: '#dcfce7', text: '#15803d', label: 'Conforme',    icon: 'fa-check-circle' },
    insuffisant: { bg: '#fef2f2', text: '#dc2626', label: 'Insuffisant', icon: 'fa-times-circle' },
    manquant:    { bg: '#fef9c3', text: '#854d0e', label: 'Manquant',    icon: 'fa-exclamation-triangle' },
};

// ─── Composant badge statut ───────────────────────────────────
function BadgeConf({ statut }) {
    const s = STATUT_CONF[statut] || STATUT_CONF.manquant;
    return (
        <span className="prog-badge" style={{ background: s.bg, color: s.text }}>
            <i className={`fa-solid ${s.icon}`} /> {s.label}
        </span>
    );
}

// ─── Modal générique liste de référentiel ─────────────────────
function ModalRef({ titre, item, champs, onSave, onClose, saving, error }) {
    const [form, setForm] = useState({});
    useEffect(() => {
        const init = {};
        champs.forEach(c => { init[c.name] = item ? (item[c.name] ?? c.default ?? '') : (c.default ?? ''); });
        setForm(init);
    }, [item]);
    const handleChange = e => {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    };
    return (
        <div className="prog-overlay" onClick={onClose}>
            <div className="prog-modal" onClick={e => e.stopPropagation()}>
                <div className="prog-modal-header">
                    <h3>{item ? `Modifier — ${titre}` : `Nouveau — ${titre}`}</h3>
                    <button className="prog-modal-close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
                </div>
                <div className="prog-modal-body">
                    {error && <div className="prog-alert prog-alert-error">{error}</div>}
                    {champs.map(c => (
                        <div className="prog-form-group" key={c.name}>
                            <label>{c.label}{c.required && ' *'}</label>
                            {c.type === 'textarea' ? (
                                <textarea name={c.name} value={form[c.name] || ''} onChange={handleChange}
                                    placeholder={c.placeholder || ''} rows={3} />
                            ) : c.type === 'color' ? (
                                <div className="prog-color-row">
                                    <input type="color" name={c.name} value={form[c.name] || '#6C757D'} onChange={handleChange} />
                                    <span>{form[c.name] || '#6C757D'}</span>
                                </div>
                            ) : c.type === 'select' ? (
                                <select name={c.name} value={form[c.name] ?? ''} onChange={handleChange}>
                                    <option value="">— Sélectionner —</option>
                                    {(c.options || []).map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            ) : (
                                <input type={c.type || 'text'} name={c.name} value={form[c.name] ?? ''}
                                    onChange={handleChange} placeholder={c.placeholder || ''} step={c.step} />
                            )}
                        </div>
                    ))}
                    <div className="prog-modal-actions">
                        <button className="prog-btn prog-btn-secondary" onClick={onClose}>Annuler</button>
                        <button className="prog-btn prog-btn-primary" disabled={saving}
                            onClick={() => onSave(form)}>
                            {saving ? <><i className="fa-solid fa-spinner fa-spin" /> Sauvegarde...</> : 'Enregistrer'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Modal cours ──────────────────────────────────────────────
function ModalCours({ cours, typesCour, semestres, famillesOptions, enseignants, departementId, anneeId, onSave, onClose }) {
    const VIDE = {
        type_cours_id: '', semestre_config_id: '', type_option: '', famille_option_id: '',
        code: '', nom: '', coefficient: '1', description: '',
        responsable_id: '', source: 'local', actif: true
    };
    const [form, setForm] = useState(VIDE);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (cours) {
            setForm({
                type_cours_id:      cours.type_cours_id || '',
                semestre_config_id: cours.semestre_config_id || '',
                type_option:        cours.type_option || '',
                famille_option_id:  cours.famille_option_id || '',
                code:               cours.code || '',
                nom:                cours.nom || '',
                coefficient:        cours.coefficient || '1',
                description:        cours.description || '',
                responsable_id:     cours.responsable_id || '',
                source:             cours.source || 'local',
                actif:              cours.actif !== false,
            });
        } else { setForm(VIDE); }
    }, [cours]);

    const handleChange = e => {
        const { name, value, type, checked } = e.target;
        if (name === 'type_option') {
            setForm(f => ({ ...f, type_option: value, famille_option_id: '' }));
        } else {
            setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
        }
    };

    const handleSubmit = async () => {
        if (!form.type_cours_id || !form.semestre_config_id || !form.code || !form.nom) {
            setError('Type, semestre, code et intitulé sont obligatoires.');
            return;
        }
        if (form.type_option === 'parallele' && !form.famille_option_id) {
            setError('Une famille d\'options est obligatoire pour un cours parallèle (Option⇄).');
            return;
        }
        setSaving(true); setError(null);
        try {
            const payload = {
                ...form,
                departement_id:     departementId,
                annee_scolaire_id:  anneeId,
                type_cours_id:      form.type_cours_id ? parseInt(form.type_cours_id) : null,
                semestre_config_id: parseInt(form.semestre_config_id),
                type_option:        form.type_option || null,
                famille_option_id:  form.famille_option_id ? parseInt(form.famille_option_id) : null,
                responsable_id:     form.responsable_id ? parseInt(form.responsable_id) : null,
                coefficient:        parseFloat(form.coefficient) || 1,
            };
            if (cours) { await coursAPI.update(cours.id, payload); }
            else        { await coursAPI.create(payload); }
            onSave();
        } catch (err) {
            setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
        } finally { setSaving(false); }
    };

    return (
        <div className="prog-overlay" onClick={onClose}>
            <div className="prog-modal prog-modal-lg" onClick={e => e.stopPropagation()}>
                <div className="prog-modal-header">
                    <h3>{cours ? 'Modifier le cours' : 'Nouveau cours'}</h3>
                    <button className="prog-modal-close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
                </div>
                <div className="prog-modal-body">
                    {error && <div className="prog-alert prog-alert-error">{error}</div>}
                    <div className="prog-form-row">
                        <div className="prog-form-group">
                            <label>Code *</label>
                            <input name="code" value={form.code} onChange={handleChange} placeholder="ex : R1.01" />
                        </div>
                        <div className="prog-form-group prog-form-group-lg">
                            <label>Intitulé *</label>
                            <input name="nom" value={form.nom} onChange={handleChange} placeholder="ex : Initiation au développement" />
                        </div>
                    </div>
                    <div className="prog-form-row">
                        <div className="prog-form-group">
                            <label>Catégorie de cours *</label>
                            <select name="type_cours_id" value={form.type_cours_id} onChange={handleChange}>
                                <option value="">— Sélectionner —</option>
                                {typesCour.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
                            </select>
                        </div>
                        <div className="prog-form-group">
                            <label>Semestre *</label>
                            <select name="semestre_config_id" value={form.semestre_config_id} onChange={handleChange}>
                                <option value="">— Sélectionner —</option>
                                {semestres.map(s => <option key={s.id} value={s.id}>{s.sous_niveau_code ? `${s.sous_niveau_code} — ${s.semestre}` : s.semestre}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="prog-form-row">
                        <div className="prog-form-group">
                            <label>Option</label>
                            <select name="type_option" value={form.type_option} onChange={handleChange}>
                                <option value="">— Cours normal —</option>
                                <option value="complementaire">Option+ (complémentaire)</option>
                                <option value="parallele">Option⇄ (parallèle — choisir une famille)</option>
                            </select>
                        </div>
                        {form.type_option === 'parallele' && (
                            <div className="prog-form-group">
                                <label>Famille d'options *</label>
                                <select name="famille_option_id" value={form.famille_option_id} onChange={handleChange}>
                                    <option value="">— Sélectionner une famille —</option>
                                    {famillesOptions.map(f => <option key={f.id} value={f.id}>{f.code} — {f.nom}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                    <div className="prog-form-row">
                        <div className="prog-form-group">
                            <label>Responsable</label>
                            <select name="responsable_id" value={form.responsable_id} onChange={handleChange}>
                                <option value="">— Aucun —</option>
                                {enseignants.map(e => <option key={e.id} value={e.id}>{e.prenom} {e.nom}</option>)}
                            </select>
                        </div>
                        <div className="prog-form-group">
                            <label>Coefficient</label>
                            <input type="number" name="coefficient" value={form.coefficient}
                                onChange={handleChange} step="0.5" min="0" />
                        </div>
                        <div className="prog-form-group">
                            <label>Source</label>
                            <select name="source" value={form.source} onChange={handleChange}>
                                <option value="local">Local (département)</option>
                                <option value="national_adapte">National adapté</option>
                            </select>
                        </div>
                    </div>
                    <div className="prog-form-group">
                        <label>Description</label>
                        <textarea name="description" value={form.description} onChange={handleChange} rows={2} />
                    </div>
                    {cours && (
                        <div className="prog-form-check">
                            <label>
                                <input type="checkbox" name="actif" checked={form.actif} onChange={handleChange} />
                                Cours actif
                            </label>
                        </div>
                    )}
                    <div className="prog-modal-actions">
                        <button className="prog-btn prog-btn-secondary" onClick={onClose}>Annuler</button>
                        <button className="prog-btn prog-btn-primary" disabled={saving} onClick={handleSubmit}>
                            {saving ? <><i className="fa-solid fa-spinner fa-spin" /> Sauvegarde...</> : 'Enregistrer'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Panel volumes + affectations d'un cours ─────────────────
function PanelVolumes({ cours, anneeId, modalites, enseignants, onClose }) {
    const [volumes, setVolumes]         = useState([]);
    const [affectations, setAffectations] = useState([]);
    const [loading, setLoading]         = useState(true);
    const [editVol, setEditVol]         = useState({});  // modalite_id → heures
    const [addAff, setAddAff]           = useState(null); // { modalite_id }
    const [formAff, setFormAff]         = useState({ enseignant_id: '', heures_affectees: '', groupe_label: '' });
    const [saving, setSaving]           = useState(false);

    const load = useCallback(async () => {
        if (!cours || !anneeId) return;
        setLoading(true);
        try {
            const [rv, ra] = await Promise.all([
                coursVolumesAPI.getVolumes(cours.id, anneeId),
                coursVolumesAPI.getAffectations(cours.id, anneeId),
            ]);
            setVolumes(rv.data.volumes || []);
            setAffectations(ra.data.affectations || []);
            // Initialiser les champs d'édition volumes
            const ev = {};
            (rv.data.volumes || []).forEach(v => { ev[v.modalite_id] = v.heures_prevues; });
            setEditVol(ev);
        } finally { setLoading(false); }
    }, [cours, anneeId]);

    useEffect(() => { load(); }, [load]);

    const saveVolume = async (modaliteId) => {
        const heures = parseFloat(editVol[modaliteId]) || 0;
        setSaving(true);
        try {
            await coursVolumesAPI.saveVolume({ cours_id: cours.id, annee_scolaire_id: anneeId, modalite_id: modaliteId, heures_prevues: heures });
            await load();
        } finally { setSaving(false); }
    };

    const deleteAffectation = async (id) => {
        await coursVolumesAPI.deleteAffectation(id);
        await load();
    };

    const submitAffectation = async () => {
        if (!formAff.enseignant_id || !addAff) return;
        setSaving(true);
        try {
            await coursVolumesAPI.addAffectation({
                annee_scolaire_id: anneeId,
                cours_id: cours.id,
                enseignant_id: parseInt(formAff.enseignant_id),
                modalite_id: addAff.modalite_id,
                heures_affectees: parseFloat(formAff.heures_affectees) || 0,
                groupe_label: formAff.groupe_label || null,
            });
            setAddAff(null);
            setFormAff({ enseignant_id: '', heures_affectees: '', groupe_label: '' });
            await load();
        } finally { setSaving(false); }
    };

    return (
        <div className="prog-overlay" onClick={onClose}>
            <div className="prog-modal prog-modal-xl" onClick={e => e.stopPropagation()}>
                <div className="prog-modal-header">
                    <h3>
                        <span className="prog-code-badge">{cours.code}</span>
                        {cours.nom}
                    </h3>
                    <button className="prog-modal-close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
                </div>
                <div className="prog-modal-body">
                    {loading ? (
                        <div className="prog-center"><i className="fa-solid fa-spinner fa-spin" /></div>
                    ) : (
                        <div className="prog-volumes-grid">
                            {modalites.map(m => {
                                const vol = volumes.find(v => v.modalite_id === m.id);
                                const affs = affectations.filter(a => a.modalite_id === m.id);
                                const totalAff = affs.reduce((s, a) => s + parseFloat(a.heures_affectees), 0);
                                const prevues = parseFloat(editVol[m.id] || 0);
                                const ok = Math.abs(totalAff - prevues) < 0.01;

                                return (
                                    <div key={m.id} className={`prog-vol-card ${affs.length === 0 && prevues === 0 ? 'prog-vol-empty' : ''}`}>
                                        <div className="prog-vol-header">
                                            <span className="prog-vol-code">{m.code}</span>
                                            <span className="prog-vol-coeff">× {m.coefficient_htd} hTD</span>
                                        </div>

                                        {/* Enveloppe */}
                                        <div className="prog-vol-enveloppe">
                                            <label>Enveloppe prévue (h)</label>
                                            <div className="prog-vol-input-row">
                                                <input type="number" step="0.5" min="0"
                                                    value={editVol[m.id] ?? ''}
                                                    onChange={e => setEditVol(ev => ({ ...ev, [m.id]: e.target.value }))}
                                                />
                                                <button className="prog-btn-xs prog-btn-primary"
                                                    onClick={() => saveVolume(m.id)} disabled={saving}>
                                                    <i className="fa-solid fa-floppy-disk" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Barre couverture */}
                                        {prevues > 0 && (
                                            <div className="prog-vol-barre">
                                                <div className="prog-vol-barre-fill"
                                                    style={{
                                                        width: `${Math.min(100, (totalAff / prevues) * 100)}%`,
                                                        background: ok ? '#16a34a' : totalAff > prevues ? '#dc2626' : '#f59e0b'
                                                    }} />
                                                <span className={`prog-vol-barre-label ${ok ? 'ok' : 'ko'}`}>
                                                    {totalAff}h / {prevues}h {ok ? '✓' : ''}
                                                </span>
                                            </div>
                                        )}

                                        {/* Affectations */}
                                        {affs.length > 0 && (
                                            <div className="prog-aff-list">
                                                {affs.map(a => (
                                                    <div key={a.id} className="prog-aff-row">
                                                        <span className="prog-aff-avatar">
                                                            {(a.enseignant_nom || '?')[0]}
                                                        </span>
                                                        <span className="prog-aff-nom">{a.enseignant_nom}</span>
                                                        <span className="prog-aff-h">{a.heures_affectees}h</span>
                                                        {a.groupe_label && (
                                                            <span className="prog-aff-groupe">{a.groupe_label}</span>
                                                        )}
                                                        <button className="prog-aff-del"
                                                            onClick={() => deleteAffectation(a.id)}
                                                            title="Retirer">
                                                            <i className="fa-solid fa-xmark" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Bouton ajouter affectation */}
                                        {addAff?.modalite_id === m.id ? (
                                            <div className="prog-aff-form">
                                                <select value={formAff.enseignant_id}
                                                    onChange={e => setFormAff(f => ({ ...f, enseignant_id: e.target.value }))}>
                                                    <option value="">— Enseignant —</option>
                                                    {enseignants.map(e => (
                                                        <option key={e.id} value={e.id}>{e.prenom} {e.nom}</option>
                                                    ))}
                                                </select>
                                                <input type="number" step="0.5" placeholder="heures"
                                                    value={formAff.heures_affectees}
                                                    onChange={e => setFormAff(f => ({ ...f, heures_affectees: e.target.value }))} />
                                                <input placeholder="Groupe (opt.)"
                                                    value={formAff.groupe_label}
                                                    onChange={e => setFormAff(f => ({ ...f, groupe_label: e.target.value }))} />
                                                <div className="prog-aff-form-btns">
                                                    <button className="prog-btn-xs prog-btn-primary"
                                                        onClick={submitAffectation} disabled={saving}>
                                                        <i className="fa-solid fa-check" />
                                                    </button>
                                                    <button className="prog-btn-xs prog-btn-secondary"
                                                        onClick={() => setAddAff(null)}>
                                                        <i className="fa-solid fa-xmark" />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button className="prog-aff-add"
                                                onClick={() => {
                                                    setAddAff({ modalite_id: m.id });
                                                    setFormAff({ enseignant_id: '', heures_affectees: '', groupe_label: '' });
                                                }}>
                                                <i className="fa-solid fa-plus" /> Affecter un enseignant
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Carte cours accordéon ────────────────────────────────────
function CoursCardAccordion({ cours: initialCours, anneeId, typesCour, semestres, famillesOptions, enseignants, modalites, onDelete, onPanelVolumes, onSaved }) {
    const [open, setOpen]               = useState(false);
    const [cours, setCours]             = useState(initialCours);
    const [form, setForm]               = useState({});
    const [volumes, setVolumes]         = useState([]);
    const [affectations, setAffectations] = useState([]);
    const [editVol, setEditVol]         = useState({});
    const [loaded, setLoaded]           = useState(false);
    const [loading, setLoading]         = useState(false);
    const [saving, setSaving]           = useState(false);
    const [error, setError]             = useState(null);
    const [success, setSuccess]         = useState(false);
    // Ajout affectation inline
    const [addAff, setAddAff]           = useState(null); // { modalite_id } ou null
    const [formAff, setFormAff]         = useState({ enseignant_id: '', heures_affectees: '', groupe_label: '' });
    const [savingAff, setSavingAff]     = useState(false);
    // Édition des heures d'une affectation existante
    const [editAffH, setEditAffH]       = useState({}); // { [affectation_id]: valeur string }

    useEffect(() => {
        setCours(initialCours);
        setForm({
            type_cours_id:      initialCours.type_cours_id      || '',
            semestre_config_id: initialCours.semestre_config_id || '',
            type_option:        initialCours.type_option        || '',
            famille_option_id:  initialCours.famille_option_id  || '',
            coefficient:        initialCours.coefficient        ?? 1,
            responsable_id:     initialCours.responsable_id     || '',
        });
        setLoaded(false);
    }, [initialCours.id]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [rv, ra] = await Promise.all([
                coursVolumesAPI.getVolumes(initialCours.id, anneeId),
                coursVolumesAPI.getAffectations(initialCours.id, anneeId),
            ]);
            const vols = rv.data.volumes || [];
            const affs = ra.data.affectations || [];
            setVolumes(vols);
            setAffectations(affs);
            // Mettre à jour cours.volumes pour que les jauges reflètent les données réelles
            setCours(c => ({ ...c, volumes: vols }));
            const ev = {};
            vols.forEach(v => { ev[v.modalite_id] = v.heures_prevues; });
            setEditVol(ev);
            const ah = {};
            affs.forEach(a => { ah[a.id] = String(a.heures_affectees); });
            setEditAffH(ah);
            setLoaded(true);
        } finally { setLoading(false); }
    }, [initialCours.id, anneeId]);

    const handleToggle = () => {
        const willOpen = !open;
        setOpen(willOpen);
        if (willOpen && !loaded) loadData();
    };

    const handleChange = e => {
        const { name, value } = e.target;
        if (name === 'type_option') {
            setForm(f => ({ ...f, type_option: value, famille_option_id: '' }));
        } else {
            setForm(f => ({ ...f, [name]: value }));
        }
    };

    const handleSave = async () => {
        setSaving(true); setError(null); setSuccess(false);
        try {
            await coursAPI.update(cours.id, {
                ...cours,
                type_cours_id:      form.type_cours_id      ? parseInt(form.type_cours_id)      : null,
                semestre_config_id: form.semestre_config_id ? parseInt(form.semestre_config_id) : null,
                type_option:        form.type_option        || null,
                famille_option_id:  form.famille_option_id  ? parseInt(form.famille_option_id)  : null,
                responsable_id:     form.responsable_id     ? parseInt(form.responsable_id)     : null,
                coefficient:        parseFloat(form.coefficient) || 1,
            });
            await Promise.all(modalites.map(m =>
                coursVolumesAPI.saveVolume({
                    cours_id:          cours.id,
                    annee_scolaire_id: anneeId,
                    modalite_id:       m.id,
                    heures_prevues:    parseFloat(editVol[m.id] || 0),
                })
            ));
            setLoaded(false);
            await loadData();
            setSuccess(true);
            setTimeout(() => setSuccess(false), 2500);
            onSaved();
        } catch (err) {
            setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
        } finally { setSaving(false); }
    };

    const affsByMod = (mId) => affectations.filter(a => a.modalite_id === mId);
    const totalAff  = (mId) => affsByMod(mId).reduce((s, a) => s + parseFloat(a.heures_affectees || 0), 0);

    const openAddAff = (modaliteId) => {
        setAddAff({ modalite_id: modaliteId });
        setFormAff({ enseignant_id: '', heures_affectees: '', groupe_label: '' });
    };

    const submitAffectation = async () => {
        if (!formAff.enseignant_id || !addAff) return;
        setSavingAff(true);
        try {
            await coursVolumesAPI.addAffectation({
                annee_scolaire_id: anneeId,
                cours_id:          cours.id,
                enseignant_id:     parseInt(formAff.enseignant_id),
                modalite_id:       addAff.modalite_id,
                heures_affectees:  parseFloat(formAff.heures_affectees) || 0,
                groupe_label:      formAff.groupe_label || null,
            });
            setAddAff(null);
            await loadData();
        } finally { setSavingAff(false); }
    };

    const deleteAffectation = async (id) => {
        await coursVolumesAPI.deleteAffectation(id);
        await loadData();
    };

    const saveAffectationH = async (id) => {
        const heures = parseFloat(editAffH[id]);
        if (isNaN(heures) || heures < 0) return;
        try {
            await coursVolumesAPI.updateAffectation(id, { heures_affectees: heures });
            await loadData();
        } catch { /* silencieux, la valeur sera rechargée */ }
    };

    const saveVolume = async (modaliteId) => {
        const heures = parseFloat(editVol[modaliteId] || 0);
        try {
            await coursVolumesAPI.saveVolume({
                cours_id:          cours.id,
                annee_scolaire_id: anneeId,
                modalite_id:       modaliteId,
                heures_prevues:    heures,
            });
            await loadData();
        } catch { /* silencieux */ }
    };

    return (
        <div className={`prog-accord-card ${!cours.actif ? 'prog-cours-inactif' : ''} ${open ? 'prog-accord-open' : ''}`}>

            {/* ── Header cliquable ── */}
            <div className="prog-accord-header" onClick={handleToggle}>
                <div className="prog-accord-left">
                    <i className={`fa-solid fa-chevron-right prog-accord-chevron ${open ? 'prog-accord-chevron-open' : ''}`} />
                    <span className="prog-cours-code"
                        style={{ background: (cours.type_cours_couleur || '#6C757D') + '22', color: cours.type_cours_couleur || '#6C757D' }}>
                        {cours.code}
                    </span>
                    <div className="prog-accord-info">
                        <div className="prog-accord-nom-row">
                            <span className="prog-cours-nom">{cours.nom}</span>
                            {cours.responsable_nom && (
                                <span className="prog-meta-tag prog-meta-resp">
                                    <i className="fa-solid fa-user-tie" /> {cours.responsable_nom}
                                </span>
                            )}
                        </div>
                        <div className="prog-cours-meta">
                            <span className="prog-meta-tag">{cours.type_cours_nom}</span>
                            {cours.type_option === 'complementaire' && (
                                <span className="prog-meta-tag prog-meta-option-plus">Option+</span>
                            )}
                            {cours.type_option === 'parallele' && (
                                <span className="prog-meta-tag prog-meta-option-par">
                                    Option⇄ {cours.famille_option_nom || cours.famille_option_code || ''}
                                </span>
                            )}
                        </div>
                        {/* Jauges par modalité + total */}
                        {(() => {
                            const vols = (cours.volumes || []).filter(v => parseFloat(v.heures_prevues) > 0);
                            if (vols.length === 0) return null;
                            const totalPrev = vols.reduce((s, v) => s + parseFloat(v.heures_prevues), 0);
                            const totalAff  = vols.reduce((s, v) => s + (parseFloat(v.heures_affectees) || 0), 0);
                            const gaugeColor = (pct) => pct > 100 ? '#7c3aed' : pct === 100 ? '#16a34a' : pct >= 50 ? '#f59e0b' : '#dc2626';
                            return (
                                <div className="prog-accord-gauges">
                                    {vols.map(v => {
                                        const pct = Math.round((v.heures_affectees || 0) / v.heures_prevues * 100);
                                        const color = gaugeColor(pct);
                                        return (
                                            <div key={v.modalite_id} className="prog-accord-gauge-item">
                                                <span className="prog-accord-gauge-label">{v.modalite_code}</span>
                                                <div className="prog-accord-gauge-bar">
                                                    <div className="prog-accord-gauge-fill"
                                                        style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
                                                </div>
                                                <span className="prog-accord-gauge-pct" style={{ color }}>{pct}%</span>
                                            </div>
                                        );
                                    })}
                                    <div className="prog-accord-gauge-item prog-accord-gauge-total">
                                        <span className="prog-accord-gauge-label">Total {totalPrev}h</span>
                                        <div className="prog-accord-gauge-bar">
                                            <div className="prog-accord-gauge-fill" style={{
                                                width: `${Math.min(totalPrev > 0 ? totalAff / totalPrev * 100 : 0, 100)}%`,
                                                background: gaugeColor(totalPrev > 0 ? Math.round(totalAff / totalPrev * 100) : 0)
                                            }} />
                                        </div>
                                        <span className="prog-accord-gauge-pct" style={{ color: gaugeColor(totalPrev > 0 ? Math.round(totalAff / totalPrev * 100) : 0) }}>
                                            {totalPrev > 0 ? Math.round(totalAff / totalPrev * 100) : 0}%
                                        </span>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
                <div className="prog-accord-actions" onClick={e => e.stopPropagation()}>
                    <button className="prog-action-btn prog-action-volumes"
                        title="Gérer les affectations enseignants"
                        onClick={() => onPanelVolumes(cours)}>
                        <i className="fa-solid fa-clock" />
                    </button>
                    <button className="prog-action-btn prog-action-delete"
                        title="Supprimer ce cours"
                        onClick={() => onDelete(cours)}>
                        <i className="fa-solid fa-trash" />
                    </button>
                </div>
            </div>

            {/* ── Corps accordéon ── */}
            {open && (
                <div className="prog-accord-body">
                    {loading ? (
                        <div className="prog-center"><i className="fa-solid fa-spinner fa-spin prog-spinner" /></div>
                    ) : (
                        <>
                            {error   && <div className="prog-alert prog-alert-error">{error}</div>}
                            {success && <div className="prog-alert prog-alert-success"><i className="fa-solid fa-check-circle" /> Modifications enregistrées</div>}

                            {/* ── Champs du cours ── */}
                            <div className="prog-accord-fields">
                                <div className="prog-form-group">
                                    <label>Catégorie de cours</label>
                                    <select name="type_cours_id" value={form.type_cours_id} onChange={handleChange}>
                                        <option value="">— Sélectionner —</option>
                                        {typesCour.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
                                    </select>
                                </div>
                                <div className="prog-form-group">
                                    <label>Semestre</label>
                                    <select name="semestre_config_id" value={form.semestre_config_id} onChange={handleChange}>
                                        <option value="">— Sélectionner —</option>
                                        {semestres.map(s => <option key={s.id} value={s.id}>{s.sous_niveau_code ? `${s.sous_niveau_code} — ${s.semestre}` : s.semestre}</option>)}
                                    </select>
                                </div>
                                <div className="prog-form-group">
                                    <label>Option</label>
                                    <select name="type_option" value={form.type_option} onChange={handleChange}>
                                        <option value="">— Cours normal —</option>
                                        <option value="complementaire">Option+</option>
                                        <option value="parallele">Option⇄ (parallèle)</option>
                                    </select>
                                </div>
                                {form.type_option === 'parallele' && (
                                    <div className="prog-form-group">
                                        <label>Famille d'options</label>
                                        <select name="famille_option_id" value={form.famille_option_id} onChange={handleChange}>
                                            <option value="">— Sélectionner —</option>
                                            {famillesOptions.map(f => <option key={f.id} value={f.id}>{f.code} — {f.nom}</option>)}
                                        </select>
                                    </div>
                                )}
                                <div className="prog-form-group">
                                    <label>Coefficient</label>
                                    <input type="number" name="coefficient" value={form.coefficient}
                                        onChange={handleChange} step="0.5" min="0"
                                        className="prog-accord-coeff-input" />
                                </div>
                                <div className="prog-form-group">
                                    <label>Responsable</label>
                                    <select name="responsable_id" value={form.responsable_id} onChange={handleChange}>
                                        <option value="">— Aucun —</option>
                                        {enseignants.map(e => <option key={e.id} value={e.id}>{e.prenom} {e.nom}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* ── Volumes + enseignants ── */}
                            <div className="prog-accord-volumes">
                                <div className="prog-accord-vol-title">
                                    <i className="fa-solid fa-chart-bar" /> Volumes horaires &amp; enseignants par modalité
                                </div>
                                <div className="prog-accord-vol-grid">
                                    {modalites.map(m => {
                                        const prevues  = parseFloat(editVol[m.id] || 0);
                                        const total    = totalAff(m.id);
                                        const pct      = prevues > 0 ? Math.min(100, (total / prevues) * 100) : 0;
                                        const ok       = prevues > 0 && Math.abs(total - prevues) < 0.01;
                                        const over     = total > prevues && prevues > 0;
                                        const affsMod  = affsByMod(m.id);
                                        const showForm = addAff?.modalite_id === m.id;

                                        return (
                                            <div key={m.id} className={`prog-accord-vol-item ${prevues === 0 && affsMod.length === 0 ? 'prog-accord-vol-empty' : ''}`}>

                                                {/* Enveloppe + code modalité */}
                                                <div className="prog-accord-vol-header">
                                                    <span className="prog-vol-code">{m.code}</span>
                                                    <span className="prog-vol-coeff">× {m.coefficient_htd} hTD</span>
                                                    <div className="prog-accord-vol-input-wrap">
                                                        <input type="number" step="0.5" min="0"
                                                            value={editVol[m.id] ?? ''}
                                                            onChange={e => setEditVol(ev => ({ ...ev, [m.id]: e.target.value }))}
                                                            onBlur={() => saveVolume(m.id)}
                                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saveVolume(m.id); } }}
                                                            placeholder="0"
                                                        />
                                                        <span className="prog-accord-vol-unit">h</span>
                                                    </div>
                                                </div>

                                                {/* Barre de couverture */}
                                                {prevues > 0 && (
                                                    <div className="prog-vol-barre">
                                                        <div className="prog-vol-barre-fill" style={{
                                                            width: `${pct}%`,
                                                            background: ok ? '#16a34a' : over ? '#dc2626' : '#f59e0b'
                                                        }} />
                                                        <span className={`prog-vol-barre-label ${ok ? 'ok' : 'ko'}`}>
                                                            {total}h / {prevues}h {ok ? '✓' : ''}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Liste des enseignants affectés */}
                                                {affsMod.length > 0 && (
                                                    <div className="prog-aff-list">
                                                        {affsMod.map(a => (
                                                            <div key={a.id} className="prog-aff-row">
                                                                <span className="prog-aff-avatar">
                                                                    {(a.enseignant_nom || '?')[0]}
                                                                </span>
                                                                <span className="prog-aff-nom">{a.enseignant_nom}</span>
                                                                <input
                                                                    className="prog-aff-h-input"
                                                                    type="number"
                                                                    step="0.5" min="0"
                                                                    value={editAffH[a.id] ?? a.heures_affectees}
                                                                    onChange={e => setEditAffH(h => ({ ...h, [a.id]: e.target.value }))}
                                                                    onBlur={() => saveAffectationH(a.id)}
                                                                    onKeyDown={e => { if (e.key === 'Enter') { e.target.blur(); } }}
                                                                    title="Modifier les heures (Entrée ou clic ailleurs pour valider)"
                                                                />
                                                                <span className="prog-aff-h-unit">h</span>
                                                                {a.groupe_label && (
                                                                    <span className="prog-aff-groupe">{a.groupe_label}</span>
                                                                )}
                                                                <button className="prog-aff-del"
                                                                    title="Retirer cet enseignant"
                                                                    onClick={() => deleteAffectation(a.id)}>
                                                                    <i className="fa-solid fa-xmark" />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {affsMod.length === 0 && !showForm && (
                                                    <div className="prog-accord-no-aff">
                                                        {prevues > 0 ? 'Aucun enseignant affecté' : '—'}
                                                    </div>
                                                )}

                                                {/* Mini-formulaire ajout affectation */}
                                                {showForm ? (
                                                    <div className="prog-accord-aff-form">
                                                        <select
                                                            value={formAff.enseignant_id}
                                                            onChange={e => setFormAff(f => ({ ...f, enseignant_id: e.target.value }))}>
                                                            <option value="">— Enseignant —</option>
                                                            {enseignants.map(e => (
                                                                <option key={e.id} value={e.id}>{e.prenom} {e.nom}</option>
                                                            ))}
                                                        </select>
                                                        <div className="prog-accord-aff-row2">
                                                            <input type="number" step="0.5" min="0"
                                                                placeholder="Heures"
                                                                value={formAff.heures_affectees}
                                                                onChange={e => setFormAff(f => ({ ...f, heures_affectees: e.target.value }))} />
                                                            <input type="text"
                                                                placeholder="Groupe (opt.)"
                                                                value={formAff.groupe_label}
                                                                onChange={e => setFormAff(f => ({ ...f, groupe_label: e.target.value }))} />
                                                        </div>
                                                        <div className="prog-accord-aff-btns">
                                                            <button className="prog-btn-xs prog-btn-primary"
                                                                onClick={submitAffectation}
                                                                disabled={savingAff || !formAff.enseignant_id}>
                                                                {savingAff
                                                                    ? <i className="fa-solid fa-spinner fa-spin" />
                                                                    : <><i className="fa-solid fa-check" /> Valider</>}
                                                            </button>
                                                            <button className="prog-btn-xs prog-btn-secondary"
                                                                onClick={() => setAddAff(null)}>
                                                                <i className="fa-solid fa-xmark" /> Annuler
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button className="prog-accord-aff-add"
                                                        onClick={() => openAddAff(m.id)}>
                                                        <i className="fa-solid fa-user-plus" /> Affecter un enseignant
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="prog-accord-save-row">
                                <button className="prog-btn prog-btn-primary" onClick={handleSave} disabled={saving}>
                                    {saving
                                        ? <><i className="fa-solid fa-spinner fa-spin" /> Sauvegarde en cours...</>
                                        : <><i className="fa-solid fa-floppy-disk" /> Enregistrer tout</>}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Modal Récupérer depuis N-1 ───────────────────────────────
function ModalCarryForward({ anneeId, annees, deptId, sousNiveaux, onClose, onDone }) {
    const [sourceAnneeId, setSourceAnneeId] = useState('');
    const [sousNiveauId,  setSousNiveauId]  = useState('');
    const [loading,   setLoading]   = useState(false);
    const [items,     setItems]     = useState([]);
    const [checkVols, setCheckVols] = useState({});
    const [checkAffs, setCheckAffs] = useState({});
    const [saving,    setSaving]    = useState(false);
    const [result,    setResult]    = useState(null);
    const [error,     setError]     = useState(null);

    const otherAnnees = (annees || []).filter(a => a.id !== anneeId)
        .sort((a, b) => b.id - a.id);

    const loadPreview = async () => {
        if (!sourceAnneeId) return;
        setLoading(true); setError(null); setItems([]);
        try {
            const res = await carryForwardAPI.preview({
                deptId,
                sourceAnneeId,
                targetAnneeId: anneeId,
                ...(sousNiveauId ? { sousNiveauId } : {}),
            });
            const data = res.data.cours || [];
            setItems(data);
            const vols = {}, affs = {};
            data.forEach(c => {
                vols[c.id] = true;
                affs[c.id] = c.source_affectations.length > 0;
            });
            setCheckVols(vols);
            setCheckAffs(affs);
        } catch (err) {
            setError(err.response?.data?.error || 'Erreur de chargement');
        } finally { setLoading(false); }
    };

    const handleExecute = async () => {
        const toProcess = items
            .filter(c => checkVols[c.id] || checkAffs[c.id])
            .map(c => ({
                coursId:          c.id,
                copyVolumes:      !!checkVols[c.id],
                copyAffectations: !!checkAffs[c.id],
            }));
        if (toProcess.length === 0) return;
        setSaving(true); setError(null);
        try {
            const res = await carryForwardAPI.execute({
                sourceAnneeId: parseInt(sourceAnneeId),
                targetAnneeId: anneeId,
                deptId,
                copyEnseignants: false,
                copyPN: false,
                items: toProcess,
            });
            setResult(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Erreur lors de la récupération');
        } finally { setSaving(false); }
    };

    const toggleAllVols = () => {
        const allOn = items.length > 0 && items.every(c => checkVols[c.id]);
        const n = {};
        items.forEach(c => { n[c.id] = !allOn; });
        setCheckVols(n);
    };

    const nbSelected = items.filter(c => checkVols[c.id] || checkAffs[c.id]).length;
    const sourceLabel = (annees || []).find(a => String(a.id) === String(sourceAnneeId))?.libelle;
    const targetLabel = (annees || []).find(a => a.id === anneeId)?.libelle;

    return (
        <div className="prog-overlay" onClick={onClose}>
            <div className="prog-modal prog-modal-xl prog-cf-modal" onClick={e => e.stopPropagation()}>
                <div className="prog-modal-header">
                    <h3><i className="fa-solid fa-clock-rotate-left" style={{ marginRight: 8 }} />Récupérer depuis une année précédente</h3>
                    <button className="prog-modal-close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
                </div>
                <div className="prog-modal-body">
                    {error && <div className="prog-alert prog-alert-error">{error}</div>}

                    {result ? (
                        /* ── Résultat ── */
                        <div>
                            <div className="prog-cf-result">
                                <i className="fa-solid fa-circle-check prog-cf-result-icon" />
                                <div className="prog-cf-result-text">
                                    <div className="prog-cf-result-title">Récupération terminée</div>
                                    <div className="prog-cf-result-sub">
                                        {result.copied} cours traité{result.copied > 1 ? 's' : ''}
                                        {sourceLabel && targetLabel && ` — de ${sourceLabel} vers ${targetLabel}`}
                                    </div>
                                    {result.errors?.length > 0 && (
                                        <div className="prog-cf-result-errors">
                                            {result.errors.length} erreur{result.errors.length > 1 ? 's' : ''} :{' '}
                                            {result.errors.slice(0, 3).join(' · ')}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="prog-modal-actions">
                                <button className="prog-btn prog-btn-primary"
                                    onClick={() => { onDone(); onClose(); }}>
                                    <i className="fa-solid fa-check" /> Fermer et rafraîchir
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* ── Sélecteurs contexte ── */}
                            <div className="prog-cf-ctx">
                                <div className="prog-cf-ctx-row">
                                    <label className="prog-cf-ctx-label">Année source</label>
                                    <select className="prog-cf-ctx-select"
                                        value={sourceAnneeId}
                                        onChange={e => { setSourceAnneeId(e.target.value); setItems([]); }}>
                                        <option value="">— Choisir l'année source —</option>
                                        {otherAnnees.map(a => (
                                            <option key={a.id} value={a.id}>{a.libelle}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="prog-cf-ctx-row">
                                    <label className="prog-cf-ctx-label">Sous-groupe</label>
                                    <select className="prog-cf-ctx-select"
                                        value={sousNiveauId}
                                        onChange={e => { setSousNiveauId(e.target.value); setItems([]); }}>
                                        <option value="">Tous les sous-groupes</option>
                                        {(sousNiveaux || []).map(sn => (
                                            <option key={sn.id} value={sn.id}>{sn.code} — {sn.nom}</option>
                                        ))}
                                    </select>
                                </div>
                                <button className="prog-btn prog-btn-primary"
                                    disabled={!sourceAnneeId || loading}
                                    onClick={loadPreview}>
                                    {loading
                                        ? <><i className="fa-solid fa-spinner fa-spin" /> Chargement...</>
                                        : <><i className="fa-solid fa-magnifying-glass" /> Charger</>}
                                </button>
                            </div>

                            {/* Bandeau destination */}
                            {anneeId && targetLabel && (
                                <div className="prog-cf-dest">
                                    <i className="fa-solid fa-arrow-right" />
                                    <span>Destination : <strong>{targetLabel}</strong> (année active)</span>
                                </div>
                            )}

                            {/* ── Liste des cours ── */}
                            {items.length > 0 && (
                                <>
                                    <div className="prog-cf-list-header">
                                        <label className="prog-cf-check-all" onClick={e => e.stopPropagation()}>
                                            <input type="checkbox"
                                                checked={items.length > 0 && items.every(c => checkVols[c.id])}
                                                onChange={toggleAllVols} />
                                            <span>Tout sélectionner les volumes</span>
                                        </label>
                                        <span className="prog-cf-count">{items.length} cours trouvés</span>
                                    </div>

                                    <div className="prog-cf-table-wrap">
                                        <table className="prog-cf-table">
                                            <thead>
                                                <tr>
                                                    <th>Cours</th>
                                                    <th className="prog-cf-th-vols">Volumes (heures N-1)</th>
                                                    <th className="prog-cf-th-affs">Affectations enseignants</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map(c => {
                                                    const rowOff = !checkVols[c.id] && !checkAffs[c.id];
                                                    return (
                                                        <tr key={c.id} className={rowOff ? 'prog-cf-row-off' : ''}>
                                                            <td>
                                                                <label className="prog-cf-cours-label">
                                                                    <input type="checkbox"
                                                                        checked={!!(checkVols[c.id] || checkAffs[c.id])}
                                                                        onChange={e => {
                                                                            const v = e.target.checked;
                                                                            setCheckVols(prev => ({ ...prev, [c.id]: v }));
                                                                            setCheckAffs(prev => ({ ...prev, [c.id]: v && c.source_affectations.length > 0 }));
                                                                        }} />
                                                                    <span className="prog-cf-cours-info">
                                                                        <code className="prog-code-inline">{c.code}</code>
                                                                        <span className="prog-cf-nom">{c.nom}</span>
                                                                        {c.semestre_label && (
                                                                            <span className="prog-meta-tag">{c.sous_niveau_code ? `${c.sous_niveau_code} ${c.semestre_label}` : c.semestre_label}</span>
                                                                        )}
                                                                    </span>
                                                                </label>
                                                            </td>
                                                            <td>
                                                                {c.source_volumes.length > 0 ? (
                                                                    <label className="prog-cf-vol-label">
                                                                        <input type="checkbox"
                                                                            checked={!!checkVols[c.id]}
                                                                            onChange={e => setCheckVols(prev => ({ ...prev, [c.id]: e.target.checked }))} />
                                                                        <span className="prog-cf-vols">
                                                                            {c.source_volumes.map(v => (
                                                                                <span key={v.modalite_code} className="prog-cf-vol-chip">
                                                                                    {v.modalite_code} <strong>{v.heures_prevues}h</strong>
                                                                                </span>
                                                                            ))}
                                                                            {c.target_has_volumes && (
                                                                                <span className="prog-cf-warn" title="Des volumes existent déjà — ils seront écrasés">
                                                                                    <i className="fa-solid fa-triangle-exclamation" /> écrase
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    </label>
                                                                ) : (
                                                                    <span className="prog-empty-cell">—</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {c.source_affectations.length > 0 ? (
                                                                    <label className="prog-cf-aff-label">
                                                                        <input type="checkbox"
                                                                            checked={!!checkAffs[c.id]}
                                                                            onChange={e => setCheckAffs(prev => ({ ...prev, [c.id]: e.target.checked }))} />
                                                                        <span className="prog-cf-affs">
                                                                            {c.source_affectations.slice(0, 3).map((a, i) => (
                                                                                <span key={i} className="prog-cf-aff-chip">
                                                                                    {a.enseignant_nom} · {a.heures_affectees}h {a.modalite_code}
                                                                                </span>
                                                                            ))}
                                                                            {c.source_affectations.length > 3 && (
                                                                                <span className="prog-cf-more">+{c.source_affectations.length - 3}</span>
                                                                            )}
                                                                            {c.target_has_affectations && (
                                                                                <span className="prog-cf-warn" title="Des affectations existent déjà — elles seront remplacées">
                                                                                    <i className="fa-solid fa-triangle-exclamation" /> remplace
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    </label>
                                                                ) : (
                                                                    <span className="prog-empty-cell">—</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="prog-modal-actions">
                                        <button className="prog-btn prog-btn-secondary" onClick={onClose}>Annuler</button>
                                        <button className="prog-btn prog-btn-primary"
                                            disabled={saving || nbSelected === 0}
                                            onClick={handleExecute}>
                                            {saving
                                                ? <><i className="fa-solid fa-spinner fa-spin" /> Récupération...</>
                                                : <><i className="fa-solid fa-clock-rotate-left" /> Récupérer ({nbSelected} cours)</>}
                                        </button>
                                    </div>
                                </>
                            )}

                            {!loading && items.length === 0 && sourceAnneeId && (
                                <div className="prog-empty">
                                    <i className="fa-solid fa-box-open" />
                                    <p>Aucune donnée trouvée pour les critères sélectionnés.<br />
                                    <small>Essayez sans filtre de sous-groupe.</small></p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Onglet Cours ─────────────────────────────────────────────
// ── Jauges agrégées par semestre (inline dans le titre) ───────────────────
function SemestreGauges({ items, modalites }) {
    const agg = {};
    items.forEach(c => {
        (c.volumes || []).forEach(v => {
            if (!agg[v.modalite_code]) agg[v.modalite_code] = { prevues: 0, affectees: 0 };
            agg[v.modalite_code].prevues   += parseFloat(v.heures_prevues  || 0);
            agg[v.modalite_code].affectees += parseFloat(v.heures_affectees || 0);
        });
    });
    const active = (modalites || []).filter(m => (agg[m.code]?.prevues || 0) > 0);
    if (active.length === 0) return null;
    const totalPrev = active.reduce((s, m) => s + (agg[m.code]?.prevues   || 0), 0);
    const totalAff  = active.reduce((s, m) => s + (agg[m.code]?.affectees || 0), 0);
    const pct = (aff, prev) => prev > 0 ? Math.min(Math.round((aff / prev) * 100), 999) : 0;
    const clr = (p) => p > 100 ? '#7c3aed' : p === 100 ? '#16a34a' : p >= 50 ? '#f59e0b' : '#ef4444';
    const pTotal = pct(totalAff, totalPrev);
    return (
        <div className="prog-sg-inline">
            {active.map(m => {
                const a = agg[m.code]; const p = pct(a.affectees, a.prevues);
                return (
                    <span key={m.code} className="prog-sg-inline-item">
                        <span className="prog-sg-inline-code">{m.code}</span>
                        <div className="prog-sg-inline-bar"><div style={{ width: `${Math.min(p,100)}%`, background: clr(p), height: '100%', borderRadius: 3 }} /></div>
                        <span className="prog-sg-inline-pct" style={{ color: clr(p) }}>{p}%</span>
                    </span>
                );
            })}
            <span className="prog-sg-inline-sep" />
            <span className="prog-sg-inline-item">
                <span className="prog-sg-inline-code" style={{ fontWeight: 800 }}>Tot</span>
                <div className="prog-sg-inline-bar"><div style={{ width: `${Math.min(pTotal,100)}%`, background: clr(pTotal), height: '100%', borderRadius: 3 }} /></div>
                <span className="prog-sg-inline-pct" style={{ color: clr(pTotal), fontWeight: 800 }}>{pTotal}%</span>
            </span>
        </div>
    );
}

// ─── ResizableSemBody — colonnes redimensionnables dans "Tout afficher" ─
function ResizableSemBody({ cols }) {
    const containerRef = useRef(null);
    const widthsRef    = useRef(null);
    const storageKey   = `prog_col_widths_${cols.length}`;

    const defaultW = () => Array(cols.length).fill(100 / cols.length);

    const [widths, setWidths] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
            if (Array.isArray(saved) && saved.length === cols.length) return saved;
        } catch {}
        return defaultW();
    });

    widthsRef.current = widths;

    useEffect(() => {
        setWidths(prev => prev.length === cols.length ? prev : defaultW());
    }, [cols.length]); // eslint-disable-line

    const handleMouseDown = useCallback((e, idx) => {
        e.preventDefault();
        const startX     = e.clientX;
        const startW     = [...widthsRef.current];
        document.body.style.userSelect = 'none';
        document.body.style.cursor     = 'col-resize';

        const onMove = (e) => {
            if (!containerRef.current) return;
            const pct   = ((e.clientX - startX) / containerRef.current.offsetWidth) * 100;
            const minW  = 10;
            const left  = Math.max(minW, startW[idx]     + pct);
            const right = Math.max(minW, startW[idx + 1] - pct);
            const newW  = [...startW];
            newW[idx]     = left;
            newW[idx + 1] = right;
            setWidths(newW);
        };
        const onUp = () => {
            document.body.style.userSelect = '';
            document.body.style.cursor     = '';
            localStorage.setItem(storageKey, JSON.stringify(widthsRef.current));
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup',   onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup',   onUp);
    }, [storageKey]);

    const eff = widths.length === cols.length ? widths : defaultW();

    return (
        <div ref={containerRef} className="prog-tout-sem-body">
            {cols.map((col, i) => (
                <React.Fragment key={col.key}>
                    <div className={`prog-tout-col ${col.className}`}
                         style={{ flex: `0 0 ${eff[i]}%`, width: `${eff[i]}%`, minWidth: 0, boxSizing: 'border-box' }}>
                        {col.content}
                    </div>
                    {i < cols.length - 1 && (
                        <div className="prog-resize-divider" onMouseDown={e => handleMouseDown(e, i)} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
}

// ─── FiltreDropdowns partagée (formation + semestre en cascade) ─
function FiltreDropdowns({ sousNiveaux, semestres, filtSems, setFiltSems }) {
    // Construire snList : [{ id, code, semIds:[], semsList:[] }]
    const snList = sousNiveaux.map(sn => {
        const semsList = semestres.filter(s => s.sous_niveau_id === sn.id);
        return { id: sn.id, code: sn.code, semIds: semsList.map(s => s.id), semsList };
    }).filter(sn => sn.semIds.length > 0);

    if (snList.length === 0) return null;

    // Dériver l'état sélectionné depuis filtSems
    let selectedSn  = 'all';
    let selectedSem = 'all';
    if (filtSems.size === 1) {
        const semId = [...filtSems][0];
        const sn = snList.find(s => s.semIds.includes(semId));
        if (sn) { selectedSn = String(sn.id); selectedSem = String(semId); }
    } else if (filtSems.size > 1) {
        const sn = snList.find(s => s.semIds.length === filtSems.size && s.semIds.every(id => filtSems.has(id)));
        if (sn) selectedSn = String(sn.id);
    }

    const availableSems = selectedSn === 'all'
        ? semestres
        : (snList.find(s => String(s.id) === selectedSn)?.semsList || []);

    const handleSnChange = (val) => {
        if (val === 'all') { setFiltSems(new Set()); return; }
        const sn = snList.find(s => String(s.id) === val);
        if (sn) setFiltSems(new Set(sn.semIds));
    };

    const handleSemChange = (val) => {
        if (val === 'all') {
            if (selectedSn === 'all') { setFiltSems(new Set()); return; }
            const sn = snList.find(s => String(s.id) === selectedSn);
            if (sn) setFiltSems(new Set(sn.semIds));
        } else {
            setFiltSems(new Set([parseInt(val)]));
        }
    };

    return (
        <div className="filtre-dropdowns">
            <select className="filtre-dd-select" value={selectedSn} onChange={e => handleSnChange(e.target.value)}>
                <option value="all">Toutes les formations</option>
                {snList.map(sn => <option key={sn.id} value={String(sn.id)}>{sn.code}</option>)}
            </select>
            <select className="filtre-dd-select" value={selectedSem} onChange={e => handleSemChange(e.target.value)}>
                <option value="all">Tous les semestres</option>
                {availableSems.map(s => <option key={s.id} value={String(s.id)}>{s.semestre}</option>)}
            </select>
            {filtSems.size > 0 && (
                <button className="prog-btn prog-btn-secondary prog-btn-sm filtre-dd-reset"
                    onClick={() => setFiltSems(new Set())} title="Tout afficher">
                    <i className="fa-solid fa-xmark" />
                </button>
            )}
        </div>
    );
}

function OngletCours({ config, anneeId, annees, sousNiveaux, semestres, typesCour, modalites, famillesOptions, enseignants, onFamillesChange, filtSems = new Set(), setFiltSems, sousOnglet = 'cours', setSousOnglet }) {
    const [cours, setCours]       = useState([]);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);
    const [search, setSearch]     = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [coursEdit, setCoursEdit] = useState(null);
    const [panelCours, setPanelCours] = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    // Familles d'options — gestion inline
    const [famillesOpen, setFamillesOpen]   = useState(false);
    const [familleForm, setFamilleForm]     = useState({ code: '', nom: '', description: '' });
    const [familleEdit, setFamilleEdit]     = useState(null); // id en cours d'édition
    const [familleSaving, setFamilleSaving] = useState(false);
    // Drag state
    const [dragSnIdx, setDragSnIdx]       = useState(null);
    const [dragSemKey, setDragSemKey]     = useState(null);
    const [dragCoursKey, setDragCoursKey] = useState(null);
    // Sélection + repli
    const [selected, setSelected]           = useState(new Set());
    const [collapsedSems, setCollapsedSems] = useState(new Set());

    const toggleSelect = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const selectAllSem = (items) => {
        const ids = items.map(c => c.id);
        setSelected(s => {
            const n = new Set(s);
            const allOn = ids.every(id => n.has(id));
            if (allOn) ids.forEach(id => n.delete(id));
            else ids.forEach(id => n.add(id));
            return n;
        });
    };
    const toggleCollapse = (semKey) => setCollapsedSems(s => { const n = new Set(s); n.has(semKey) ? n.delete(semKey) : n.add(semKey); return n; });
    const handleBulkDelete = async () => {
        const ids = [...selected];
        if (!window.confirm(`Supprimer ${ids.length} cours sélectionné${ids.length > 1 ? 's' : ''} ? Cette action est irréversible.`)) return;
        await Promise.all(ids.map(id => coursAPI.delete(id, anneeId)));
        setSelected(new Set());
        load();
    };

    const deptId = config?.departement?.id;

    const load = useCallback(async () => {
        if (!deptId) return;
        setLoading(true); setError(null);
        try {
            const res = await coursAPI.getAll(deptId, { anneeId });
            setCours(res.data.cours || []);
        } catch { setError('Impossible de charger les cours.'); }
        finally { setLoading(false); }
    }, [deptId, anneeId]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (c) => {
        // Récupérer les affectations pour informer l'utilisateur
        let msg = `Supprimer "${c.code} — ${c.nom}" ?\n\nCette action est irréversible.`;
        try {
            if (anneeId) {
                const ra = await coursVolumesAPI.getAffectations(c.id, anneeId);
                const affs = ra.data.affectations || [];
                if (affs.length > 0) {
                    const detail = affs.map(a =>
                        `  • ${a.enseignant_nom} ${a.enseignant_prenom} : ${a.heures_affectees}h ${a.modalite_code}`
                    ).join('\n');
                    msg = `⚠️ "${c.code} — ${c.nom}" a des affectations enregistrées :\n\n${detail}\n\nSupprimer supprimera ces heures du calcul de service.\n\nConfirmer la suppression ?`;
                }
            }
        } catch { /* si erreur de chargement, on affiche quand même la confirmation simple */ }

        if (!window.confirm(msg)) return;
        await coursAPI.delete(c.id, anneeId);
        load();
    };

    const handleFamilleSave = async () => {
        if (!familleForm.code || !familleForm.nom) return;
        setFamilleSaving(true);
        try {
            if (familleEdit) {
                await famillesOptionsAPI.update(familleEdit, { ...familleForm, departement_id: deptId, annee_scolaire_id: anneeId });
            } else {
                await famillesOptionsAPI.create({ ...familleForm, departement_id: deptId, annee_scolaire_id: anneeId });
            }
            setFamilleForm({ code: '', nom: '', description: '' });
            setFamilleEdit(null);
            onFamillesChange();
        } catch (e) {
            alert(e.response?.data?.error || 'Erreur sauvegarde famille');
        } finally {
            setFamilleSaving(false);
        }
    };

    const handleFamilleDelete = async (id) => {
        if (!window.confirm('Supprimer cette famille d\'options ? Les cours liés doivent être dissociés au préalable.')) return;
        try {
            await famillesOptionsAPI.delete(id, anneeId);
            onFamillesChange();
        } catch (e) {
            alert(e.response?.data?.error || 'Erreur suppression famille');
        }
    };

    const coursActifs = cours.filter(c => c.actif !== false);
    const hasPlus  = coursActifs.some(c => c.type_option === 'complementaire');
    const hasPar   = coursActifs.some(c => c.type_option === 'parallele');

    // ── Sous-onglet Cours locaux ──
    const liste = coursActifs.filter(c => {
        if (c.type_option) return false; // exclure options
        const q = search.toLowerCase();
        const matchQ = !q || c.code.toLowerCase().includes(q) || c.nom.toLowerCase().includes(q);
        const matchS = filtSems.size === 0 || filtSems.has(c.semestre_config_id);
        return matchQ && matchS;
    });

    // ── Sous-onglet Option+ ──
    const listePlus = coursActifs.filter(c => c.type_option === 'complementaire');

    // ── Sous-onglet Option⇄ ──
    const listePar  = coursActifs.filter(c => c.type_option === 'parallele');

    // Helper : grouper par sous-niveau → semestre
    const groupBySN = (items) => {
        const map = {};
        items.forEach(c => {
            const snKey  = c.sous_niveau_id ? `${c.sous_niveau_code}||${c.sous_niveau_id}` : 'zz_sans||0';
            const semKey = c.semestre_label || 'Sans semestre';
            if (!map[snKey]) map[snKey] = { label: c.sous_niveau_code ? `${c.sous_niveau_code} — ${c.sous_niveau_nom}` : 'Sans sous-niveau', semId: {}, semestres: {} };
            if (!map[snKey].semestres[semKey]) { map[snKey].semestres[semKey] = []; map[snKey].semId[semKey] = c.semestre_config_id; }
            map[snKey].semestres[semKey].push(c);
        });
        return Object.entries(map).sort();
    };

    const snEntries     = groupBySN(liste);
    const snEntriesPlus = groupBySN(listePlus);
    const snEntriesPar  = groupBySN(listePar);
    const snEntriesAll  = groupBySN(coursActifs);

    return (
        <div className="prog-tab-content">
            <div className="prog-toolbar">
                <div className="prog-search-wrap">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input className="prog-search" placeholder="Rechercher par code ou intitulé..."
                        value={search} onChange={e => setSearch(e.target.value)} />
                    {search && <button className="prog-search-clear" onClick={() => setSearch('')}><i className="fa-solid fa-xmark" /></button>}
                </div>
                <FiltreDropdowns sousNiveaux={sousNiveaux} semestres={semestres} filtSems={filtSems} setFiltSems={setFiltSems} />
                <button className={`prog-btn ${famillesOpen ? 'prog-btn-primary' : 'prog-btn-secondary'}`}
                    onClick={() => setFamillesOpen(o => !o)}
                    title="Gérer les familles d'options parallèles">
                    <i className="fa-solid fa-code-branch" /> Familles d'options
                    {famillesOptions.length > 0 && <span className="prog-badge-count">{famillesOptions.length}</span>}
                </button>
                <button className="prog-btn prog-btn-secondary"
                    onClick={() => setImportOpen(true)}>
                    <i className="fa-solid fa-file-import" /> Importer
                </button>
                <button className="prog-btn prog-btn-primary"
                    onClick={() => { setCoursEdit(null); setModalOpen(true); }}>
                    <i className="fa-solid fa-plus" /> Ajouter un cours
                </button>
                {selected.size > 0 && (
                    <button className="prog-btn prog-btn-danger" onClick={handleBulkDelete}>
                        <i className="fa-solid fa-trash" /> Supprimer la sélection ({selected.size})
                    </button>
                )}
            </div>

            {/* ── Sous-onglets ── */}
            <div className="prog-cours-subtabs">
                <button className={`prog-subtab${sousOnglet === 'cours' ? ' active' : ''}`}
                    onClick={() => setSousOnglet('cours')}>
                    <i className="fa-solid fa-book" /> Cours locaux
                    <span className="prog-subtab-count">{coursActifs.filter(c => !c.type_option).length}</span>
                </button>
                {hasPlus && (
                    <button className={`prog-subtab prog-subtab-plus${sousOnglet === 'option_plus' ? ' active' : ''}`}
                        onClick={() => setSousOnglet('option_plus')}>
                        <i className="fa-solid fa-plus-circle" /> Option+
                        <span className="prog-subtab-count">{listePlus.length}</span>
                    </button>
                )}
                {hasPar && (
                    <button className={`prog-subtab prog-subtab-par${sousOnglet === 'option_par' ? ' active' : ''}`}
                        onClick={() => setSousOnglet('option_par')}>
                        <i className="fa-solid fa-arrows-left-right" /> Option⇄
                        <span className="prog-subtab-count">{listePar.length}</span>
                    </button>
                )}
                {(hasPlus || hasPar) && (
                    <button className={`prog-subtab prog-subtab-tout${sousOnglet === 'tout' ? ' active' : ''}`}
                        onClick={() => setSousOnglet('tout')}>
                        <i className="fa-solid fa-table" /> Tout afficher
                        <span className="prog-subtab-count">{coursActifs.length}</span>
                    </button>
                )}
            </div>

            {/* ── Panel familles d'options ── */}
            {famillesOpen && (
                <div className="prog-familles-panel">
                    <div className="prog-familles-panel-header">
                        <i className="fa-solid fa-code-branch" /> <strong>Familles d'options parallèles</strong>
                        <span className="prog-familles-panel-hint">
                            Un cours Option⇄ appartient à une famille — l'étudiant choisit un seul cours de la famille.
                        </span>
                    </div>
                    <div className="prog-familles-list">
                        {famillesOptions.length === 0 && (
                            <div className="prog-familles-empty">Aucune famille définie.</div>
                        )}
                        {famillesOptions.map(f => (
                            <div key={f.id} className={`prog-famille-row${familleEdit === f.id ? ' prog-famille-editing' : ''}`}>
                                {familleEdit === f.id ? (
                                    <>
                                        <input className="prog-famille-input-code" placeholder="Code"
                                            value={familleForm.code}
                                            onChange={e => setFamilleForm(v => ({ ...v, code: e.target.value }))} />
                                        <input className="prog-famille-input-nom" placeholder="Nom"
                                            value={familleForm.nom}
                                            onChange={e => setFamilleForm(v => ({ ...v, nom: e.target.value }))} />
                                        <input className="prog-famille-input-desc" placeholder="Description (optionnel)"
                                            value={familleForm.description}
                                            onChange={e => setFamilleForm(v => ({ ...v, description: e.target.value }))} />
                                        <button className="prog-btn prog-btn-primary prog-btn-sm" disabled={familleSaving} onClick={handleFamilleSave}>
                                            {familleSaving ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-check" />}
                                        </button>
                                        <button className="prog-btn prog-btn-secondary prog-btn-sm" onClick={() => { setFamilleEdit(null); setFamilleForm({ code: '', nom: '', description: '' }); }}>
                                            <i className="fa-solid fa-xmark" />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <span className="prog-famille-code">{f.code}</span>
                                        <span className="prog-famille-nom">{f.nom}</span>
                                        {f.description && <span className="prog-famille-desc">{f.description}</span>}
                                        <button className="prog-btn prog-btn-secondary prog-btn-sm" title="Modifier"
                                            onClick={() => { setFamilleEdit(f.id); setFamilleForm({ code: f.code, nom: f.nom, description: f.description || '' }); }}>
                                            <i className="fa-solid fa-pencil" />
                                        </button>
                                        <button className="prog-btn prog-btn-danger prog-btn-sm" title="Supprimer"
                                            onClick={() => handleFamilleDelete(f.id)}>
                                            <i className="fa-solid fa-trash" />
                                        </button>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                    {/* Formulaire nouvelle famille */}
                    {familleEdit === null && (
                        <div className="prog-famille-new">
                            <input className="prog-famille-input-code" placeholder="Code *"
                                value={familleForm.code}
                                onChange={e => setFamilleForm(v => ({ ...v, code: e.target.value }))} />
                            <input className="prog-famille-input-nom" placeholder="Nom *"
                                value={familleForm.nom}
                                onChange={e => setFamilleForm(v => ({ ...v, nom: e.target.value }))} />
                            <input className="prog-famille-input-desc" placeholder="Description (optionnel)"
                                value={familleForm.description}
                                onChange={e => setFamilleForm(v => ({ ...v, description: e.target.value }))} />
                            <button className="prog-btn prog-btn-primary prog-btn-sm" disabled={familleSaving || !familleForm.code || !familleForm.nom}
                                onClick={handleFamilleSave}>
                                {familleSaving ? <><i className="fa-solid fa-spinner fa-spin" /> Ajout...</> : <><i className="fa-solid fa-plus" /> Ajouter</>}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {loading ? (
                <div className="prog-center"><i className="fa-solid fa-spinner fa-spin prog-spinner" /><p>Chargement...</p></div>
            ) : error ? (
                <div className="prog-alert prog-alert-error">{error}</div>

            // ── Sous-onglet Option+ ──
            ) : sousOnglet === 'option_plus' ? (
                listePlus.length === 0 ? (
                    <div className="prog-empty"><i className="fa-solid fa-plus-circle" /><p>Aucun cours Option+.</p></div>
                ) : snEntriesPlus.map(([snKey, snData]) => (
                    <div key={snKey} className="prog-sn-block">
                        <div className="prog-sn-title"><i className="fa-solid fa-graduation-cap" /> {snData.label}</div>
                        <div className="prog-sem-cols">
                            {Object.entries(snData.semestres).sort().map(([sem, items]) => (
                                <div key={sem} className="prog-semestre-col">
                                    <div className="prog-semestre-title">
                                        <i className="fa-solid fa-layer-group" /> {sem}
                                        <span className="prog-semestre-count">{items.length}</span>
                                    </div>
                                    <div className="prog-cours-list">
                                        {items.map(c => (
                                            <CoursCardAccordion key={c.id} cours={c} anneeId={anneeId} typesCour={typesCour}
                                                semestres={semestres} famillesOptions={famillesOptions}
                                                enseignants={enseignants} modalites={modalites}
                                                onDelete={handleDelete} onPanelVolumes={setPanelCours} onSaved={load} />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))

            // ── Sous-onglet Option⇄ ──
            ) : sousOnglet === 'option_par' ? (
                listePar.length === 0 ? (
                    <div className="prog-empty"><i className="fa-solid fa-arrows-left-right" /><p>Aucun cours Option⇄.</p></div>
                ) : snEntriesPar.map(([snKey, snData]) => (
                    <div key={snKey} className="prog-sn-block">
                        <div className="prog-sn-title"><i className="fa-solid fa-graduation-cap" /> {snData.label}</div>
                        {Object.entries(snData.semestres).sort().map(([sem, items]) => {
                            const famMap = {};
                            items.forEach(c => {
                                if (!famMap[c.famille_option_id]) famMap[c.famille_option_id] = { id: c.famille_option_id, nom: c.famille_option_nom || c.famille_option_code || '?', cours: [] };
                                famMap[c.famille_option_id].cours.push(c);
                            });
                            const famList = Object.values(famMap);
                            famList.forEach(f => f.cours.sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0)));
                            const maxL = famList.reduce((max, f) => Math.max(max, f.cours.length), 0);
                            return (
                                <div key={sem} className="prog-par-sem-block">
                                    <div className="prog-par-sem-title">
                                        <i className="fa-solid fa-layer-group" /> {sem}
                                        <span className="prog-semestre-count">{famList.length} famille{famList.length > 1 ? 's' : ''} · {maxL} ligne{maxL > 1 ? 's' : ''}</span>
                                    </div>
                                    <div className="prog-parallele-grid">
                                        {famList.map(f => (
                                            <div key={f.id} className="prog-parallele-col">
                                                <div className="prog-parallele-col-header">
                                                    <span className="prog-meta-tag prog-meta-option-par">{f.nom}</span>
                                                </div>
                                                {Array.from({ length: maxL }, (_, li) => {
                                                    const c = f.cours[li];
                                                    return (
                                                        <div key={li} className="prog-parallele-ligne">
                                                            <span className="prog-par-ligne-num">Groupe option {li + 1}</span>
                                                            {c ? (
                                                                <CoursCardAccordion cours={c} anneeId={anneeId} typesCour={typesCour}
                                                                    semestres={semestres} famillesOptions={famillesOptions}
                                                                    enseignants={enseignants} modalites={modalites}
                                                                    onDelete={handleDelete} onPanelVolumes={setPanelCours} onSaved={load} />
                                                            ) : (
                                                                <div className="prog-parallele-empty-slot">
                                                                    <button className="prog-btn prog-btn-secondary prog-btn-sm" onClick={() => { setCoursEdit(null); setModalOpen(true); }}>
                                                                        <i className="fa-solid fa-plus" /> Ajouter
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))

            // ── Sous-onglet Tout afficher ──
            ) : sousOnglet === 'tout' ? (
                coursActifs.length === 0 ? (
                    <div className="prog-empty"><i className="fa-solid fa-book-open" /><p>Aucun cours.</p></div>
                ) : snEntriesAll.map(([snKey, snData]) => (
                    <div key={snKey} className="prog-sn-block">
                        <div className="prog-sn-title"><i className="fa-solid fa-graduation-cap" /> {snData.label}</div>
                        {Object.entries(snData.semestres).sort().map(([sem, items]) => {
                            const normaux = items.filter(c => !c.type_option);
                            const plus    = items.filter(c => c.type_option === 'complementaire');
                            const par     = items.filter(c => c.type_option === 'parallele');
                            const famMap  = {};
                            par.forEach(c => {
                                if (!famMap[c.famille_option_id]) famMap[c.famille_option_id] = { id: c.famille_option_id, nom: c.famille_option_nom || c.famille_option_code || '?', cours: [] };
                                famMap[c.famille_option_id].cours.push(c);
                            });
                            const famList = Object.values(famMap);
                            famList.forEach(f => f.cours.sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0)));
                            const maxL = famList.reduce((max, f) => Math.max(max, f.cours.length), 0);
                            return (
                                <div key={sem} className="prog-tout-sem-block">
                                    <div className="prog-tout-sem-title">
                                        <i className="fa-solid fa-layer-group" /> {sem}
                                        <span className="prog-semestre-count">{items.length} cours</span>
                                    </div>
                                    <ResizableSemBody cols={[
                                        normaux.length > 0 ? {
                                            key: 'normal', className: 'prog-tout-col-normal',
                                            content: <>
                                                <div className="prog-tout-col-title">
                                                    <i className="fa-solid fa-book" /> Cours locaux
                                                    <span className="prog-subtab-count">{normaux.length}</span>
                                                </div>
                                                <div className="prog-cours-list">
                                                    {normaux.map(c => (
                                                        <CoursCardAccordion key={c.id} cours={c} anneeId={anneeId} typesCour={typesCour}
                                                            semestres={semestres} famillesOptions={famillesOptions}
                                                            enseignants={enseignants} modalites={modalites}
                                                            onDelete={handleDelete} onPanelVolumes={setPanelCours} onSaved={load} />
                                                    ))}
                                                </div>
                                            </>
                                        } : null,
                                        plus.length > 0 ? {
                                            key: 'plus', className: 'prog-tout-col-plus',
                                            content: <>
                                                <div className="prog-tout-col-title">
                                                    <i className="fa-solid fa-plus-circle" /> Option+
                                                    <span className="prog-subtab-count">{plus.length}</span>
                                                </div>
                                                <div className="prog-cours-list">
                                                    {plus.map(c => (
                                                        <CoursCardAccordion key={c.id} cours={c} anneeId={anneeId} typesCour={typesCour}
                                                            semestres={semestres} famillesOptions={famillesOptions}
                                                            enseignants={enseignants} modalites={modalites}
                                                            onDelete={handleDelete} onPanelVolumes={setPanelCours} onSaved={load} />
                                                    ))}
                                                </div>
                                            </>
                                        } : null,
                                        famList.length > 0 ? {
                                            key: 'par', className: 'prog-tout-col-par',
                                            content: <>
                                                <div className="prog-tout-col-title">
                                                    <i className="fa-solid fa-arrows-left-right" /> Option⇄
                                                    <span className="prog-subtab-count">{par.length}</span>
                                                </div>
                                                <div className="prog-parallele-grid prog-parallele-grid-tout">
                                                    {famList.map(f => (
                                                        <div key={f.id} className="prog-parallele-col">
                                                            <div className="prog-parallele-col-header">
                                                                <span className="prog-meta-tag prog-meta-option-par">{f.nom}</span>
                                                            </div>
                                                            {Array.from({ length: maxL }, (_, li) => {
                                                                const c = f.cours[li];
                                                                return (
                                                                    <div key={li} className="prog-parallele-ligne">
                                                                        <span className="prog-par-ligne-num">Groupe option {li + 1}</span>
                                                                        {c ? (
                                                                            <CoursCardAccordion cours={c} anneeId={anneeId} typesCour={typesCour}
                                                                                semestres={semestres} famillesOptions={famillesOptions}
                                                                                enseignants={enseignants} modalites={modalites}
                                                                                onDelete={handleDelete} onPanelVolumes={setPanelCours} onSaved={load} />
                                                                        ) : (
                                                                            <div className="prog-parallele-empty-slot">
                                                                                <button className="prog-btn prog-btn-secondary prog-btn-sm" onClick={() => { setCoursEdit(null); setModalOpen(true); }}>
                                                                                    <i className="fa-solid fa-plus" /> Ajouter
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        } : null,
                                    ].filter(Boolean)} />
                                </div>
                            );
                        })}
                    </div>
                ))

            // ── Sous-onglet Cours locaux (défaut) ──
            ) : liste.length === 0 ? (
                <div className="prog-empty">
                    <i className="fa-solid fa-book-open" />
                    <p>{search ? 'Aucun résultat.' : 'Aucun cours enregistré.'}</p>
                    {!search && (
                        <button className="prog-btn prog-btn-primary"
                            onClick={() => { setCoursEdit(null); setModalOpen(true); }}>
                            <i className="fa-solid fa-plus" /> Ajouter le premier cours
                        </button>
                    )}
                </div>
            ) : (
                snEntries.map(([snKey, snData], snIdx) => {
                    const semEntries = Object.entries(snData.semestres).sort();
                    const snDragging = dragSnIdx === snIdx;

                    const handleSnDrop = async (e) => {
                        e.preventDefault();
                        if (dragSnIdx === null || dragSnIdx === snIdx) { setDragSnIdx(null); return; }
                        const newEntries = [...snEntries];
                        const [removed] = newEntries.splice(dragSnIdx, 1);
                        newEntries.splice(snIdx, 0, removed);
                        const ordre = newEntries.map(([k], i) => ({ id: parseInt(k.split('||')[1]), ordre: i }));
                        setDragSnIdx(null);
                        await sousNiveauxAPI.reorder(ordre);
                        load();
                    };

                    return (
                        <div key={snKey}
                            className={`prog-sn-block${snDragging ? ' prog-drag-active' : ''}`}
                            draggable
                            onDragStart={e => { setDragSnIdx(snIdx); e.dataTransfer.effectAllowed = 'move'; }}
                            onDragOver={e => e.preventDefault()}
                            onDragEnd={() => setDragSnIdx(null)}
                            onDrop={handleSnDrop}
                        >
                            <div className="prog-sn-title">
                                <i className="fa-solid fa-grip-vertical prog-drag-grip" title="Glisser pour réordonner" />
                                <i className="fa-solid fa-graduation-cap" /> {snData.label}
                                <span className="prog-semestre-count">
                                    {Object.values(snData.semestres).reduce((s, a) => s + a.length, 0)} cours
                                </span>
                            </div>
                            <div className="prog-sem-cols">
                            {semEntries.map(([sem, items], semIdx) => {
                                const semKey     = `${snKey}||${sem}`;
                                const semDragging = dragSemKey?.snKey === snKey && dragSemKey?.semIdx === semIdx;
                                const isCollapsed = collapsedSems.has(semKey);
                                const allChecked  = items.length > 0 && items.every(c => selected.has(c.id));

                                const handleSemDrop = async (e) => {
                                    e.preventDefault(); e.stopPropagation();
                                    if (!dragSemKey || dragSemKey.snKey !== snKey) { setDragSemKey(null); return; }
                                    const fromIdx = dragSemKey.semIdx;
                                    if (fromIdx === semIdx) { setDragSemKey(null); return; }
                                    const newSemEntries = [...semEntries];
                                    const [removed] = newSemEntries.splice(fromIdx, 1);
                                    newSemEntries.splice(semIdx, 0, removed);
                                    const ordre = newSemEntries.map(([, its], i) => ({ id: its[0].semestre_config_id, ordre: i }));
                                    setDragSemKey(null);
                                    await semestresConfigAPI.reorder(ordre);
                                    load();
                                };

                                return (
                                    <div key={sem}
                                        className={`prog-semestre-col${semDragging ? ' prog-drag-active' : ''}`}
                                        draggable
                                        onDragStart={e => { e.stopPropagation(); setDragSemKey({ snKey, semIdx }); e.dataTransfer.effectAllowed = 'move'; }}
                                        onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                        onDragEnd={e => { e.stopPropagation(); setDragSemKey(null); }}
                                        onDrop={handleSemDrop}
                                    >
                                        <div className="prog-semestre-title">
                                            <i className="fa-solid fa-grip-vertical prog-drag-grip" title="Glisser pour réordonner" />
                                            <button className="prog-sem-collapse-btn" onClick={e => { e.stopPropagation(); toggleCollapse(semKey); }} title={isCollapsed ? 'Déplier' : 'Replier'}>
                                                <i className={`fa-solid fa-chevron-${isCollapsed ? 'right' : 'down'}`} />
                                            </button>
                                            <i className="fa-solid fa-layer-group" /> {sem}
                                            <SemestreGauges items={items} modalites={modalites} />
                                            <span className="prog-semestre-count">{items.length}</span>
                                        </div>
                                        {!isCollapsed && (
                                            <>
                                                <div className="prog-sem-select-bar">
                                                    <label>
                                                        <input type="checkbox" checked={allChecked} onChange={() => selectAllSem(items)} />
                                                        <span>Tout sélectionner</span>
                                                    </label>
                                                </div>
                                                {(() => {
                                                    const itemsNorm = items.filter(c => c.type_option !== 'parallele');
                                                    const itemsPar  = items.filter(c => c.type_option === 'parallele');
                                                    // Grouper par famille, trier par ordre → lignes
                                                    const famillesMap = {};
                                                    itemsPar.forEach(c => {
                                                        const fid = c.famille_option_id;
                                                        if (!famillesMap[fid]) famillesMap[fid] = { id: fid, nom: c.famille_option_nom || c.famille_option_code || '?', cours: [] };
                                                        famillesMap[fid].cours.push(c);
                                                    });
                                                    const famillesList = Object.values(famillesMap);
                                                    famillesList.forEach(f => f.cours.sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0)));
                                                    const maxLignes = famillesList.reduce((max, f) => Math.max(max, f.cours.length), 0);

                                                    const renderCard = (c, cIdx, itemsForDrag) => {
                                                        const coursDragging = dragCoursKey?.semId === c.semestre_config_id && dragCoursKey?.coursIdx === cIdx;
                                                        const handleCoursDrop = async (e) => {
                                                            e.preventDefault(); e.stopPropagation();
                                                            if (!dragCoursKey || dragCoursKey.semId !== c.semestre_config_id) { setDragCoursKey(null); return; }
                                                            const fromIdx = dragCoursKey.coursIdx;
                                                            if (fromIdx === cIdx) { setDragCoursKey(null); return; }
                                                            const newItems = [...itemsForDrag];
                                                            const [removed] = newItems.splice(fromIdx, 1);
                                                            newItems.splice(cIdx, 0, removed);
                                                            const ordre = newItems.map((cr, i) => ({ id: cr.id, ordre: i }));
                                                            setDragCoursKey(null);
                                                            await coursAPI.reorder(ordre);
                                                            load();
                                                        };
                                                        return (
                                                            <div key={c.id} className="prog-cours-check-wrap">
                                                                <input type="checkbox" className="prog-cours-check"
                                                                    checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)}
                                                                    onClick={e => e.stopPropagation()} />
                                                                <div className={`prog-cours-drag-wrap${coursDragging ? ' prog-drag-active' : ''}`}
                                                                    draggable
                                                                    onDragStart={e => { e.stopPropagation(); setDragCoursKey({ semId: c.semestre_config_id, coursIdx: cIdx }); e.dataTransfer.effectAllowed = 'move'; }}
                                                                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                                                    onDragEnd={e => { e.stopPropagation(); setDragCoursKey(null); }}
                                                                    onDrop={handleCoursDrop}>
                                                                    <CoursCardAccordion cours={c} anneeId={anneeId} typesCour={typesCour}
                                                                        semestres={semestres} famillesOptions={famillesOptions}
                                                                        enseignants={enseignants} modalites={modalites}
                                                                        onDelete={handleDelete} onPanelVolumes={setPanelCours} onSaved={load} />
                                                                </div>
                                                            </div>
                                                        );
                                                    };

                                                    return (
                                                        <>
                                                            {/* ── Cours normaux + Option+ ── */}
                                                            {itemsNorm.length > 0 && (
                                                                <div className="prog-cours-list">
                                                                    {itemsNorm.map((c, cIdx) => renderCard(c, cIdx, itemsNorm))}
                                                                </div>
                                                            )}
                                                            {/* ── Bloc Option⇄ : familles en colonnes ── */}
                                                            {famillesList.length > 0 && (
                                                                <div className="prog-parallele-bloc">
                                                                    <div className="prog-parallele-titre">
                                                                        <i className="fa-solid fa-arrows-left-right" /> Option⇄ — Choisir une famille
                                                                        <span className="prog-parallele-hint">{famillesList.length} famille{famillesList.length > 1 ? 's' : ''} · {maxLignes} groupe option{maxLignes > 1 ? 's' : ''}</span>
                                                                    </div>
                                                                    <div className="prog-parallele-grid">
                                                                        {famillesList.map(f => (
                                                                            <div key={f.id} className="prog-parallele-col">
                                                                                <div className="prog-parallele-col-header">
                                                                                    <span className="prog-meta-tag prog-meta-option-par">{f.nom}</span>
                                                                                </div>
                                                                                {Array.from({ length: maxLignes }, (_, ligneIdx) => {
                                                                                    const c = f.cours[ligneIdx];
                                                                                    return (
                                                                                        <div key={ligneIdx} className="prog-parallele-ligne">
                                                                                            {c ? renderCard(c, ligneIdx, f.cours) : (
                                                                                                <div className="prog-parallele-empty-slot">
                                                                                                    <button className="prog-btn prog-btn-secondary prog-btn-sm"
                                                                                                        onClick={() => { setCoursEdit(null); setModalOpen(true); }}>
                                                                                                        <i className="fa-solid fa-plus" /> Ajouter
                                                                                                    </button>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                            </div>
                        </div>
                    );
                })
            )}

            {modalOpen && (
                <ModalCours
                    cours={coursEdit}
                    typesCour={typesCour}
                    semestres={semestres}
                    famillesOptions={famillesOptions}
                    enseignants={enseignants}
                    departementId={deptId}
                    anneeId={anneeId}
                    onSave={() => { setModalOpen(false); load(); }}
                    onClose={() => setModalOpen(false)}
                />
            )}

            {panelCours && (
                <PanelVolumes
                    cours={panelCours}
                    anneeId={anneeId}
                    modalites={modalites}
                    enseignants={enseignants}
                    onClose={() => setPanelCours(null)}
                />
            )}


            {importOpen && (
                <ImportModal
                    titre="Cours"
                    contextFields={[{
                        key:      'semestre_config_id',
                        label:    'Semestre',
                        required: true,
                        options:  semestres.map(s => ({
                            value: s.id,
                            label: `${s.sous_niveau_code} — ${s.semestre}`
                        }))
                    }]}
                    champs={[
                        { key: 'code',        label: 'Code',                   required: true },
                        { key: 'nom',         label: 'Intitulé',               required: true },
                        { key: 'type_cours',  label: 'Catégorie (code ou nom)' },
                        { key: 'famille_option', label: 'Famille option (code)'  },
                        { key: 'type_option',    label: 'Type option (complementaire/parallele)' },
                        { key: 'coefficient', label: 'Coefficient'             },
                        { key: 'description', label: 'Description'             },
                        ...modalites.map(m => ({ key: `vol_${m.code}`, label: `Heures ${m.code}` })),
                    ]}
                    onPreview={async (rows) => {
                        // Clé = code + semestre_config_id → même code dans un autre semestre = nouveau cours
                        const existingMap = {};
                        cours.forEach(c => {
                            const key = `${c.code}||${c.semestre_config_id}`;
                            existingMap[key] = c;
                        });
                        return rows.map(row => {
                            const key = `${row.code}||${row.semestre_config_id}`;
                            const existing = existingMap[key];
                            if (!existing) return { status: 'new', row };
                            const changes = [];
                            if (row.nom && row.nom !== existing.nom)
                                changes.push({ key: 'nom', label: 'Intitulé', old: existing.nom, new: row.nom });
                            if (row.coefficient && parseFloat(row.coefficient) !== parseFloat(existing.coefficient))
                                changes.push({ key: 'coefficient', label: 'Coeff', old: existing.coefficient, new: row.coefficient });
                            modalites.forEach(m => {
                                const colKey = `vol_${m.code}`;
                                if (row[colKey] !== undefined && row[colKey] !== '') {
                                    const existingVol = (existing.volumes || []).find(v => v.modalite_code === m.code);
                                    const oldH = existingVol ? String(existingVol.heures_prevues) : '—';
                                    if (String(row[colKey]) !== oldH)
                                        changes.push({ key: colKey, label: `H. ${m.code}`, old: oldH, new: row[colKey] });
                                }
                            });
                            if (changes.length === 0) return { status: 'identical', row, existing };
                            return { status: 'modified', row, existing, changes };
                        });
                    }}
                    onImport={async (rows) => {
                        const res = await importAPI.importer('cours', deptId, anneeId, rows);
                        load();
                        return res.data;
                    }}
                    onClose={() => setImportOpen(false)}
                />
            )}
        </div>
    );
}

// ─── ConformiteCard v7 — drop zone colonne, affectations, création cours ──────
function ConformiteCard({ pnItem, localCours, modalites, enseignants, typesCour, semestres, famillesOptions = [], departementId, anneeId, onRefresh, onDrop, onDragStartLinked }) {
    const [dragOver, setDragOver]         = useState(false);
    const [expandedId, setExpandedId]     = useState(null);
    const [volsMap, setVolsMap]           = useState({});
    const [affsMap, setAffsMap]           = useState({});
    const [editVolMap, setEditVolMap]     = useState({});
    const [formMap, setFormMap]           = useState({});
    const [addAffMap, setAddAffMap]       = useState({});   // { coursId: { modalite_id } | null }
    const [formAffMap, setFormAffMap]     = useState({});   // { coursId: { enseignant_id, heures_affectees, groupe_label } }
    const [editAffHMap, setEditAffHMap]   = useState({});   // { coursId: { affId: value } }
    const [showCreate, setShowCreate]     = useState(false);
    const [createForm, setCreateForm]     = useState({ code: '', nom: '', type_cours_id: '', type_option: '', famille_option_id: '' });
    const [createError, setCreateError]   = useState(null);
    const [savingCreate, setSavingCreate]   = useState(false);
    const [pendingExpand, setPendingExpand] = useState(null);
    const loadedRef                         = React.useRef(new Set());

    const totalMin    = pnItem.modalites.reduce((s, m) => s + (m.heures_min || 0), 0);
    const totalLocal  = pnItem.modalites.reduce((s, m) => s + (m.heures_prevues || 0), 0);
    // Somme de toutes les modalités de tous les cours locaux associés (via volsMap)
    const totalAllVols = localCours.reduce((s, c) =>
        s + Object.values(volsMap[c.id] || {}).reduce((ss, h) => ss + (parseFloat(h) || 0), 0), 0);
    const totalDisplay = totalAllVols > 0 ? totalAllVols : totalLocal;
    const statusColor = pnItem.statut_global === 'conforme' ? '#16a34a'
                      : pnItem.statut_global === 'insuffisant' ? '#f59e0b' : '#dc2626';

    // ── Chargement volumes + affectations ──
    const loadCours = useCallback(async (cours) => {
        if (loadedRef.current.has(cours.id)) return;
        loadedRef.current.add(cours.id);
        try {
            const [rv, ra] = await Promise.all([
                coursVolumesAPI.getVolumes(cours.id, anneeId),
                coursVolumesAPI.getAffectations(cours.id, anneeId),
            ]);
            const volumes = rv.data.volumes || [];
            const byMod = {};
            volumes.forEach(v => { byMod[v.modalite_id] = v.heures_prevues; });
            setVolsMap(prev => ({ ...prev, [cours.id]: byMod }));
            setAffsMap(prev => ({ ...prev, [cours.id]: ra.data.affectations || [] }));
            const ev = {};
            volumes.forEach(v => { ev[v.modalite_id] = v.heures_prevues; });
            setEditVolMap(prev => ({ ...prev, [cours.id]: ev }));
        } catch { loadedRef.current.delete(cours.id); }
    }, [anneeId]);

    const reloadCours = async (cours) => {
        loadedRef.current.delete(cours.id);
        await loadCours(cours);
        onRefresh();
    };

    const localCoursKey = localCours.map(c => c.id).join(',');
    useEffect(() => { localCours.forEach(c => loadCours(c)); }, [localCoursKey]); // eslint-disable-line
    useEffect(() => {
        if (pendingExpand) {
            const c = localCours.find(lc => lc.id === pendingExpand);
            if (c) { toggleExpand(c); setPendingExpand(null); }
        }
    }, [localCours, pendingExpand]); // eslint-disable-line

    // ── D&D sur toute la colonne ──
    const handleColDrop = (e) => {
        e.preventDefault(); setDragOver(false);
        const idsJson = e.dataTransfer.getData('coursIds');
        const ids = idsJson ? JSON.parse(idsJson) : [parseInt(e.dataTransfer.getData('coursId'))];
        if (ids.length && ids[0]) onDrop(ids, pnItem.pn_id);
    };

    const handleUnlink = async (coursId) => {
        if (!window.confirm('Casser le lien avec ce cours local ?')) return;
        await coursAPI.updatePN(coursId, null);
        onRefresh();
    };

    const handleUnlinkLigne = async (coursIds) => {
        if (!window.confirm(`Dissocier ce groupe option (${coursIds.length} cours en parallèle) ?`)) return;
        await Promise.all(coursIds.map(id => coursAPI.updatePN(id, null)));
        onRefresh();
    };

    // ── Volumes ──
    const saveVolume = async (cours, modaliteId) => {
        const val = parseFloat(editVolMap[cours.id]?.[modaliteId] || 0);
        try {
            await coursVolumesAPI.saveVolume({ cours_id: cours.id, annee_scolaire_id: anneeId, modalite_id: modaliteId, heures_prevues: val });
            await reloadCours(cours);
        } catch { /* silencieux */ }
    };

    // ── Infos (auto-save) ──
    const saveInfos = async (coursId, overrides = {}) => {
        const form = { ...(formMap[coursId] || {}), ...overrides };
        if (!form.code || !form.nom) return;
        try {
            await coursAPI.update(coursId, {
                code:                        form.code,
                nom:                         form.nom,
                type_cours_id:               form.type_cours_id ? parseInt(form.type_cours_id) : null,
                semestre_config_id:          form.semestre_config_id ? parseInt(form.semestre_config_id) : null,
                responsable_id:              form.responsable_id ? parseInt(form.responsable_id) : null,
                coefficient:                 parseFloat(form.coefficient) || 1,
                programme_national_cours_id: pnItem.pn_id,
                type_option:                 form.type_option || null,
                famille_option_id:           form.famille_option_id ? parseInt(form.famille_option_id) : null,
            });
            onRefresh();
        } catch { /* silencieux */ }
    };

    // ── Affectations ──
    const deleteAffectation = async (cours, affId) => {
        await coursVolumesAPI.deleteAffectation(affId);
        await reloadCours(cours);
    };

    const saveAffectationH = async (cours, affId) => {
        const val = parseFloat(editAffHMap[cours.id]?.[affId] ?? 0);
        try { await coursVolumesAPI.updateAffectation(affId, { heures_affectees: val }); await reloadCours(cours); }
        catch { /* silencieux */ }
    };

    const submitAff = async (cours, modaliteId) => {
        const fa = formAffMap[cours.id] || {};
        if (!fa.enseignant_id) return;
        try {
            await coursVolumesAPI.addAffectation({
                annee_scolaire_id: anneeId, cours_id: cours.id,
                enseignant_id:    parseInt(fa.enseignant_id),
                modalite_id:      modaliteId,
                heures_affectees: parseFloat(fa.heures_affectees) || 0,
                groupe_label:     fa.groupe_label || null,
            });
            setAddAffMap(p => ({ ...p, [cours.id]: null }));
            setFormAffMap(p => ({ ...p, [cours.id]: { enseignant_id: '', heures_affectees: '', groupe_label: '' } }));
            await reloadCours(cours);
        } catch { /* silencieux */ }
    };

    // ── Création d'un cours lié au PN ──
    const handleCreateCours = async () => {
        if (!createForm.code || !createForm.nom || !createForm.type_cours_id) {
            setCreateError('Code, intitulé et type sont obligatoires.'); return;
        }
        setSavingCreate(true); setCreateError(null);
        try {
            const resC = await coursAPI.create({
                departement_id:            departementId,
                annee_scolaire_id:         anneeId,
                semestre_config_id:        pnItem.semestre_config_id,
                programme_national_cours_id: pnItem.pn_id,
                code:                      createForm.code,
                nom:                       createForm.nom,
                type_cours_id:             parseInt(createForm.type_cours_id),
                type_option:               createForm.type_option || null,
                famille_option_id:         createForm.famille_option_id ? parseInt(createForm.famille_option_id) : null,
                coefficient:               1,
                actif:                     true,
            });
            const newId = resC.data?.cours?.id || resC.data?.id || null;
            setShowCreate(false);
            setCreateForm({ code: '', nom: '', type_cours_id: '', type_option: '', famille_option_id: '' });
            if (newId) setPendingExpand(newId);
            onRefresh();
        } catch (e) { setCreateError(e.response?.data?.error || 'Erreur création'); }
        finally { setSavingCreate(false); }
    };

    const toggleExpand = (cours) => {
        if (expandedId === cours.id) { setExpandedId(null); return; }
        setExpandedId(cours.id);
        setFormMap(prev => ({
            ...prev,
            [cours.id]: {
                type_cours_id:      cours.type_cours_id || '',
                semestre_config_id: cours.semestre_config_id || '',
                code:               cours.code || '',
                nom:                cours.nom || '',
                coefficient:        cours.coefficient || '1',
                responsable_id:     cours.responsable_id || '',
                type_option:        cours.type_option || '',
                famille_option_id:  cours.famille_option_id || '',
            }
        }));
    };

    return (
        <div className={`cfc-card cfc-card-${pnItem.statut_global}`}>
            {/* ── BANDE HAUTE (1 ligne) ── */}
            <div className="cfc-header">
                <div className="cfc-header-id">
                    <span className="cfc-pn-code">{pnItem.pn_code}</span>
                    <span className="cfc-pn-nom">{pnItem.pn_nom}</span>
                </div>
                <div className="cfc-header-bars">
                    {pnItem.modalites.filter(m => m.heures_min > 0 || m.heures_prevues > 0).map(m => {
                        const pct   = m.heures_min > 0 ? Math.min(100, (m.heures_prevues / m.heures_min) * 100) : 100;
                        const color = m.statut === 'conforme' ? '#16a34a' : m.statut === 'insuffisant' ? '#f59e0b' : '#dc2626';
                        return (
                            <div key={m.modalite_id} className="cfc-bar-item">
                                <span className="cfc-bar-label">{m.modalite_code}</span>
                                <div className="cfc-bar-track"><div className="cfc-bar-fill" style={{ width: `${pct}%`, background: color }} /></div>
                                <span className="cfc-bar-val" style={{ color }}>{m.heures_prevues}h<span className="cfc-bar-min">/{m.heures_min}h</span></span>
                            </div>
                        );
                    })}
                </div>
                <div className="cfc-header-totaux">
                    {pnItem.pn_heures_totales > 0 ? (
                        <>
                            <span style={{ color: totalDisplay >= pnItem.pn_heures_totales ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                                {totalDisplay}h
                            </span>
                            <span className="cfc-tot-sep">/</span>
                            <span style={{ color: '#64748b' }}>{pnItem.pn_heures_totales}h préconisé</span>
                            <span style={{ color: totalDisplay - pnItem.pn_heures_totales >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600, fontSize: 11 }}>
                                ({totalDisplay - pnItem.pn_heures_totales >= 0 ? '+' : ''}{totalDisplay - pnItem.pn_heures_totales}h)
                            </span>
                        </>
                    ) : (
                        <>
                            <span style={{ color: totalDisplay >= totalMin ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                                {totalDisplay}h
                            </span>
                            <span className="cfc-tot-sep">/</span>
                            <span style={{ color: '#64748b' }}>{totalMin}h min</span>
                            <span style={{ color: totalDisplay - totalMin >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600, fontSize: 11 }}>
                                ({totalDisplay - totalMin >= 0 ? '+' : ''}{totalDisplay - totalMin}h)
                            </span>
                        </>
                    )}
                </div>
                <div className="cfc-header-badge">
                    {pnItem.statut_global === 'conforme'
                        ? <span className="cfc-badge-ok"><i className="fa-solid fa-circle-check" /> Conforme</span>
                        : <span className="cfc-badge-ko"><i className="fa-solid fa-circle-xmark" /> Non conforme</span>
                    }
                </div>
            </div>

            {/* ── CORPS : PN gauche | Local droite ── */}
            <div className="cfc-body">
                {/* Colonne PN (gauche) */}
                <div className="cfc-col-pn">
                    <div className="cfc-col-title"><i className="fa-solid fa-book" /> Référentiel national</div>
                    <table className="cfc-min-table">
                        <tbody>
                            {pnItem.modalites.map(m => (
                                <tr key={m.modalite_id}>
                                    <td className="cfc-min-code">{m.modalite_code}</td>
                                    <td className="cfc-min-sep">≥</td>
                                    <td className="cfc-min-val">{m.heures_min}h</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {pnItem.pn_heures_totales > 0
                        ? <div className="cfc-min-total">Heures totales (indicatif) = <strong>{pnItem.pn_heures_totales}h</strong></div>
                        : <div className="cfc-min-total">Total ≥ <strong>{totalMin}h</strong></div>
                    }
                </div>

                {/* Colonne Local (droite) — toute la colonne est drop zone */}
                <div className={`cfc-col-local${dragOver ? ' cfc-col-local-drop' : ''}`}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
                    onDrop={handleColDrop}>

                    <div className="cfc-col-title">
                        <i className="fa-solid fa-chalkboard-teacher" /> Cours locaux associés
                        {localCours.length > 0 && <span className="cfc-local-count">{localCours.length}</span>}
                    </div>

                    {localCours.length === 0 && !dragOver && (
                        <div className="cfc-col-local-empty">
                            <i className="fa-solid fa-link" />
                            <span>Glisser un cours depuis le pool de gauche</span>
                        </div>
                    )}

                    {(() => {
                        // Séparer les cours normaux des cours parallèles
                        const coursNorm = localCours.filter(c => c.type_option !== 'parallele');
                        const coursPar  = localCours.filter(c => c.type_option === 'parallele');
                        // Grouper les parallèles par famille → lignes
                        const pfMap = {};
                        coursPar.forEach(c => {
                            if (!pfMap[c.famille_option_id]) pfMap[c.famille_option_id] = { cours: [], nom: c.famille_option_nom || c.famille_option_code || '?' };
                            pfMap[c.famille_option_id].cours.push(c);
                        });
                        const pfArr = Object.values(pfMap);
                        pfArr.forEach(f => f.cours.sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0)));
                        const maxL = pfArr.reduce((max, f) => Math.max(max, f.cours.length), 0);
                        const lignes = Array.from({ length: maxL }, (_, i) => pfArr.map(f => f.cours[i]).filter(Boolean));

                        const renderLinkedCard = (c, hideUnlink = false, inLigne = false) => {
                        const vols      = volsMap[c.id] || {};
                        const affs      = affsMap[c.id] || [];
                        const totalPrev = pnItem.modalites.reduce((s, m) => s + (vols[m.modalite_id] || 0), 0);
                        const totalAffH = affs.reduce((s, a) => s + parseFloat(a.heures_affectees || 0), 0);
                        const pctAff    = totalPrev > 0 ? Math.min(100, (totalAffH / totalPrev) * 100) : 0;
                        const affColor  = totalPrev > 0 && totalAffH > totalPrev ? '#dc2626'
                                        : totalPrev > 0 && Math.abs(totalAffH - totalPrev) < 0.01 ? '#16a34a' : '#f59e0b';
                        const isExpanded = expandedId === c.id;
                        const form    = formMap[c.id] || {};
                        const editVol = editVolMap[c.id] || {};
                        const addAff  = addAffMap[c.id] || null;
                        const formAff = formAffMap[c.id] || { enseignant_id: '', heures_affectees: '', groupe_label: '' };
                        const editAffH = editAffHMap[c.id] || {};

                        const setFA = (field, val) => setFormAffMap(p => ({ ...p, [c.id]: { ...(p[c.id] || {}), [field]: val } }));
                        const setEAH = (affId, val) => setEditAffHMap(p => ({ ...p, [c.id]: { ...(p[c.id] || {}), [affId]: val } }));

                        return (
                            <div key={c.id} className={`cfc-linked-card${isExpanded ? ' cfc-linked-expanded' : ''}`}
                                draggable={!isExpanded && !inLigne}
                                onDragStart={!isExpanded && !inLigne ? e => { e.stopPropagation(); onDragStartLinked(e, c.id); } : undefined}>

                                {/* Header — clic pour expand */}
                                <div className="cfc-linked-header" onClick={() => toggleExpand(c)}>
                                    {!isExpanded && <i className="fa-solid fa-grip-vertical cfc-grip" />}
                                    <i className={`fa-solid fa-chevron-${isExpanded ? 'down' : 'right'} cfc-expand-chevron`} />
                                    {c.type_option === 'complementaire' && <span className="maq-badge-plus" title="Option+">⊕</span>}
                                    <span className="cfc-linked-code">{c.code}</span>
                                    <span className="cfc-linked-nom">{c.nom}</span>
                                    {!hideUnlink && (
                                        <button className="cfc-unlink-btn" title="Dissocier"
                                            onClick={e => { e.stopPropagation(); handleUnlink(c.id); }}>
                                            <i className="fa-solid fa-scissors" />
                                        </button>
                                    )}
                                </div>

                                {/* Mode réduit : mini-jauges par modalité */}
                                {!isExpanded && (
                                    <div className="cfc-mini-gauges">
                                        {modalites.map(m => {
                                            const h      = parseFloat(vols[m.id] ?? 0);
                                            const affsM  = affs.filter(a => a.modalite_id === m.id);
                                            const totalA = affsM.reduce((s, a) => s + parseFloat(a.heures_affectees || 0), 0);
                                            const pct    = h > 0 ? Math.min(100, (totalA / h) * 100) : 0;
                                            const color  = h > 0 && Math.abs(totalA - h) < 0.01 ? '#16a34a'
                                                         : h > 0 && totalA > h ? '#dc2626' : '#94a3b8';
                                            if (h === 0) return null;
                                            return (
                                                <div key={m.id} className="cfc-mini-gauge-row">
                                                    <span className="cfc-mini-gauge-code">{m.code}</span>
                                                    <span className="cfc-mini-gauge-h">{h}h</span>
                                                    <div className="cfc-mini-gauge-track">
                                                        <div className="cfc-mini-gauge-fill" style={{ width: `${pct}%`, background: h > 0 ? color : '#e2e8f0' }} />
                                                    </div>
                                                    <span className="cfc-mini-gauge-pct" style={{ color: h > 0 ? color : '#94a3b8' }}>{Math.round(pct)}%</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Mode étendu : infos + volumes & affectations */}
                                {isExpanded && (
                                    <div className="cfc-inline-edit">
                                        {/* ── Champs infos ── */}
                                        <div className="cfc-inline-fields">
                                            <div className="cfc-inline-hint-autosave">
                                                <i className="fa-solid fa-floppy-disk" /> Sauvegarde automatique — clic ailleurs pour valider
                                            </div>
                                            <div className="prog-form-row">
                                                <div className="prog-form-group">
                                                    <label>Code</label>
                                                    <input value={form.code || ''}
                                                        onChange={e => setFormMap(p => ({ ...p, [c.id]: { ...p[c.id], code: e.target.value } }))}
                                                        onBlur={() => saveInfos(c.id)} />
                                                </div>
                                                <div className="prog-form-group" style={{ flex: 2 }}>
                                                    <label>Intitulé</label>
                                                    <input value={form.nom || ''}
                                                        onChange={e => setFormMap(p => ({ ...p, [c.id]: { ...p[c.id], nom: e.target.value } }))}
                                                        onBlur={() => saveInfos(c.id)} />
                                                </div>
                                            </div>
                                            <div className="prog-form-row">
                                                <div className="prog-form-group">
                                                    <label>Type</label>
                                                    <select value={form.type_cours_id || ''}
                                                        onChange={e => {
                                                            const v = e.target.value;
                                                            setFormMap(p => ({ ...p, [c.id]: { ...p[c.id], type_cours_id: v } }));
                                                            saveInfos(c.id, { type_cours_id: v });
                                                        }}>
                                                        <option value="">— Type —</option>
                                                        {typesCour.map(t => <option key={t.id} value={t.id}>{t.code} — {t.nom}</option>)}
                                                    </select>
                                                </div>
                                                <div className="prog-form-group">
                                                    <label>Responsable</label>
                                                    <select value={form.responsable_id || ''}
                                                        onChange={e => {
                                                            const v = e.target.value;
                                                            setFormMap(p => ({ ...p, [c.id]: { ...p[c.id], responsable_id: v } }));
                                                            saveInfos(c.id, { responsable_id: v });
                                                        }}>
                                                        <option value="">— Aucun —</option>
                                                        {enseignants.map(ens => <option key={ens.id} value={ens.id}>{ens.prenom} {ens.nom}</option>)}
                                                    </select>
                                                </div>
                                                <div className="prog-form-group" style={{ maxWidth: 80 }}>
                                                    <label>Coeff.</label>
                                                    <input type="number" step="0.5" value={form.coefficient || ''}
                                                        onChange={e => setFormMap(p => ({ ...p, [c.id]: { ...p[c.id], coefficient: e.target.value } }))}
                                                        onBlur={() => saveInfos(c.id)} />
                                                </div>
                                            </div>
                                            <div className="prog-form-row">
                                                <div className="prog-form-group">
                                                    <label>Type option</label>
                                                    <select value={form.type_option || ''}
                                                        onChange={e => {
                                                            const v = e.target.value;
                                                            setFormMap(p => ({ ...p, [c.id]: { ...p[c.id], type_option: v, famille_option_id: '' } }));
                                                            saveInfos(c.id, { type_option: v, famille_option_id: null });
                                                        }}>
                                                        <option value="">Normal</option>
                                                        <option value="complementaire">Option+</option>
                                                        <option value="parallele">Option⇄ (parallèle)</option>
                                                    </select>
                                                </div>
                                                {form.type_option === 'parallele' && (
                                                    <div className="prog-form-group" style={{ flex: 2 }}>
                                                        <label>Famille d'option</label>
                                                        <select value={form.famille_option_id || ''}
                                                            onChange={e => {
                                                                const v = e.target.value;
                                                                setFormMap(p => ({ ...p, [c.id]: { ...p[c.id], famille_option_id: v } }));
                                                                saveInfos(c.id, { famille_option_id: v });
                                                            }}>
                                                            <option value="">— Sélectionner —</option>
                                                            {famillesOptions.map(f => <option key={f.id} value={f.id}>{f.code} — {f.nom}</option>)}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* ── Volumes & enseignants par modalité (prog-vol-card) ── */}
                                        <div className="cfc-inline-vols">
                                            <div className="cfc-inline-vols-title">
                                                <i className="fa-solid fa-chart-bar" /> Volumes horaires &amp; enseignants par modalité
                                            </div>
                                            <div className="cfc-inline-vols-grid">
                                                {modalites.map(m => {
                                                    const affsM      = affs.filter(a => a.modalite_id === m.id);
                                                    const totalA     = affsM.reduce((s, a) => s + parseFloat(a.heures_affectees || 0), 0);
                                                    const prev       = parseFloat(editVol[m.id] || 0);
                                                    const ok         = prev > 0 && Math.abs(totalA - prev) < 0.01;
                                                    const over       = totalA > prev && prev > 0;
                                                    const pnRef      = pnItem.modalites.find(pm => pm.modalite_id === m.id);
                                                    const isAddingHere = addAff?.modalite_id === m.id;
                                                    return (
                                                        <div key={m.id} className={`prog-vol-card${affsM.length === 0 && prev === 0 ? ' prog-vol-empty' : ''}`}>
                                                            <div className="prog-vol-header">
                                                                <span className="prog-vol-code">{m.code}</span>
                                                                {m.coefficient_htd && <span className="prog-vol-coeff">× {m.coefficient_htd} hTD</span>}
                                                                {pnRef && pnRef.heures_min > 0 && <span className="conf-pn-ref">PN ≥ {pnRef.heures_min}h</span>}
                                                            </div>
                                                            <div className="prog-vol-enveloppe">
                                                                <label>Enveloppe prévue (h)</label>
                                                                <div className="prog-vol-input-row">
                                                                    <input type="number" step="0.5" min="0"
                                                                        value={editVol[m.id] ?? ''}
                                                                        onChange={e => setEditVolMap(p => ({ ...p, [c.id]: { ...p[c.id], [m.id]: e.target.value } }))}
                                                                        onBlur={() => saveVolume(c, m.id)}
                                                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saveVolume(c, m.id); } }}
                                                                    />
                                                                    <span className="prog-accord-vol-unit">h</span>
                                                                </div>
                                                            </div>
                                                            {prev > 0 && (
                                                                <div className="prog-vol-barre">
                                                                    <div className="prog-vol-barre-fill" style={{
                                                                        width: `${Math.min(100, prev > 0 ? (totalA / prev) * 100 : 0)}%`,
                                                                        background: ok ? '#16a34a' : over ? '#dc2626' : '#f59e0b'
                                                                    }} />
                                                                    <span className={`prog-vol-barre-label ${ok ? 'ok' : 'ko'}`}>
                                                                        {totalA}h / {prev}h {ok ? '✓' : ''}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {affsM.length > 0 && (
                                                                <div className="prog-aff-list">
                                                                    {affsM.map(a => (
                                                                        <div key={a.id} className="prog-aff-row">
                                                                            <span className="prog-aff-avatar">{(a.enseignant_nom || '?')[0]}</span>
                                                                            <span className="prog-aff-nom">{a.enseignant_nom}</span>
                                                                            <input className="prog-aff-h-input" type="number" step="0.5" min="0"
                                                                                value={editAffH[a.id] ?? a.heures_affectees}
                                                                                onChange={e => setEAH(a.id, e.target.value)}
                                                                                onBlur={() => saveAffectationH(c, a.id)}
                                                                                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                                                                                title="Modifier les heures (Entrée ou clic ailleurs)"
                                                                            />
                                                                            <span className="prog-aff-h-unit">h</span>
                                                                            {a.groupe_label && <span className="prog-aff-groupe">{a.groupe_label}</span>}
                                                                            <button className="prog-aff-del" title="Retirer"
                                                                                onClick={() => deleteAffectation(c, a.id)}>
                                                                                <i className="fa-solid fa-xmark" />
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {isAddingHere ? (
                                                                <div className="prog-aff-form">
                                                                    <select value={formAff.enseignant_id}
                                                                        onChange={e => setFA('enseignant_id', e.target.value)}>
                                                                        <option value="">— Enseignant —</option>
                                                                        {enseignants.map(ens => <option key={ens.id} value={ens.id}>{ens.prenom} {ens.nom}</option>)}
                                                                    </select>
                                                                    <input type="number" step="0.5" placeholder="heures"
                                                                        value={formAff.heures_affectees}
                                                                        onChange={e => setFA('heures_affectees', e.target.value)} />
                                                                    <input placeholder="Groupe (opt.)" value={formAff.groupe_label || ''}
                                                                        onChange={e => setFA('groupe_label', e.target.value)} />
                                                                    <div className="prog-aff-form-btns">
                                                                        <button className="prog-btn-xs prog-btn-primary"
                                                                            onClick={() => submitAff(c, m.id)}>
                                                                            <i className="fa-solid fa-check" />
                                                                        </button>
                                                                        <button className="prog-btn-xs prog-btn-secondary"
                                                                            onClick={() => setAddAffMap(p => ({ ...p, [c.id]: null }))}>
                                                                            <i className="fa-solid fa-xmark" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <button className="prog-aff-add"
                                                                    onClick={() => {
                                                                        setAddAffMap(p => ({ ...p, [c.id]: { modalite_id: m.id } }));
                                                                        setFormAffMap(p => ({ ...p, [c.id]: { enseignant_id: '', heures_affectees: '', groupe_label: '' } }));
                                                                    }}>
                                                                    <i className="fa-solid fa-plus" /> Affecter un enseignant
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                        }; // fin renderLinkedCard

                        return (
                            <>
                                {coursNorm.map(c => renderLinkedCard(c))}
                                {lignes.map((ligne, li) => (
                                    <div key={`lig-${li}`} className="cfc-ligne-group"
                                        draggable
                                        onDragStart={e => {
                                            e.stopPropagation();
                                            const ids = ligne.map(c => c.id);
                                            e.dataTransfer.setData('coursIds', JSON.stringify(ids));
                                            e.dataTransfer.setData('coursId', String(ids[0]));
                                            e.dataTransfer.setData('mode', 'link');
                                            e.dataTransfer.effectAllowed = 'link';
                                        }}>
                                        <div className="cfc-ligne-group-hdr">
                                            <i className="fa-solid fa-grip-vertical cfc-grip" style={{ marginRight: 4 }} />
                                            <i className="fa-solid fa-arrows-left-right" />
                                            <span className="cfc-ligne-group-label">Groupe option {li + 1}</span>
                                            <span className="cfc-ligne-group-hint">{ligne.map(c => c.famille_option_code || c.famille_option_nom).join(' · ')}</span>
                                            <button className="cfc-unlink-btn cfc-unlink-ligne" title="Dissocier toute la ligne"
                                                onClick={e => { e.stopPropagation(); handleUnlinkLigne(ligne.map(c => c.id)); }}>
                                                <i className="fa-solid fa-scissors" /> ligne
                                            </button>
                                        </div>
                                        <div className="cfc-ligne-group-cards">
                                            {ligne.map(c => renderLinkedCard(c, true, true))}
                                        </div>
                                    </div>
                                ))}
                            </>
                        );
                    })()}

                    {/* ── Bouton créer + formulaire inline ── */}
                    {!showCreate ? (
                        <button className="cfc-create-btn-open" onClick={() => setShowCreate(true)}>
                            <i className="fa-solid fa-plus" /> Créer un cours local
                        </button>
                    ) : (
                        <div className="cfc-create-form">
                            <div className="cfc-create-form-title">
                                <i className="fa-solid fa-plus-circle" /> Nouveau cours — lié à {pnItem.pn_code}
                            </div>
                            {createError && <div style={{ fontSize: 11, color: '#dc2626' }}>{createError}</div>}
                            <div className="cfc-create-form-row">
                                <input placeholder="Code (ex: R1.01)" value={createForm.code}
                                    onChange={e => setCreateForm(f => ({ ...f, code: e.target.value }))} />
                                <input placeholder="Intitulé" value={createForm.nom} style={{ flex: 2 }}
                                    onChange={e => setCreateForm(f => ({ ...f, nom: e.target.value }))} />
                            </div>
                            <div className="cfc-create-form-row">
                                <select value={createForm.type_cours_id}
                                    onChange={e => setCreateForm(f => ({ ...f, type_cours_id: e.target.value }))}>
                                    <option value="">— Type —</option>
                                    {typesCour.map(t => <option key={t.id} value={t.id}>{t.code} — {t.nom}</option>)}
                                </select>
                            </div>
                            <div className="cfc-create-form-row">
                                <select value={createForm.type_option}
                                    onChange={e => setCreateForm(f => ({ ...f, type_option: e.target.value, famille_option_id: '' }))}>
                                    <option value="">Normal</option>
                                    <option value="complementaire">Option+</option>
                                    <option value="parallele">Option⇄ (parallèle)</option>
                                </select>
                                {createForm.type_option === 'parallele' && (
                                    <select value={createForm.famille_option_id} style={{ flex: 2 }}
                                        onChange={e => setCreateForm(f => ({ ...f, famille_option_id: e.target.value }))}>
                                        <option value="">— Famille —</option>
                                        {famillesOptions.map(fo => <option key={fo.id} value={fo.id}>{fo.code} — {fo.nom}</option>)}
                                    </select>
                                )}
                            </div>
                            <div className="cfc-create-btns">
                                <button className="cfc-create-btn-add" disabled={savingCreate} onClick={handleCreateCours}>
                                    {savingCreate ? <><i className="fa-solid fa-spinner fa-spin" /> Création...</> : <><i className="fa-solid fa-check" /> Créer et lier</>}
                                </button>
                                <button className="cfc-create-btn-cancel" onClick={() => { setShowCreate(false); setCreateError(null); setCreateForm({ code: '', nom: '', type_cours_id: '', type_option: '', famille_option_id: '' }); }}>
                                    Annuler
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Onglet Conformité v6 — pool par semestre, stats headers, sans sidebar ───
function OngletConformite({ config, anneeId, semestres, sousNiveaux, modalites, enseignants, typesCour, famillesOptions = [], filtSems = new Set(), setFiltSems = () => {} }) {
    const [comparaison, setComparaison]   = useState([]);
    const [coursList, setCoursList]       = useState([]);
    const [loading, setLoading]           = useState(true);
    const [errMsg, setErrMsg]             = useState(null);
    const [collNiveaux, setCollNiveaux]   = useState(new Set());
    const [collSns, setCollSns]           = useState(new Set());
    const [collSems, setCollSems]         = useState(new Set());
    const [semDragOver, setSemDragOver]   = useState(null);
    const [collapsedPools, setCollapsedPools] = useState(new Set());
    const scrollRef = useRef(null);
    const deptId = config?.departement?.id;

    const load = useCallback(async () => {
        if (!deptId || !anneeId) { setErrMsg(`Paramètres manquants : deptId=${deptId} anneeId=${anneeId}`); setLoading(false); return; }
        setLoading(true); setErrMsg(null);
        try {
            const [rc, rcours] = await Promise.all([
                programmeNationalAPI.comparaison(deptId, anneeId),
                coursAPI.getAll(deptId, { anneeId }),
            ]);
            setComparaison(rc.data.comparaison || []);
            setCoursList(rcours.data.cours || []);
        } catch (e) { setErrMsg('Erreur API : ' + (e.response?.data?.error || e.message)); }
        finally { setLoading(false); }
    }, [deptId, anneeId]);

    useEffect(() => { load(); }, [load]);

    // Auto-expand accordéons quand un filtre est actif
    useEffect(() => {
        if (filtSems.size > 0) {
            setCollNiveaux(new Set());
            setCollSns(new Set());
            setCollSems(new Set());
        }
    }, [filtSems]);

    // Restaurer la position de scroll après un rechargement déclenché par D&D
    useEffect(() => {
        if (!loading && scrollRef.current !== null) {
            const y = scrollRef.current;
            scrollRef.current = null;
            requestAnimationFrame(() => window.scrollTo(0, y));
        }
    }, [loading]);

    const coursByPN = {};
    coursList.forEach(c => {
        const pnId = c.programme_national_cours_id;
        if (pnId) { if (!coursByPN[pnId]) coursByPN[pnId] = []; coursByPN[pnId].push(c); }
    });
    const unlinkedCours = coursList.filter(c => !c.programme_national_cours_id && c.actif !== false);

    // Pool de cours non liés par semestre
    const poolBySem = {};
    unlinkedCours.forEach(c => {
        const sid = c.semestre_config_id;
        if (!poolBySem[sid]) poolBySem[sid] = [];
        poolBySem[sid].push(c);
    });

    // Lookup semestres_config par id (pour heures_presentiel / heures_autonomie)
    const semestresById = {};
    semestres.forEach(s => { semestresById[s.id] = s; });

    // ── D&D — tout en mode 'link', le pool drop zone dissocie ──
    // Drag simple (cours individuel) ou multi (ligne parallèle)
    const handleDragStart = (e, coursId) => {
        e.dataTransfer.setData('coursIds', JSON.stringify([coursId]));
        e.dataTransfer.setData('coursId', String(coursId));
        e.dataTransfer.setData('mode', 'link');
        e.dataTransfer.effectAllowed = 'link';
    };
    const handleDragStartLigne = (e, coursIds) => {
        e.dataTransfer.setData('coursIds', JSON.stringify(coursIds));
        e.dataTransfer.setData('coursId', String(coursIds[0]));
        e.dataTransfer.setData('mode', 'link');
        e.dataTransfer.effectAllowed = 'link';
    };

    const handleDrop = async (coursIds, pnId) => {
        // coursIds : array ou entier (compat ancien)
        const ids = Array.isArray(coursIds) ? coursIds : [coursIds];
        scrollRef.current = window.scrollY;
        try {
            await Promise.all(ids.map(id => coursAPI.updatePN(id, pnId)));
            load();
        } catch (e) { scrollRef.current = null; alert('Erreur association : ' + e.message); }
    };

    const handleSemPoolDrop = async (e) => {
        e.preventDefault(); setSemDragOver(null);
        const idsJson = e.dataTransfer.getData('coursIds');
        const ids = idsJson ? JSON.parse(idsJson) : [parseInt(e.dataTransfer.getData('coursId'))];
        if (!ids.length || !ids[0]) return;
        scrollRef.current = window.scrollY;
        try {
            await Promise.all(ids.map(id => coursAPI.updatePN(id, null)));
            load();
        } catch (e) { scrollRef.current = null; alert('Erreur dissociation : ' + e.message); }
    };

    const togglePool = (scId) => setCollapsedPools(prev => { const n = new Set(prev); n.has(scId) ? n.delete(scId) : n.add(scId); return n; });

    // ── Structure niveaux ──
    const snById = {};
    sousNiveaux.forEach(sn => { snById[sn.id] = sn; });

    const isFiltered = filtSems.size > 0;
    const filteredData = isFiltered ? comparaison.filter(d => filtSems.has(d.semestre_config_id)) : comparaison;

    const nivGrouped = {};
    filteredData.forEach(d => {
        const sn = snById[d.sous_niveau_id]; const nid = sn?.niveau_id ?? 0;
        if (!nivGrouped[nid]) nivGrouped[nid] = { id: nid, code: sn?.niveau_code ?? '?', nom: sn?.niveau_nom ?? '?', ordre: sn?.niveau_ordre ?? 999, sousNiveaux: {} };
        const snKey = d.sous_niveau_id ?? 0;
        if (!nivGrouped[nid].sousNiveaux[snKey]) nivGrouped[nid].sousNiveaux[snKey] = { sous_niveau_id: d.sous_niveau_id, code: d.sous_niveau_code, nom: d.sous_niveau_nom, ordre: d.sn_ordre || 0, semestres: {} };
        const semKey = d.semestre_config_id;
        if (!nivGrouped[nid].sousNiveaux[snKey].semestres[semKey])
            nivGrouped[nid].sousNiveaux[snKey].semestres[semKey] = { semestre_config_id: semKey, label: d.semestre_label, items: [] };
        if (!nivGrouped[nid].sousNiveaux[snKey].semestres[semKey].items.find(i => i.pn_id === d.pn_id))
            nivGrouped[nid].sousNiveaux[snKey].semestres[semKey].items.push(d);
    });
    const niveauxList = Object.values(nivGrouped).sort((a, b) => a.ordre - b.ordre);

    // ── Stats agrégées ──
    // Stats par modalité : [{ code, pn, local, ecart }, ...]
    const calcStats = (items) => {
        const byMod = {};
        items.forEach(d => {
            d.modalites.filter(m => m.is_presentiel).forEach(m => {
                if (!byMod[m.modalite_id]) byMod[m.modalite_id] = { code: m.modalite_code, pn: 0, local: 0 };
                byMod[m.modalite_id].pn    += m.heures_min    || 0;
                byMod[m.modalite_id].local += m.heures_prevues || 0;
            });
        });
        return Object.values(byMod)
            .filter(m => m.pn > 0 || m.local > 0)
            .map(m => ({ ...m, ecart: m.local - m.pn }));
    };

    const renderStats = (modStats) => (
        <span className="conf-acc-stats">
            {modStats.map((m, i) => (
                <React.Fragment key={m.code}>
                    {i > 0 && <span className="conf-acc-sep" />}
                    <span className="conf-acc-mod-stat">
                        <code className="conf-acc-mod-code">{m.code}</code>
                        <span className="conf-acc-mod-label">PN</span> <strong>{m.pn}h</strong>
                        <span className="conf-acc-mod-slash">/</span>
                        <span className="conf-acc-mod-label">Local</span> <strong>{m.local}h</strong>
                        <span style={{ color: m.ecart >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                            {' '}{m.ecart >= 0 ? '+' : ''}{m.ecart}h
                        </span>
                    </span>
                </React.Fragment>
            ))}
        </span>
    );

    // ── Toggles ──
    const toggleNiv = (nid)  => setCollNiveaux(prev => { const n = new Set(prev); n.has(nid)  ? n.delete(nid)  : n.add(nid);  return n; });
    const toggleSn  = (snId) => setCollSns(prev => { const n = new Set(prev); n.has(snId) ? n.delete(snId) : n.add(snId); return n; });
    const toggleSem = (scId) => setCollSems(prev => { const n = new Set(prev); n.has(scId) ? n.delete(scId) : n.add(scId); return n; });
    const collapseAll = () => setCollNiveaux(new Set(niveauxList.map(n => n.id)));
    const expandAll   = () => { setCollNiveaux(new Set()); setCollSns(new Set()); setCollSems(new Set()); };

    const nbOk     = filteredData.filter(d => d.statut_global === 'conforme').length;
    const nbAlerts = filteredData.length - nbOk;

    return (
        <div className="prog-tab-content conf-root">
            {errMsg && <div className="prog-alert prog-alert-error" style={{ marginBottom: 12 }}>{errMsg}</div>}

            {/* ── Barre outils ── */}
            <div className="conf-toolbar">
                <div className="conf-toolbar-left">
                    <button className="prog-btn prog-btn-secondary prog-btn-sm" onClick={collapseAll}>
                        <i className="fa-solid fa-compress" /> Tout réduire
                    </button>
                    <button className="prog-btn prog-btn-secondary prog-btn-sm" onClick={expandAll}>
                        <i className="fa-solid fa-expand" /> Tout développer
                    </button>
                    <FiltreDropdowns sousNiveaux={sousNiveaux} semestres={semestres} filtSems={filtSems} setFiltSems={setFiltSems} />
                </div>
                {!loading && filteredData.length > 0 && (
                    <div className="conf-summary-inline">
                        <span className="conf-s-ok"><i className="fa-solid fa-check-circle" /> {nbOk} conformes</span>
                        <span className="conf-s-ko"><i className="fa-solid fa-triangle-exclamation" /> {nbAlerts} à corriger</span>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="prog-center"><i className="fa-solid fa-spinner fa-spin prog-spinner" /></div>
            ) : filteredData.length === 0 ? (
                <div className="prog-empty">
                    <i className="fa-solid fa-scale-balanced" />
                    <p>Aucun programme national saisi.</p>
                </div>
            ) : (
                <div className="conf-acc-root">
                    {niveauxList.map(niv => {
                        const isNivColl = collNiveaux.has(niv.id);
                        const nivItems  = filteredData.filter(d => snById[d.sous_niveau_id]?.niveau_id === niv.id);
                        const nivOk     = nivItems.filter(d => d.statut_global === 'conforme').length;
                        return (
                            <div key={niv.id} className="conf-acc-niv">
                                <div className="conf-acc-niv-hdr conf-sticky-niv" onClick={() => toggleNiv(niv.id)}>
                                    <i className={`fa-solid fa-chevron-${isNivColl ? 'right' : 'down'}`} />
                                    <i className="fa-solid fa-layer-group conf-acc-icon" />
                                    <span className="conf-acc-code">{niv.code}</span>
                                    <span className="conf-acc-nom">{niv.nom}</span>
                                    {renderStats(calcStats(nivItems))}
                                    <span className={`conf-acc-badge ${nivOk === nivItems.length ? 'ok' : 'ko'}`}>{nivOk}/{nivItems.length}</span>
                                </div>
                                {!isNivColl && (
                                    <div className="conf-acc-niv-body">
                                        {Object.values(niv.sousNiveaux).sort((a, b) => a.ordre - b.ordre).map(sn => {
                                            const isSnColl = collSns.has(sn.sous_niveau_id);
                                            const snItems  = filteredData.filter(d => d.sous_niveau_id === sn.sous_niveau_id);
                                            const snOk     = snItems.filter(d => d.statut_global === 'conforme').length;
                                            return (
                                                <div key={sn.sous_niveau_id} className="conf-acc-sn">
                                                    <div className="conf-acc-sn-hdr conf-sticky-sn" onClick={() => toggleSn(sn.sous_niveau_id)}>
                                                        <i className={`fa-solid fa-chevron-${isSnColl ? 'right' : 'down'}`} />
                                                        <span className="conf-acc-sn-code">{sn.code}</span>
                                                        <span className="conf-acc-sn-nom">{sn.nom}</span>
                                                        {renderStats(calcStats(snItems))}
                                                        <span className={`conf-acc-badge ${snOk === snItems.length ? 'ok' : 'ko'}`}>{snOk}/{snItems.length}</span>
                                                    </div>
                                                    {!isSnColl && (
                                                        <div className={`conf-acc-sn-body${filtSems.size === 1 ? ' conf-single-sem' : ''}`}>
                                                            {Object.values(sn.semestres).sort((a, b) => a.label.localeCompare(b.label)).map(sem => {
                                                                const isScoll      = collSems.has(sem.semestre_config_id);
                                                                const semOk        = sem.items.filter(i => i.statut_global === 'conforme').length;
                                                                const semPool      = poolBySem[sem.semestre_config_id] || [];
                                                                const isDO         = semDragOver === sem.semestre_config_id;
                                                                const semCfg       = semestresById[sem.semestre_config_id] || {};
                                                                const localPres    = sem.items.reduce((s, d) => s + d.modalites.filter(m => m.is_presentiel).reduce((ss, m) => ss + (m.heures_prevues || 0), 0), 0);
                                                                const localAuto    = sem.items.reduce((s, d) => s + d.modalites.filter(m => m.is_autonomie).reduce((ss, m) => ss + (m.heures_prevues || 0), 0), 0);
                                                                return (
                                                                    <div key={sem.semestre_config_id} className="conf-acc-sem">
                                                                        <div className="conf-acc-sem-hdr conf-sticky-sem" onClick={() => toggleSem(sem.semestre_config_id)}>
                                                                            <i className={`fa-solid fa-chevron-${isScoll ? 'right' : 'down'}`} />
                                                                            <span className="conf-acc-sem-label">{sem.label}</span>
                                                                            {sn.code && <span className="conf-acc-sem-sn">{sn.code}</span>}
                                                                            {renderStats(calcStats(sem.items))}
                                                                            {semCfg.heures_presentiel > 0 && <>
                                                                                <span className="conf-acc-sep" />
                                                                                <span className="conf-sem-indic conf-sem-indic-pres">
                                                                                    <i className="fa-solid fa-chalkboard-user" />
                                                                                    Présentiel&nbsp;
                                                                                    <strong style={{color: localPres >= semCfg.heures_presentiel ? '#16a34a' : '#dc2626'}}>{localPres}h</strong>
                                                                                    <span className="conf-sem-indic-ref">/{semCfg.heures_presentiel}h</span>
                                                                                </span>
                                                                            </>}
                                                                            {semCfg.heures_autonomie > 0 && <>
                                                                                <span className="conf-acc-sep" />
                                                                                <span className="conf-sem-indic conf-sem-indic-auto">
                                                                                    <i className="fa-solid fa-book-open-reader" />
                                                                                    Autonomie&nbsp;
                                                                                    <strong style={{color: localAuto >= semCfg.heures_autonomie ? '#16a34a' : '#dc2626'}}>{localAuto}h</strong>
                                                                                    <span className="conf-sem-indic-ref">/{semCfg.heures_autonomie}h</span>
                                                                                </span>
                                                                            </>}
                                                                            <span className={`conf-acc-badge ${semOk === sem.items.length ? 'ok' : 'ko'}`}>{semOk}/{sem.items.length}</span>
                                                                        </div>
                                                                        {!isScoll && (
                                                                            <div className="conf-acc-sem-body">
                                                                                <div className="conf-sem-layout">
                                                                                    {/* ── Pool gauche (rétractable) ── */}
                                                                                    {collapsedPools.has(sem.semestre_config_id) ? (
                                                                                        <div className="conf-sem-pool-collapsed"
                                                                                            onDragOver={e => { e.preventDefault(); setSemDragOver(sem.semestre_config_id); }}
                                                                                            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setSemDragOver(null); }}
                                                                                            onDrop={handleSemPoolDrop}
                                                                                            title="À associer — cliquer pour développer">
                                                                                            <button className="conf-pool-toggle-btn" onClick={() => togglePool(sem.semestre_config_id)}>
                                                                                                <i className="fa-solid fa-chevron-right" />
                                                                                            </button>
                                                                                            {semPool.length > 0 && <span className="cfc-local-count">{semPool.length}</span>}
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div className="conf-sem-pool">
                                                                                            <div className="conf-sem-pool-title">
                                                                                                <i className="fa-solid fa-hand-pointer" /> À associer
                                                                                                {semPool.length > 0 && <span className="cfc-local-count">{semPool.length}</span>}
                                                                                                <button className="conf-pool-toggle-btn conf-pool-toggle-collapse" onClick={() => togglePool(sem.semestre_config_id)} title="Réduire">
                                                                                                    <i className="fa-solid fa-chevron-left" />
                                                                                                </button>
                                                                                            </div>
                                                                                            <div className={`conf-sem-pool-chips${isDO ? ' conf-sem-pool-chips-drop' : ''}`}
                                                                                                onDragOver={e => { e.preventDefault(); setSemDragOver(sem.semestre_config_id); }}
                                                                                                onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setSemDragOver(null); }}
                                                                                                onDrop={handleSemPoolDrop}>
                                                                                                {semPool.length === 0 ? (
                                                                                                    <div className="conf-sem-pool-empty">
                                                                                                        {isDO ? <><i className="fa-solid fa-link-slash" /> Déposer pour dissocier</> : 'Tous associés ✓'}
                                                                                                    </div>
                                                                                                ) : (() => {
                                                                                                    const poolNorm = semPool.filter(c => c.type_option !== 'parallele');
                                                                                                    const poolPar  = semPool.filter(c => c.type_option === 'parallele');
                                                                                                    // Grouper par famille → lignes
                                                                                                    const pfMap = {};
                                                                                                    poolPar.forEach(c => {
                                                                                                        if (!pfMap[c.famille_option_id]) pfMap[c.famille_option_id] = [];
                                                                                                        pfMap[c.famille_option_id].push(c);
                                                                                                    });
                                                                                                    const pfArr = Object.values(pfMap);
                                                                                                    pfArr.forEach(cs => cs.sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0)));
                                                                                                    const maxL = pfArr.reduce((max, cs) => Math.max(max, cs.length), 0);
                                                                                                    const lignes = Array.from({ length: maxL }, (_, i) =>
                                                                                                        pfArr.map(cs => cs[i]).filter(Boolean)
                                                                                                    );
                                                                                                    return (
                                                                                                        <>
                                                                                                            {poolNorm.map(c => (
                                                                                                                <div key={c.id} className="conf-sem-pool-chip"
                                                                                                                    draggable onDragStart={e => handleDragStart(e, c.id)} title={c.nom}>
                                                                                                                    <i className="fa-solid fa-grip-vertical" />
                                                                                                                    <div className="conf-sidebar-chip-info">
                                                                                                                        {c.type_option === 'complementaire' && <span className="maq-badge-plus" title="Option+">⊕</span>}
                                                                                                                        <span className="conf-chip-code">{c.code}</span>
                                                                                                                        <span className="conf-chip-nom">{c.nom}</span>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            ))}
                                                                                                            {lignes.map((ligne, li) => (
                                                                                                                <div key={`ligne-${li}`} className="conf-par-ligne"
                                                                                                                    draggable
                                                                                                                    onDragStart={e => handleDragStartLigne(e, ligne.map(c => c.id))}
                                                                                                                    title="Glisser pour associer tous les cours en parallèle">
                                                                                                                    <i className="fa-solid fa-grip-vertical conf-par-grip" />
                                                                                                                    <i className="fa-solid fa-right-left conf-par-icon-par" title="Option parallèle" />
                                                                                                                    <div className="conf-par-familles">
                                                                                                                        {ligne.map(c => (
                                                                                                                            <div key={c.id} className="conf-par-famille-cell">
                                                                                                                                <div className="conf-par-famille-code">
                                                                                                                                    <span className="conf-par-fam-badge">{c.famille_option_nom || c.famille_option_code}</span>
                                                                                                                                    <span className="conf-par-famille-code-text">{c.code}</span>
                                                                                                                                </div>
                                                                                                                                <div className="conf-par-famille-nom">{c.nom}</div>
                                                                                                                            </div>
                                                                                                                        ))}
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            ))}
                                                                                                        </>
                                                                                                    );
                                                                                                })()}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                    {/* ── Cartes droite ── */}
                                                                                    <div className="conf-sem-cards">
                                                                                        {sem.items.map(item => (
                                                                                            <ConformiteCard
                                                                                                key={item.pn_id}
                                                                                                pnItem={item}
                                                                                                localCours={(coursByPN[item.pn_id] || []).filter(c => c.semestre_config_id === item.semestre_config_id)}
                                                                                                modalites={modalites}
                                                                                                enseignants={enseignants}
                                                                                                typesCour={typesCour}
                                                                                                semestres={semestres}
                                                                                                famillesOptions={famillesOptions}
                                                                                                departementId={deptId}
                                                                                                anneeId={anneeId}
                                                                                                onRefresh={load}
                                                                                                onDrop={handleDrop}
                                                                                                onDragStartLinked={handleDragStart}
                                                                                            />
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── Onglet Référentiel PN ───────────────────────────────────
// ─── Modal Programme National (avec volumes cadrage) ──────────
function ModalPN({ pn, semestres, modalites, departementId, anneeId, onSave, onClose }) {
    const VIDE = { semestre_config_id: '', code: '', nom: '', coefficient: '1', heures_totales: '0', description: '' };
    const [form, setForm]         = useState(VIDE);
    const [volumes, setVolumes]   = useState([]); // [{ modalite_id, heures_min }]
    const [saving, setSaving]     = useState(false);
    const [error, setError]       = useState(null);

    useEffect(() => {
        if (pn) {
            setForm({
                semestre_config_id: pn.semestre_config_id || '',
                code:               pn.code               || '',
                nom:                pn.nom                || '',
                coefficient:        pn.coefficient        ?? 1,
                heures_totales:     pn.heures_totales     ?? 0,
                description:        pn.description        || '',
            });
            setVolumes((pn.volumes_min || []).map(v => ({ modalite_id: v.modalite_id, heures_min: v.heures_min })));
        } else {
            setForm(VIDE);
            setVolumes([]);
        }
    }, [pn]);

    const handleChange = e => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
    };

    const addVolumeLine = () => {
        // Trouver la première modalité non encore utilisée
        const used = volumes.map(v => v.modalite_id);
        const first = modalites.find(m => !used.includes(m.id));
        if (!first) return;
        setVolumes(vs => [...vs, { modalite_id: first.id, heures_min: 0 }]);
    };

    const removeVolumeLine = (idx) => setVolumes(vs => vs.filter((_, i) => i !== idx));

    const updateVolumeLine = (idx, field, value) => {
        setVolumes(vs => vs.map((v, i) => i === idx ? { ...v, [field]: value } : v));
    };

    const handleSubmit = async () => {
        if (!form.semestre_config_id || !form.code || !form.nom) {
            setError('Semestre, code et intitulé sont obligatoires.');
            return;
        }
        setSaving(true); setError(null);
        try {
            const payload = {
                departement_id:     departementId,
                annee_scolaire_id:  anneeId,
                semestre_config_id: parseInt(form.semestre_config_id),
                code:               form.code.trim(),
                nom:                form.nom.trim(),
                coefficient:        parseFloat(form.coefficient) || 1,
                heures_totales:     parseFloat(form.heures_totales) || 0,
                description:        form.description || null,
            };
            let id;
            if (pn && pn.id) { await programmeNationalAPI.update(pn.id, payload); id = pn.id; }
            else              { const r = await programmeNationalAPI.create(payload); id = r.data.cours_national.id; }

            // Supprimer volumes retirés (si édition)
            if (pn) {
                const oldIds = (pn.volumes_min || []).map(v => v.modalite_id);
                const newIds = volumes.map(v => v.modalite_id);
                for (const mId of oldIds) {
                    if (!newIds.includes(mId)) {
                        await programmeNationalAPI.deleteVolume(id, mId);
                    }
                }
            }
            // Upsert volumes
            for (const v of volumes) {
                if (v.modalite_id) {
                    await programmeNationalAPI.saveVolume(id, {
                        modalite_id: parseInt(v.modalite_id),
                        heures_min:  parseFloat(v.heures_min) || 0,
                    });
                }
            }
            onSave(id);
        } catch (err) {
            setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
        } finally { setSaving(false); }
    };

    const usedMods = volumes.map(v => Number(v.modalite_id));

    return (
        <div className="prog-overlay" onClick={onClose}>
            <div className="prog-modal prog-modal-lg" onClick={e => e.stopPropagation()}>
                <div className="prog-modal-header">
                    <h3>{pn ? 'Modifier' : 'Nouveau'} — Cours national</h3>
                    <button className="prog-modal-close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
                </div>
                <div className="prog-modal-body">
                    {error && <div className="prog-alert prog-alert-error">{error}</div>}

                    {/* Champs de base */}
                    <div className="prog-form-row">
                        <div className="prog-form-group">
                            <label>Code *</label>
                            <input name="code" value={form.code} onChange={handleChange} placeholder="ex : R1.01" />
                        </div>
                        <div className="prog-form-group prog-form-group-lg">
                            <label>Intitulé *</label>
                            <input name="nom" value={form.nom} onChange={handleChange} placeholder="ex : Initiation au développement" />
                        </div>
                    </div>
                    <div className="prog-form-row">
                        <div className="prog-form-group">
                            <label>Semestre *</label>
                            <select name="semestre_config_id" value={form.semestre_config_id} onChange={handleChange}>
                                <option value="">— Sélectionner —</option>
                                {semestres.map(s => <option key={s.id} value={s.id}>{s.sous_niveau_code ? `${s.sous_niveau_code} — ${s.semestre}` : s.semestre}</option>)}
                            </select>
                        </div>
                        <div className="prog-form-group">
                            <label>Coefficient</label>
                            <input type="number" name="coefficient" value={form.coefficient} onChange={handleChange} step="0.5" min="0" />
                        </div>
                    </div>
                    <div className="prog-form-row">
                        <div className="prog-form-group">
                            <label>Coefficient</label>
                            <input type="number" name="coefficient" value={form.coefficient} onChange={handleChange} step="0.5" min="0" />
                        </div>
                    </div>
                    <div className="prog-form-row">
                        <div className="prog-form-group">
                            <label>Heures totales <span className="prog-hint">(indicatif)</span></label>
                            <input type="number" name="heures_totales" value={form.heures_totales} onChange={handleChange} step="1" min="0" />
                        </div>
                    </div>

                    {/* Section cadrage national */}
                    <div className="prog-pn-cadrage">
                        <div className="prog-pn-cadrage-title">
                            <i className="fa-solid fa-landmark" /> Cadrage national — heures minimales par modalité
                        </div>
                        <div className="prog-pn-cadrage-hint">
                            Ces valeurs servent de référence pour la vérification de conformité du programme local.
                        </div>
                        {volumes.length === 0 && (
                            <div className="prog-accord-no-aff">Aucune modalité cadrée — cliquez sur ＋ pour en ajouter</div>
                        )}
                        {volumes.map((v, idx) => (
                            <div key={idx} className="prog-pn-vol-row">
                                <select
                                    value={v.modalite_id}
                                    onChange={e => updateVolumeLine(idx, 'modalite_id', parseInt(e.target.value))}>
                                    <option value="">— Modalité —</option>
                                    {modalites
                                        .filter(m => m.id === v.modalite_id || !usedMods.includes(m.id))
                                        .map(m => <option key={m.id} value={m.id}>{m.code} — {m.nom}</option>)}
                                </select>
                                <input type="number" step="0.5" min="0" placeholder="heures min"
                                    value={v.heures_min}
                                    onChange={e => updateVolumeLine(idx, 'heures_min', e.target.value)} />
                                <span className="prog-accord-vol-unit">h</span>
                                <button className="prog-aff-del" onClick={() => removeVolumeLine(idx)}>
                                    <i className="fa-solid fa-xmark" />
                                </button>
                            </div>
                        ))}
                        <button className="prog-accord-aff-add" onClick={addVolumeLine}
                            disabled={volumes.length >= modalites.length}>
                            <i className="fa-solid fa-plus" /> Ajouter une modalité
                        </button>
                    </div>

                    <div className="prog-modal-actions">
                        <button className="prog-btn prog-btn-secondary" onClick={onClose}>Annuler</button>
                        <button className="prog-btn prog-btn-primary" disabled={saving} onClick={handleSubmit}>
                            {saving ? <><i className="fa-solid fa-spinner fa-spin" /> Sauvegarde...</> : 'Enregistrer'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Formulaire inline parcours PN ───────────────────────────
function ParcoursForm({ item, onSave, onCancel, saving }) {
    const [form, setForm] = useState({ code: item?.code || '', nom: item?.nom || '', description: item?.description || '' });
    const h = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    return (
        <>
            <div className="prog-form-group">
                <label>Code *</label>
                <input name="code" value={form.code} onChange={h} placeholder="ex : PARC-A" style={{ textTransform: 'uppercase' }} />
            </div>
            <div className="prog-form-group">
                <label>Nom *</label>
                <input name="nom" value={form.nom} onChange={h} placeholder="ex : Développement Web" />
            </div>
            <div className="prog-form-group">
                <label>Description</label>
                <textarea name="description" value={form.description} onChange={h} rows={2} placeholder="Description optionnelle" />
            </div>
            <div className="prog-modal-actions">
                <button className="prog-btn prog-btn-secondary" onClick={onCancel}>Annuler</button>
                <button className="prog-btn prog-btn-primary" disabled={saving || !form.code.trim() || !form.nom.trim()}
                    onClick={() => onSave(form)}>
                    {saving ? <><i className="fa-solid fa-spinner fa-spin" /> Sauvegarde...</> : 'Enregistrer'}
                </button>
            </div>
        </>
    );
}

function OngletPN({ config, anneeId, annees, modalites, sousNiveaux = [], semestres: semestresParent = [], filtSems = new Set(), setFiltSems = () => {} }) {
    const deptId = config?.departement?.id;

    // ── États existants ───────────────────────────────────────
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);
    const [modalOpen, setModalOpen]   = useState(false);
    const [itemEdit, setItemEdit]     = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    const [snOrder, setSnOrder]       = useState([]);
    const [semOrders, setSemOrders]   = useState({});
    const [dragSnIdx, setDragSnIdx]   = useState(null);
    const [dragSemKey, setDragSemKey] = useState(null);
    const [selected, setSelected]         = useState(new Set()); // IDs cours PN
    const [selectedUes, setSelectedUes]   = useState(new Set()); // IDs UEs
    const [collapsedSems, setCollapsedSems] = useState(new Set());
    // ── Carry-forward PN ──────────────────────────────────────
    // ── Récupérer tout depuis N-1 ─────────────────────────────
    const [rtOpen, setRtOpen]               = useState(false);
    const [rtSource, setRtSource]           = useState('');
    const [rtPreview, setRtPreview]         = useState(null);
    const [rtExecuting, setRtExecuting]     = useState(false);
    const [rtResult, setRtResult]           = useState(null);
    const [rtError, setRtError]             = useState(null);
    const [rtCopyCours, setRtCopyCours]     = useState(true);
    const [rtCopyVolumes, setRtCopyVolumes] = useState(true);
    const [rtCopyAff, setRtCopyAff]         = useState(true);
    // ── UE management ─────────────────────────────────────────
    const [ues, setUes]               = useState({});    // semConfigId → [{id, code, nom, pnIds:Set}]
    const [newUeForm, setNewUeForm]   = useState({});    // semConfigId → {code, nom}
    const [editUeInline, setEditUeInline] = useState(null); // {id, code, nom}
    const [dragPnInfo, setDragPnInfo] = useState(null);  // {pnId, sourceUeId}
    const [dragOverTarget, setDragOverTarget] = useState(null); // {type:'ue'|'pool', id}
    const [dragUeKey, setDragUeKey] = useState(null);    // {semId, ueIdx}
    const [pendingUeId, setPendingUeId] = useState(null);
    const [collapsedPools, setCollapsedPools] = useState(new Set());
    const [collapsedUes, setCollapsedUes] = useState(new Set());
    const toggleUe = (ueId) => setCollapsedUes(s => { const n = new Set(s); n.has(ueId) ? n.delete(ueId) : n.add(ueId); return n; });
    const [pnViewMode, setPnViewMode] = useState(() => localStorage.getItem('prog_pn_view') || 'tabs');
    const [parcoursTabs, setParcoursTabs] = useState({}); // { [semId]: parcoursPnId | null } — null = tronc commun

    // ── Semestres (sous-onglet PN) ────────────────────────────
    const [pnTab, setPnTab]                 = useState(() => localStorage.getItem('prog_pn_tab') || 'semestres');
    const [semestres, setSemestres]         = useState([]);
    const [sousNiveauxPN, setSousNiveauxPN] = useState([]);
    const [semModal, setSemModal]           = useState(null);
    const [semSaving, setSemSaving]         = useState(false);
    const [semErrSave, setSemErrSave]       = useState(null);
    const [semErrDelete, setSemErrDelete]   = useState(null);

    // ── Parcours PN ───────────────────────────────────────────
    const [parcoursPn, setParcoursPn]           = useState([]);    // liste [{id, code, nom, semestres:[]}]
    const [parcoursModal, setParcoursModal]     = useState(null);  // null | {item: null|{...}}
    const [parcoursSaving, setParcoursSaving]   = useState(false);
    const [parcoursErr, setParcoursErr]         = useState(null);

    const load = useCallback(async () => {
        if (!deptId || !anneeId) return;
        setLoading(true);
        try {
            const [pnRes, ueRes] = await Promise.all([
                programmeNationalAPI.getAll(deptId, { anneeId }),
                ueAPI.getMaquette(deptId, anneeId),
            ]);
            // ── Cours PN ──
            // Préserver l'ordre de la requête SQL (triée par sn.ordre, sc.ordre)
            // Object.values() sur clés entières trierait par ID — on utilise des tableaux à la place
            const raw = pnRes.data.programme_national || [];
            const snArr = [];
            const snIndex = {};    // snId → index dans snArr
            const semIndex = {};   // snId → { semId → index dans semArr }
            raw.forEach(pn => {
                const snId = pn.sous_niveau_id || 'inconnu';
                if (snIndex[snId] === undefined) {
                    snIndex[snId] = snArr.length;
                    snArr.push({ id: snId, code: pn.sous_niveau_code || '?', nom: pn.sous_niveau_nom || '' });
                    semIndex[snId] = {};
                }
                const sn = snArr[snIndex[snId]];
                if (!sn.semestres) sn.semestres = [];
                const semId = pn.semestre_config_id;
                if (semIndex[snId][semId] === undefined) {
                    semIndex[snId][semId] = sn.semestres.length;
                    sn.semestres.push({ id: semId, label: pn.semestre_label, cours: [] });
                }
                sn.semestres[semIndex[snId][semId]].cours.push(pn);
            });
            // ── Parcours actifs par semestre ──
            let parcoursBySem = {};
            try {
                const rpn = await parcoursPnAPI.getAll(deptId, anneeId);
                const allParcours = rpn.data.parcours || [];
                setParcoursPn(allParcours);
                allParcours.forEach(p => {
                    (p.semestres || []).forEach(s => {
                        if (!parcoursBySem[s.semestre_config_id]) parcoursBySem[s.semestre_config_id] = [];
                        parcoursBySem[s.semestre_config_id].push(p);
                    });
                });
            } catch {}

            setSnOrder(snArr);
            const semOrd = {};
            snArr.forEach(sn => {
                semOrd[sn.id] = (sn.semestres || []).map(sem => ({
                    ...sem,
                    parcours: parcoursBySem[sem.id] || [],
                }));
            });
            setSemOrders(semOrd);
            // ── UEs ──
            const ueMap = {};
            (ueRes.data.maquette || []).forEach(ue => {
                const semId = ue.semestre_config_id;
                if (!ueMap[semId]) ueMap[semId] = [];
                ueMap[semId].push({
                    id: ue.ue_id, code: ue.ue_code, nom: ue.ue_nom,
                    ordre: ue.ue_ordre,
                    statut: ue.statut || 'commun',
                    parcours_pn_id: ue.parcours_pn_id || null,
                    parcours_pn_code: ue.parcours_pn_code || null,
                    parcours_pn_nom: ue.parcours_pn_nom || null,
                    pnIds: new Set(ue.cours.filter(c => !c.is_transversal).map(c => c.pn_id)),
                });
            });
            setUes(ueMap);
        } catch { setError('Impossible de charger le programme national.'); }
        finally { setLoading(false); }
    }, [deptId, anneeId]);

    useEffect(() => { load(); }, [load]);

    const loadSemestres = useCallback(async () => {
        if (!deptId || !anneeId) return;
        const [rs, rsn] = await Promise.all([
            semestresConfigAPI.getAll(deptId, anneeId),
            sousNiveauxAPI.getSousNiveaux(deptId),
        ]);
        setSemestres(rs.data.semestres || []);
        setSousNiveauxPN(rsn.data.sous_niveaux || []);
        // Charger les parcours en même temps
        try {
            const rp = await parcoursPnAPI.getAll(deptId, anneeId);
            setParcoursPn(rp.data.parcours || []);
        } catch { setParcoursPn([]); }
    }, [deptId, anneeId]);

    useEffect(() => { loadSemestres(); }, [loadSemestres]);

    const handleSemSave = async (form) => {
        setSemSaving(true); setSemErrSave(null);
        try {
            const payload = {
                sous_niveau_id:    parseInt(form.sous_niveau_id),
                semestre:          form.semestre?.trim().toUpperCase(),
                heures_presentiel: parseFloat(form.heures_presentiel) || 0,
                heures_autonomie:  parseFloat(form.heures_autonomie)  || 0,
                annee_scolaire_id: anneeId,
            };
            semModal?.item
                ? await semestresConfigAPI.update(semModal.item.id, payload)
                : await semestresConfigAPI.create(payload);
            setSemModal(null); loadSemestres();
        } catch (err) { setSemErrSave(err.response?.data?.error || 'Erreur'); }
        finally { setSemSaving(false); }
    };

    const handleSemDelete = async (id) => {
        if (!window.confirm('Supprimer ce semestre ?')) return;
        setSemErrDelete(null);
        try {
            await semestresConfigAPI.delete(id);
            loadSemestres();
        } catch (err) {
            setSemErrDelete(err.response?.data?.error || 'Impossible de supprimer ce semestre.');
        }
    };

    // ── Handlers Parcours PN ──────────────────────────────────
    const handleParcoursSave = async (form) => {
        setParcoursSaving(true); setParcoursErr(null);
        try {
            if (parcoursModal?.item) {
                await parcoursPnAPI.update(parcoursModal.item.id, {
                    code: form.code, nom: form.nom, description: form.description });
            } else {
                await parcoursPnAPI.create({
                    departementId: deptId, anneeId,
                    code: form.code, nom: form.nom, description: form.description });
            }
            setParcoursModal(null);
            loadSemestres();
        } catch (err) { setParcoursErr(err.response?.data?.error || 'Erreur'); }
        finally { setParcoursSaving(false); }
    };
    const handleParcoursDelete = async (id) => {
        if (!window.confirm('Supprimer ce parcours ? Les UEs liées seront dissociées.')) return;
        try { await parcoursPnAPI.delete(id); loadSemestres(); }
        catch (err) { alert(err.response?.data?.error || 'Erreur suppression'); }
    };
    const toggleSemestreParcours = async (semestreConfigId, parcoursPnId, active) => {
        try {
            if (active) await parcoursPnAPI.linkSemestre(semestreConfigId, parcoursPnId);
            else        await parcoursPnAPI.unlinkSemestre(semestreConfigId, parcoursPnId);
            // loadSemestres met à jour les checkboxes de l'onglet Semestres
            // load() reconstruit semOrders.sem.parcours pour l'onglet Cours PN
            loadSemestres();
            load();
        } catch (err) { alert(err.response?.data?.error || 'Erreur'); }
    };

    // ── Récupérer tout : handlers ─────────────────────────────
    const openRt = () => {
        setRtOpen(true); setRtSource(''); setRtPreview(null);
        setRtResult(null); setRtError(null);
        setRtCopyCours(true); setRtCopyVolumes(true); setRtCopyAff(true);
    };
    const loadRtPreview = async (sourceId) => {
        if (!sourceId || !deptId || !anneeId) { setRtPreview(null); return; }
        try {
            const res = await carryForwardAPI.preview({ deptId, sourceAnneeId: sourceId, targetAnneeId: anneeId });
            setRtPreview(res.data);
        } catch { setRtPreview(null); }
    };
    const handleRecupererTout = async () => {
        if (!rtSource) return;
        setRtExecuting(true); setRtError(null);
        try {
            const items = rtCopyCours
                ? (rtPreview?.cours || []).map(c => ({
                    coursId: c.id,
                    copyVolumes: rtCopyVolumes,
                    copyAffectations: rtCopyAff,
                  }))
                : [];
            const res = await carryForwardAPI.execute({
                sourceAnneeId: parseInt(rtSource),
                targetAnneeId: anneeId,
                deptId,
                copyPN: true,
                copyEnseignants: rtCopyAff,
                items,
            });
            setRtResult(res.data);
            load();
            loadSemestres();
        } catch (err) {
            setRtError(err.response?.data?.error || 'Erreur lors de la récupération');
        } finally { setRtExecuting(false); }
    };

    // ── Sélection multiple ────────────────────────────────────
    const toggleSelect = (id) => setSelected(s => {
        const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
    });
    const toggleSelectSem = (cours) => {
        const ids = cours.map(c => c.id);
        setSelected(s => {
            const n = new Set(s);
            const allOn = ids.every(id => n.has(id));
            allOn ? ids.forEach(id => n.delete(id)) : ids.forEach(id => n.add(id));
            return n;
        });
    };
    const allPnIds  = snOrder.flatMap(sn => (semOrders[sn.id] || []).flatMap(sem => sem.cours.map(c => c.id)));
    const allUeIds  = Object.values(ues).flat().map(u => u.id);
    const allSelected = (allPnIds.length > 0 || allUeIds.length > 0)
        && allPnIds.every(id => selected.has(id))
        && allUeIds.every(id => selectedUes.has(id));
    const toggleSelectAll = () => {
        if (allSelected) {
            setSelected(new Set());
            setSelectedUes(new Set());
        } else {
            setSelected(new Set(allPnIds));
            setSelectedUes(new Set(allUeIds));
        }
    };
    const toggleSelectUe = (ueId) => setSelectedUes(s => {
        const n = new Set(s); n.has(ueId) ? n.delete(ueId) : n.add(ueId); return n;
    });
    const toggleCollapse = (semId) => setCollapsedSems(s => {
        const n = new Set(s); n.has(semId) ? n.delete(semId) : n.add(semId); return n;
    });

    const handleBulkDelete = async () => {
        const pnIds = [...selected];
        const ueIds = [...selectedUes];
        const total = pnIds.length + ueIds.length;
        if (!window.confirm(`Supprimer ${pnIds.length > 0 ? `${pnIds.length} cours PN` : ''}${pnIds.length > 0 && ueIds.length > 0 ? ' et ' : ''}${ueIds.length > 0 ? `${ueIds.length} UE${ueIds.length > 1 ? 's' : ''}` : ''} ?`)) return;
        await Promise.all([
            ...pnIds.map(id => programmeNationalAPI.delete(id)),
            ...ueIds.map(id => ueAPI.delete(id)),
        ]);
        setSelected(new Set());
        setSelectedUes(new Set());
        load();
    };

    // ── Preview import (diff) ─────────────────────────────────
    const handlePreview = useCallback(async (rows) => {
        // Construire la map code → cours existant
        const existingMap = {};
        Object.values(semOrders).flat().forEach(sem =>
            sem.cours.forEach(pn => { existingMap[pn.code] = pn; })
        );
        return rows.map(row => {
            const existing = existingMap[row.code];
            if (!existing) return { status: 'new', row };
            const changes = [];
            if (row.nom && row.nom !== existing.nom)
                changes.push({ key: 'nom', label: 'Intitulé', old: existing.nom, new: row.nom });
            if (row.heures_totales !== '' && parseFloat(row.heures_totales) !== parseFloat(existing.heures_totales || 0))
                changes.push({ key: 'heures_totales', label: 'Total (h)', old: existing.heures_totales, new: row.heures_totales });
            modalites.forEach(m => {
                const colKey = `vol_${m.code}`;
                if (row[colKey] !== undefined && row[colKey] !== '') {
                    const existingVol = (existing.volumes_min || []).find(v => v.modalite_code === m.code);
                    const oldH = existingVol ? String(existingVol.heures_min) : '—';
                    if (String(row[colKey]) !== oldH)
                        changes.push({ key: colKey, label: `Min ${m.code}`, old: oldH, new: row[colKey] });
                }
            });
            if (changes.length === 0) return { status: 'identical', row, existing };
            return { status: 'modified', row, existing, changes };
        });
    }, [semOrders, modalites]);

    // ── Drag & drop sous-niveaux ──────────────────────────────
    const handleSnDragStart = (e, idx) => { setDragSnIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
    const handleSnDrop = async (e, toIdx) => {
        e.preventDefault();
        if (dragSnIdx === null || dragSnIdx === toIdx) { setDragSnIdx(null); return; }
        const next = [...snOrder];
        const [moved] = next.splice(dragSnIdx, 1);
        next.splice(toIdx, 0, moved);
        setSnOrder(next);
        setDragSnIdx(null);
        try { await sousNiveauxAPI.reorder(next.map((sn, i) => ({ id: sn.id, ordre: i }))); } catch {}
    };

    // ── Drag & drop semestres (colonnes) ──────────────────────
    const handleSemDragStart = (e, snId, semIdx) => {
        e.stopPropagation();
        setDragSemKey({ snId, semIdx });
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleSemDrop = async (e, snId, toIdx) => {
        e.preventDefault(); e.stopPropagation();
        if (!dragSemKey || dragSemKey.snId !== snId || dragSemKey.semIdx === toIdx) { setDragSemKey(null); return; }
        const next = [...(semOrders[snId] || [])];
        const [moved] = next.splice(dragSemKey.semIdx, 1);
        next.splice(toIdx, 0, moved);
        setSemOrders(s => ({ ...s, [snId]: next }));
        setDragSemKey(null);
        try { await semestresConfigAPI.reorder(next.map((sem, i) => ({ id: sem.id, ordre: i }))); } catch {}
    };

    // ── Handlers UE ──────────────────────────────────────────
    const handleCreateUe = async (semConfigId, options = {}) => {
        const { formKey, parcoursPnId } = options;
        const key = formKey ?? String(semConfigId);
        const form = newUeForm[key] || {};
        if (!form.code?.trim() || !form.nom?.trim()) return;
        try {
            await ueAPI.create({
                departement_id: deptId, annee_scolaire_id: anneeId, semestre_config_id: semConfigId,
                code: form.code.trim(), nom: form.nom.trim(),
                ...(parcoursPnId ? { statut: 'parcours', parcours_pn_id: parcoursPnId } : {}),
            });
            setNewUeForm(p => { const n = { ...p }; delete n[key]; return n; });
            load();
        } catch (e) { alert('Erreur création UE : ' + e.message); }
    };
    const handleDeleteUe = async (ueId) => {
        if (!window.confirm('Supprimer cette UE ? Les liaisons seront perdues.')) return;
        try { await ueAPI.delete(ueId); load(); }
        catch (e) { alert('Erreur suppression : ' + e.message); }
    };
    const handleUpdateUe = async () => {
        if (!editUeInline?.code?.trim() || !editUeInline?.nom?.trim()) return;
        try {
            await ueAPI.update(editUeInline.id, { code: editUeInline.code.trim(), nom: editUeInline.nom.trim() });
            setEditUeInline(null);
            load();
        } catch (e) { alert('Erreur modification UE : ' + e.message); }
    };
    const handleToggleTransversal = async (pn) => {
        try { await programmeNationalAPI.update(pn.id, { is_transversal: !pn.is_transversal }); load(); }
        catch (e) { alert('Erreur : ' + e.message); }
    };
    const handleDropOnUe = async (e, ueId) => {
        e.preventDefault(); e.stopPropagation();
        setDragOverTarget(null); setDragPnInfo(null);
        let info;
        try { info = JSON.parse(e.dataTransfer.getData('application/pn-link')); } catch { return; }
        if (!info?.pnId || info.sourceUeId === ueId) return;
        try {
            if (info.sourceUeId) await ueAPI.unlinkPN(info.sourceUeId, info.pnId);
            await ueAPI.linkPN(ueId, info.pnId);
            load();
        } catch (err) { alert('Erreur liaison : ' + err.message); }
    };
    const handleDropOnPool = async (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragOverTarget(null); setDragPnInfo(null);
        let info;
        try { info = JSON.parse(e.dataTransfer.getData('application/pn-link')); } catch { return; }
        if (!info?.sourceUeId) return;
        try { await ueAPI.unlinkPN(info.sourceUeId, info.pnId); load(); }
        catch (err) { alert('Erreur déliaison : ' + err.message); }
    };

    const handleUeReorderDrop = async (e, semId, toIdx) => {
        e.preventDefault(); e.stopPropagation();
        if (!dragUeKey || dragUeKey.semId !== semId || dragUeKey.ueIdx === toIdx) {
            setDragUeKey(null); return;
        }
        const next = [...(ues[semId] || [])];
        const [moved] = next.splice(dragUeKey.ueIdx, 1);
        next.splice(toIdx, 0, moved);
        setUes(u => ({ ...u, [semId]: next }));
        setDragUeKey(null);
        try { await ueAPI.reorder(next.map((u, i) => ({ id: u.id, ordre: i }))); } catch {}
    };

    return (
        <div className="prog-tab-content">
            {/* ── En-tête : sous-onglets + bouton Récupérer tout ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <div className="prog-ref-tabs" style={{ marginBottom: 0 }}>
                    <button className={`prog-ref-tab ${pnTab === 'semestres' ? 'active' : ''}`} onClick={() => { setPnTab('semestres'); localStorage.setItem('prog_pn_tab', 'semestres'); }}>
                        <i className="fa-solid fa-calendar-week" /> Semestres <span className="prog-ref-count">{semestres.length}</span>
                    </button>
                    <button className={`prog-ref-tab ${pnTab === 'cours' ? 'active' : ''}`} onClick={() => { setPnTab('cours'); localStorage.setItem('prog_pn_tab', 'cours'); }}>
                        <i className="fa-solid fa-scroll" /> Cours PN
                    </button>
                </div>
                {(annees || []).filter(a => a.id !== anneeId).length > 0 && (
                    <button className="prog-btn prog-btn-secondary prog-btn-carry"
                        style={{ marginLeft: 'auto' }}
                        onClick={openRt}
                        title="Récupérer semestres, cours PN, UEs, cours locaux et affectations depuis N-1">
                        <i className="fa-solid fa-clock-rotate-left" /> Récupérer tout depuis N-1
                    </button>
                )}
            </div>

            {pnTab === 'cours' && (<>
            <div className="prog-toolbar">
                <FiltreDropdowns sousNiveaux={sousNiveaux} semestres={semestresParent} filtSems={filtSems} setFiltSems={setFiltSems} />
                <button className="prog-btn prog-btn-secondary" onClick={() => setImportOpen(true)}>
                    <i className="fa-solid fa-file-import" /> Importer
                </button>
                <button className="prog-btn prog-btn-primary" onClick={() => { setItemEdit(null); setModalOpen(true); }}>
                    <i className="fa-solid fa-plus" /> Ajouter
                </button>
                <div className="prog-pn-view-toggle">
                    <button className={`prog-pn-vt-btn${pnViewMode === 'tabs' ? ' active' : ''}`}
                        title="Afficher un parcours à la fois via des onglets"
                        onClick={() => { setPnViewMode('tabs'); localStorage.setItem('prog_pn_view', 'tabs'); }}>
                        <i className="fa-solid fa-layer-group" /> Onglets
                    </button>
                    <button className={`prog-pn-vt-btn${pnViewMode === 'all' ? ' active' : ''}`}
                        title="Afficher tous les parcours côte à côte"
                        onClick={() => { setPnViewMode('all'); localStorage.setItem('prog_pn_view', 'all'); }}>
                        <i className="fa-solid fa-table-columns" /> Tout afficher
                    </button>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, marginLeft: 4 }}>
                    <input type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        disabled={allPnIds.length === 0 && allUeIds.length === 0}
                    />
                    Tout sélectionner
                </label>
                {(selected.size > 0 || selectedUes.size > 0) && (
                    <button className="prog-btn prog-btn-danger" onClick={handleBulkDelete}>
                        <i className="fa-solid fa-trash" /> Supprimer la sélection ({selected.size + selectedUes.size})
                    </button>
                )}
            </div>

            {loading ? (
                <div className="prog-center"><i className="fa-solid fa-spinner fa-spin prog-spinner" /></div>
            ) : error ? (
                <div className="prog-alert prog-alert-error">{error}</div>
            ) : snOrder.length === 0 ? (
                <div className="prog-empty">
                    <i className="fa-solid fa-scroll" />
                    <p>Aucun cours dans le programme national. Saisissez le référentiel de votre formation.</p>
                </div>
            ) : snOrder.filter(sn =>
                    filtSems.size === 0 || (semOrders[sn.id] || []).some(s => filtSems.has(s.id))
                ).map((sn, snIdx) => (
                <div key={sn.id}
                    className={`prog-pn-sn-block${dragSnIdx === snIdx ? ' prog-drag-active' : ''}`}
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={e => handleSnDrop(e, snIdx)}
                    onDragEnd={() => setDragSnIdx(null)}
                >
                    <div className="prog-pn-sn-title" draggable onDragStart={e => handleSnDragStart(e, snIdx)}>
                        <i className="fa-solid fa-grip-vertical prog-pn-grip" />
                        <i className="fa-solid fa-graduation-cap" />
                        <span>{sn.code}{sn.nom ? ` — ${sn.nom}` : ''}</span>
                    </div>

                    <div className={`prog-pn-sem-cols${pnViewMode === 'all' && (semOrders[sn.id] || []).some(s => s.parcours.length > 0) ? ' pn-mode-all' : ''}`}>
                        {(semOrders[sn.id] || []).filter(s => filtSems.size === 0 || filtSems.has(s.id)).map((sem, semIdx) => {
                            const semUes = ues[sem.id] || [];
                            // IDs non-transversaux explicitement liés à une UE
                            const assignedPnIds = new Set(semUes.flatMap(u => [...u.pnIds]));
                            // Transversaux du semestre
                            const transversaux = sem.cours.filter(c => c.is_transversal);
                            // Pool = cours non-transversaux non liés à une UE
                            const poolCours = sem.cours.filter(c => !c.is_transversal && !assignedPnIds.has(c.id));

                            return (
                                <div key={sem.id}
                                    className={`prog-pn-sem-col${dragSemKey?.snId === sn.id && dragSemKey?.semIdx === semIdx ? ' prog-drag-active' : ''}`}
                                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                    onDrop={e => handleSemDrop(e, sn.id, semIdx)}
                                    onDragEnd={() => setDragSemKey(null)}
                                >
                                    {/* ── En-tête semestre ── */}
                                    <div className="prog-pn-sem-header" draggable onDragStart={e => handleSemDragStart(e, sn.id, semIdx)}>
                                        <i className="fa-solid fa-grip-vertical prog-pn-grip" />
                                        <button className="prog-pn-collapse-btn"
                                            onClick={e => { e.stopPropagation(); toggleCollapse(sem.id); }}>
                                            <i className={`fa-solid fa-chevron-${collapsedSems.has(sem.id) ? 'right' : 'down'}`} />
                                        </button>
                                        <span>{sem.label}</span>
                                        <span className="prog-semestre-count">{sem.cours.length}</span>
                                    </div>

                                    {!collapsedSems.has(sem.id) && <>
                                        {/* ── UE cards — mode sans parcours + mode tabs ── */}
                                        {(sem.parcours.length === 0 || pnViewMode === 'tabs') && <>
                                            {sem.parcours.length > 0 && (
                                                <div className="prog-pn-parc-pills">
                                                    {[{ id: null, _tc: true }, ...sem.parcours].map(p => {
                                                        const tabId = p._tc ? null : p.id;
                                                        const cnt = p._tc
                                                            ? semUes.filter(u => !u.parcours_pn_id).length
                                                            : semUes.filter(u => u.parcours_pn_id === p.id).length;
                                                        return (
                                                            <button key={p._tc ? 'tc' : p.id}
                                                                className={`prog-pn-parc-pill${tabId === (parcoursTabs[sem.id] ?? null) ? ' active' : ''}${p._tc ? ' tc' : ''}`}
                                                                onClick={() => setParcoursTabs(prev => ({ ...prev, [sem.id]: tabId }))}>
                                                                <i className={`fa-solid ${p._tc ? 'fa-users' : 'fa-code-branch'}`} />
                                                                {p._tc ? 'Tronc commun' : p.code}
                                                                <span className="prog-pn-parc-pill-count">{cnt}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {(sem.parcours.length === 0
                                                ? semUes
                                                : (parcoursTabs[sem.id] ?? null) === null
                                                    ? semUes.filter(u => !u.parcours_pn_id)
                                                    : semUes.filter(u => u.parcours_pn_id === parcoursTabs[sem.id])
                                            ).map(ue => {
                                            const ueIdx = semUes.indexOf(ue);
                                            const linkedCours = sem.cours.filter(c => !c.is_transversal && ue.pnIds.has(c.id));
                                            const displayedCours = [...transversaux, ...linkedCours];
                                            const isDropTarget = dragOverTarget?.type === 'ue' && dragOverTarget.id === ue.id;
                                            const isUeReorderTarget = dragUeKey?.semId === sem.id && dragUeKey?.ueIdx !== ueIdx;
                                            const isUeDragging = dragUeKey?.semId === sem.id && dragUeKey?.ueIdx === ueIdx;
                                            return (
                                                <div key={ue.id}
                                                    className={`pn-ue-card${isDropTarget ? ' pn-ue-drop-active' : ''}${isUeDragging ? ' pn-ue-dragging' : ''}${isUeReorderTarget ? ' pn-ue-reorder-target' : ''}`}
                                                    onDragOver={e => {
                                                        e.preventDefault(); e.stopPropagation();
                                                        if (dragUeKey?.semId === sem.id) return; // reorder en cours, pas de drop contenu
                                                        setDragOverTarget({ type: 'ue', id: ue.id });
                                                    }}
                                                    onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverTarget(null); }}
                                                    onDrop={e => {
                                                        if (dragUeKey?.semId === sem.id) { handleUeReorderDrop(e, sem.id, ueIdx); }
                                                        else { handleDropOnUe(e, ue.id); }
                                                    }}
                                                    onDragEnd={() => setDragUeKey(null)}
                                                >
                                                    {/* En-tête UE */}
                                                    {editUeInline?.id === ue.id ? (
                                                        <div className="pn-ue-edit-row">
                                                            <input className="pn-ue-edit-code" value={editUeInline.code}
                                                                onChange={e => setEditUeInline(p => ({ ...p, code: e.target.value }))}
                                                                placeholder="Code" />
                                                            <input className="pn-ue-edit-nom" value={editUeInline.nom}
                                                                onChange={e => setEditUeInline(p => ({ ...p, nom: e.target.value }))}
                                                                placeholder="Nom" onKeyDown={e => e.key === 'Enter' && handleUpdateUe()} />
                                                            <button className="pn-ue-btn pn-ue-btn-save" onClick={handleUpdateUe}><i className="fa-solid fa-check" /></button>
                                                            <button className="pn-ue-btn pn-ue-btn-cancel" onClick={() => setEditUeInline(null)}><i className="fa-solid fa-xmark" /></button>
                                                        </div>
                                                    ) : (
                                                        <div className="pn-ue-header"
                                                            draggable
                                                            onDragStart={e => {
                                                                e.stopPropagation();
                                                                setDragUeKey({ semId: sem.id, ueIdx });
                                                                e.dataTransfer.effectAllowed = 'move';
                                                            }}
                                                            onClick={() => toggleUe(ue.id)}
                                                        >
                                                            <input type="checkbox"
                                                                className="prog-pn-res-check"
                                                                checked={selectedUes.has(ue.id)}
                                                                onChange={() => toggleSelectUe(ue.id)}
                                                                onClick={e => e.stopPropagation()}
                                                                style={{ marginRight: 2 }}
                                                            />
                                                            <i className="fa-solid fa-grip-vertical pn-ue-grip" />
                                                            <i className={`fa-solid fa-chevron-${collapsedUes.has(ue.id) ? 'right' : 'down'} pn-ue-chevron`} />
                                                            <span className="pn-ue-code">{ue.code}</span>
                                                            <span className="pn-ue-nom">{ue.nom}</span>
                                                            {collapsedUes.has(ue.id) && (
                                                                <span className="pn-ue-count">{displayedCours.length}</span>
                                                            )}
                                                            <div className="pn-ue-actions">
                                                                <button className="pn-ue-btn pn-ue-btn-edit" title="Modifier"
                                                                    onClick={e => { e.stopPropagation(); setEditUeInline({ id: ue.id, code: ue.code, nom: ue.nom }); }}>
                                                                    <i className="fa-solid fa-pen" />
                                                                </button>
                                                                <button className="pn-ue-btn pn-ue-btn-del" title="Supprimer"
                                                                    onClick={e => { e.stopPropagation(); handleDeleteUe(ue.id); }}>
                                                                    <i className="fa-solid fa-trash" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Cours dans l'UE */}
                                                    {!collapsedUes.has(ue.id) && <div className="pn-ue-body">
                                                        {displayedCours.length === 0 && (
                                                            <div className="pn-ue-empty">Glissez des cours ici</div>
                                                        )}
                                                        {displayedCours.map(pn => (
                                                            <div key={pn.id}
                                                                className={`pn-ue-row${pn.is_transversal ? ' pn-ue-row-transversal' : ''}${dragPnInfo?.pnId === pn.id ? ' pn-row-dragging' : ''}`}
                                                                draggable={!pn.is_transversal}
                                                                onDragStart={!pn.is_transversal ? e => {
                                                                    e.stopPropagation();
                                                                    e.dataTransfer.setData('application/pn-link', JSON.stringify({ pnId: pn.id, sourceUeId: ue.id }));
                                                                    setDragPnInfo({ pnId: pn.id, sourceUeId: ue.id });
                                                                } : undefined}
                                                                onDragEnd={() => setDragPnInfo(null)}
                                                            >
                                                                {!pn.is_transversal && (
                                                                    <input type="checkbox" className="prog-pn-res-check"
                                                                        checked={selected.has(pn.id)}
                                                                        onChange={() => toggleSelect(pn.id)}
                                                                        onClick={e => e.stopPropagation()}
                                                                    />
                                                                )}
                                                                <button className={`pn-transversal-btn${pn.is_transversal ? ' active' : ''}`}
                                                                    title={pn.is_transversal ? 'Transversal — cliquer pour désactiver' : 'Rendre transversal'}
                                                                    onClick={e => { e.stopPropagation(); handleToggleTransversal(pn); }}>
                                                                    <i className={pn.is_transversal ? 'fa-solid fa-star' : 'fa-regular fa-star'} />
                                                                </button>
                                                                {!pn.is_transversal && <i className="fa-solid fa-grip-vertical pn-row-grip" />}
                                                                <code className="prog-code-inline prog-pn-code">{pn.code}</code>
                                                                <span className="prog-pn-res-nom" title={pn.nom}>{pn.nom}</span>
                                                                <span className="prog-pn-res-h">{pn.heures_totales > 0 ? `${pn.heures_totales}h` : '—'}</span>
                                                                <div className="prog-pn-res-vols">
                                                                    {(pn.volumes_min || []).map(v => (
                                                                        <span key={v.modalite_id} className="prog-pn-chip">{v.modalite_code} {v.heures_min}h</span>
                                                                    ))}
                                                                </div>
                                                                <div className="prog-pn-res-actions">
                                                                    <button className="prog-action-btn prog-action-edit"
                                                                        onClick={e => { e.stopPropagation(); setItemEdit(pn); setModalOpen(true); }}>
                                                                        <i className="fa-solid fa-pen" />
                                                                    </button>
                                                                    <button className="prog-action-btn prog-action-delete"
                                                                        onClick={async e => { e.stopPropagation(); if (window.confirm(`Supprimer "${pn.code}" ?`)) { await programmeNationalAPI.delete(pn.id); load(); } }}>
                                                                        <i className="fa-solid fa-trash" />
                                                                    </button>
                                                                    {!pn.is_transversal && (
                                                                        <button className="prog-action-btn pn-btn-unlink" title="Retirer de l'UE"
                                                                            onClick={async e => { e.stopPropagation(); await ueAPI.unlinkPN(ue.id, pn.id); load(); }}>
                                                                            <i className="fa-solid fa-link-slash" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>}

                                                    {/* Créer un cours dans cette UE */}
                                                    {!collapsedUes.has(ue.id) && (
                                                        <button className="pn-ue-add-cours"
                                                            onClick={() => { setPendingUeId(ue.id); setItemEdit({ semestre_config_id: sem.id }); setModalOpen(true); }}>
                                                            <i className="fa-solid fa-plus" /> Créer un cours ici
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        </>}

                                        {/* ── UE cards — mode avec parcours : colonnes parallèles (mode "Tout afficher") ── */}
                                        {sem.parcours.length > 0 && pnViewMode === 'all' && (
                                            <div className="prog-pn-parcours-cols">
                                                {[{ id: null, code: 'Tronc commun', nom: '', _tc: true }, ...sem.parcours].map(parc => {
                                                    const formKey = parc._tc ? `${sem.id}||tc` : `${sem.id}||${parc.id}`;
                                                    const filteredUes = parc._tc
                                                        ? semUes.filter(u => !u.parcours_pn_id)
                                                        : semUes.filter(u => u.parcours_pn_id === parc.id);
                                                    return (
                                                        <div key={parc._tc ? 'tc' : parc.id} className="prog-pn-parcours-col">
                                                            <div className={`prog-pn-parcours-col-hdr${parc._tc ? ' prog-pn-pc-tc' : ''}`}>
                                                                {parc._tc
                                                                    ? <><i className="fa-solid fa-users" /> Tronc commun</>
                                                                    : <><i className="fa-solid fa-code-branch" /> <strong>{parc.code}</strong>{parc.nom ? <span className="prog-pn-pc-nom"> — {parc.nom}</span> : null}</>
                                                                }
                                                                <span className="prog-pn-pc-count">{filteredUes.length}</span>
                                                            </div>
                                                            {filteredUes.map(ue => {
                                                                const ueIdx = semUes.indexOf(ue);
                                                                const linkedCours = sem.cours.filter(c => !c.is_transversal && ue.pnIds.has(c.id));
                                                                const displayedCours = [...transversaux, ...linkedCours];
                                                                const isDropTarget = dragOverTarget?.type === 'ue' && dragOverTarget.id === ue.id;
                                                                const isUeReorderTarget = dragUeKey?.semId === sem.id && dragUeKey?.ueIdx !== ueIdx;
                                                                const isUeDragging = dragUeKey?.semId === sem.id && dragUeKey?.ueIdx === ueIdx;
                                                                return (
                                                                    <div key={ue.id}
                                                                        className={`pn-ue-card${isDropTarget ? ' pn-ue-drop-active' : ''}${isUeDragging ? ' pn-ue-dragging' : ''}${isUeReorderTarget ? ' pn-ue-reorder-target' : ''}`}
                                                                        onDragOver={e => {
                                                                            e.preventDefault(); e.stopPropagation();
                                                                            if (dragUeKey?.semId === sem.id) return;
                                                                            setDragOverTarget({ type: 'ue', id: ue.id });
                                                                        }}
                                                                        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverTarget(null); }}
                                                                        onDrop={e => {
                                                                            if (dragUeKey?.semId === sem.id) { handleUeReorderDrop(e, sem.id, ueIdx); }
                                                                            else { handleDropOnUe(e, ue.id); }
                                                                        }}
                                                                        onDragEnd={() => setDragUeKey(null)}
                                                                    >
                                                                        {editUeInline?.id === ue.id ? (
                                                                            <div className="pn-ue-edit-row">
                                                                                <input className="pn-ue-edit-code" value={editUeInline.code}
                                                                                    onChange={e => setEditUeInline(p => ({ ...p, code: e.target.value }))}
                                                                                    placeholder="Code" />
                                                                                <input className="pn-ue-edit-nom" value={editUeInline.nom}
                                                                                    onChange={e => setEditUeInline(p => ({ ...p, nom: e.target.value }))}
                                                                                    placeholder="Nom" onKeyDown={e => e.key === 'Enter' && handleUpdateUe()} />
                                                                                <button className="pn-ue-btn pn-ue-btn-save" onClick={handleUpdateUe}><i className="fa-solid fa-check" /></button>
                                                                                <button className="pn-ue-btn pn-ue-btn-cancel" onClick={() => setEditUeInline(null)}><i className="fa-solid fa-xmark" /></button>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="pn-ue-header"
                                                                                draggable
                                                                                onDragStart={e => {
                                                                                    e.stopPropagation();
                                                                                    setDragUeKey({ semId: sem.id, ueIdx });
                                                                                    e.dataTransfer.effectAllowed = 'move';
                                                                                }}
                                                                                onClick={() => toggleUe(ue.id)}
                                                                            >
                                                                                <input type="checkbox"
                                                                                    className="prog-pn-res-check"
                                                                                    checked={selectedUes.has(ue.id)}
                                                                                    onChange={() => toggleSelectUe(ue.id)}
                                                                                    onClick={e => e.stopPropagation()}
                                                                                    style={{ marginRight: 2 }}
                                                                                />
                                                                                <i className="fa-solid fa-grip-vertical pn-ue-grip" />
                                                                                <i className={`fa-solid fa-chevron-${collapsedUes.has(ue.id) ? 'right' : 'down'} pn-ue-chevron`} />
                                                                                <span className="pn-ue-code">{ue.code}</span>
                                                                                <span className="pn-ue-nom">{ue.nom}</span>
                                                                                {collapsedUes.has(ue.id) && (
                                                                                    <span className="pn-ue-count">{displayedCours.length}</span>
                                                                                )}
                                                                                <div className="pn-ue-actions">
                                                                                    <button className="pn-ue-btn pn-ue-btn-edit" title="Modifier"
                                                                                        onClick={e => { e.stopPropagation(); setEditUeInline({ id: ue.id, code: ue.code, nom: ue.nom }); }}>
                                                                                        <i className="fa-solid fa-pen" />
                                                                                    </button>
                                                                                    <button className="pn-ue-btn pn-ue-btn-del" title="Supprimer"
                                                                                        onClick={e => { e.stopPropagation(); handleDeleteUe(ue.id); }}>
                                                                                        <i className="fa-solid fa-trash" />
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {!collapsedUes.has(ue.id) && <div className="pn-ue-body">
                                                                            {displayedCours.length === 0 && (
                                                                                <div className="pn-ue-empty">Glissez des cours ici</div>
                                                                            )}
                                                                            {displayedCours.map(pn => (
                                                                                <div key={pn.id}
                                                                                    className={`pn-ue-row${pn.is_transversal ? ' pn-ue-row-transversal' : ''}${dragPnInfo?.pnId === pn.id ? ' pn-row-dragging' : ''}`}
                                                                                    draggable={!pn.is_transversal}
                                                                                    onDragStart={!pn.is_transversal ? e => {
                                                                                        e.stopPropagation();
                                                                                        e.dataTransfer.setData('application/pn-link', JSON.stringify({ pnId: pn.id, sourceUeId: ue.id }));
                                                                                        setDragPnInfo({ pnId: pn.id, sourceUeId: ue.id });
                                                                                    } : undefined}
                                                                                    onDragEnd={() => setDragPnInfo(null)}
                                                                                >
                                                                                    {!pn.is_transversal && (
                                                                                        <input type="checkbox" className="prog-pn-res-check"
                                                                                            checked={selected.has(pn.id)}
                                                                                            onChange={() => toggleSelect(pn.id)}
                                                                                            onClick={e => e.stopPropagation()}
                                                                                        />
                                                                                    )}
                                                                                    <button className={`pn-transversal-btn${pn.is_transversal ? ' active' : ''}`}
                                                                                        title={pn.is_transversal ? 'Transversal — cliquer pour désactiver' : 'Rendre transversal'}
                                                                                        onClick={e => { e.stopPropagation(); handleToggleTransversal(pn); }}>
                                                                                        <span className="pn-transversal-t">T</span>
                                                                                    </button>
                                                                                    {!pn.is_transversal && <i className="fa-solid fa-grip-vertical pn-row-grip" />}
                                                                                    <code className="prog-code-inline prog-pn-code">{pn.code}</code>
                                                                                    <span className="prog-pn-res-nom" title={pn.nom}>{pn.nom}</span>
                                                                                    <span className="prog-pn-res-h">{pn.heures_totales > 0 ? `${pn.heures_totales}h` : '—'}</span>
                                                                                    <div className="prog-pn-res-vols">
                                                                                        {(pn.volumes_min || []).map(v => (
                                                                                            <span key={v.modalite_id} className="prog-pn-chip">{v.modalite_code} {v.heures_min}h</span>
                                                                                        ))}
                                                                                    </div>
                                                                                    <div className="prog-pn-res-actions">
                                                                                        <button className="prog-action-btn prog-action-edit"
                                                                                            onClick={e => { e.stopPropagation(); setItemEdit(pn); setModalOpen(true); }}>
                                                                                            <i className="fa-solid fa-pen" />
                                                                                        </button>
                                                                                        <button className="prog-action-btn prog-action-delete"
                                                                                            onClick={async e => { e.stopPropagation(); if (window.confirm(`Supprimer "${pn.code}" ?`)) { await programmeNationalAPI.delete(pn.id); load(); } }}>
                                                                                            <i className="fa-solid fa-trash" />
                                                                                        </button>
                                                                                        {!pn.is_transversal && (
                                                                                            <button className="prog-action-btn pn-btn-unlink" title="Retirer de l'UE"
                                                                                                onClick={async e => { e.stopPropagation(); await ueAPI.unlinkPN(ue.id, pn.id); load(); }}>
                                                                                                <i className="fa-solid fa-link-slash" />
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>}
                                                                        {!collapsedUes.has(ue.id) && (
                                                                            <button className="pn-ue-add-cours"
                                                                                onClick={() => { setPendingUeId(ue.id); setItemEdit({ semestre_config_id: sem.id }); setModalOpen(true); }}>
                                                                                <i className="fa-solid fa-plus" /> Créer un cours ici
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                            {/* Nouvelle UE pour cette colonne parcours */}
                                                            {newUeForm[formKey] ? (
                                                                <div className="pn-new-ue-form">
                                                                    <input className="pn-ue-edit-code" placeholder="Code (ex: UE1.1)"
                                                                        value={newUeForm[formKey]?.code || ''}
                                                                        onChange={e => setNewUeForm(p => ({ ...p, [formKey]: { ...p[formKey], code: e.target.value } }))} />
                                                                    <input className="pn-ue-edit-nom" placeholder="Nom de l'UE"
                                                                        value={newUeForm[formKey]?.nom || ''}
                                                                        onChange={e => setNewUeForm(p => ({ ...p, [formKey]: { ...p[formKey], nom: e.target.value } }))}
                                                                        onKeyDown={e => e.key === 'Enter' && handleCreateUe(sem.id, { formKey, parcoursPnId: parc._tc ? null : parc.id })} />
                                                                    <button className="pn-ue-btn pn-ue-btn-save" onClick={() => handleCreateUe(sem.id, { formKey, parcoursPnId: parc._tc ? null : parc.id })}>
                                                                        <i className="fa-solid fa-check" /> Créer
                                                                    </button>
                                                                    <button className="pn-ue-btn pn-ue-btn-cancel"
                                                                        onClick={() => setNewUeForm(p => { const n = { ...p }; delete n[formKey]; return n; })}>
                                                                        <i className="fa-solid fa-xmark" />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <button className="pn-add-ue-btn"
                                                                    onClick={() => setNewUeForm(p => ({ ...p, [formKey]: { code: '', nom: '' } }))}>
                                                                    <i className="fa-solid fa-plus" /> Nouvelle UE
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* ── Pool cours non assignés ── */}
                                        <div className={`pn-pool${dragOverTarget?.type === 'pool' && dragOverTarget.id === sem.id ? ' pn-pool-drop-active' : ''}`}
                                            onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverTarget({ type: 'pool', id: sem.id }); }}
                                            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverTarget(null); }}
                                            onDrop={handleDropOnPool}
                                        >
                                            <div className="pn-pool-header"
                                                onClick={() => setCollapsedPools(s => { const n = new Set(s); n.has(sem.id) ? n.delete(sem.id) : n.add(sem.id); return n; })}>
                                                <i className={`fa-solid fa-chevron-${collapsedPools.has(sem.id) ? 'right' : 'down'}`} />
                                                <span>Cours non assignés</span>
                                                {poolCours.length > 0 && <span className="pn-pool-count">{poolCours.length}</span>}
                                            </div>
                                            {!collapsedPools.has(sem.id) && (
                                                <div className="pn-pool-body">
                                                    {poolCours.length === 0 && semUes.length > 0 && (
                                                        <p className="pn-pool-empty">Tous les cours sont dans une UE</p>
                                                    )}
                                                    {poolCours.map(pn => (
                                                        <div key={pn.id}
                                                            className={`prog-pn-res-row${selected.has(pn.id) ? ' selected-row' : ''}${dragPnInfo?.pnId === pn.id ? ' pn-row-dragging' : ''}`}
                                                            draggable
                                                            onDragStart={e => {
                                                                e.stopPropagation();
                                                                e.dataTransfer.setData('application/pn-link', JSON.stringify({ pnId: pn.id, sourceUeId: null }));
                                                                setDragPnInfo({ pnId: pn.id, sourceUeId: null });
                                                            }}
                                                            onDragEnd={() => setDragPnInfo(null)}
                                                        >
                                                            <input type="checkbox" className="prog-pn-res-check"
                                                                checked={selected.has(pn.id)}
                                                                onChange={() => toggleSelect(pn.id)}
                                                                onClick={e => e.stopPropagation()} />
                                                            <button className={`pn-transversal-btn${pn.is_transversal ? ' active' : ''}`}
                                                                title={pn.is_transversal ? 'Transversal — cliquer pour désactiver' : 'Rendre transversal'}
                                                                onClick={e => { e.stopPropagation(); handleToggleTransversal(pn); }}>
                                                                <i className={pn.is_transversal ? 'fa-solid fa-star' : 'fa-regular fa-star'} />
                                                            </button>
                                                            <i className="fa-solid fa-grip-vertical prog-pn-res-grip" />
                                                            <code className="prog-code-inline prog-pn-code">{pn.code}</code>
                                                            <span className="prog-pn-res-nom" title={pn.nom}>{pn.nom}</span>
                                                            <span className="prog-pn-res-h">{pn.heures_totales > 0 ? `${pn.heures_totales}h` : '—'}</span>
                                                            <div className="prog-pn-res-vols">
                                                                {(pn.volumes_min || []).map(v => (
                                                                    <span key={v.modalite_id} className="prog-pn-chip">{v.modalite_code} {v.heures_min}h</span>
                                                                ))}
                                                            </div>
                                                            <div className="prog-pn-res-actions">
                                                                <button className="prog-action-btn prog-action-edit"
                                                                    onClick={e => { e.stopPropagation(); setItemEdit(pn); setModalOpen(true); }}>
                                                                    <i className="fa-solid fa-pen" />
                                                                </button>
                                                                <button className="prog-action-btn prog-action-delete"
                                                                    onClick={async e => { e.stopPropagation(); if (window.confirm(`Supprimer "${pn.code}" ?`)) { await programmeNationalAPI.delete(pn.id); load(); } }}>
                                                                    <i className="fa-solid fa-trash" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <button className="prog-pn-add-res"
                                                        onClick={() => { setPendingUeId(null); setItemEdit({ semestre_config_id: sem.id }); setModalOpen(true); }}>
                                                        <i className="fa-solid fa-plus" /> Ressource
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* ── Nouvelle UE (mode sans parcours + mode tabs) ── */}
                                        {(sem.parcours.length === 0 || pnViewMode === 'tabs') && (() => {
                                            const activeTab = sem.parcours.length > 0 ? (parcoursTabs[sem.id] ?? null) : undefined;
                                            const formKey = sem.parcours.length === 0
                                                ? String(sem.id)
                                                : (activeTab === null ? `${sem.id}||tc` : `${sem.id}||${activeTab}`);
                                            const parcoursPnId = activeTab;
                                            return newUeForm[formKey] ? (
                                                <div className="pn-new-ue-form">
                                                    <input className="pn-ue-edit-code" placeholder="Code (ex: UE1.1)"
                                                        value={newUeForm[formKey]?.code || ''}
                                                        onChange={e => setNewUeForm(p => ({ ...p, [formKey]: { ...p[formKey], code: e.target.value } }))} />
                                                    <input className="pn-ue-edit-nom" placeholder="Nom de l'UE"
                                                        value={newUeForm[formKey]?.nom || ''}
                                                        onChange={e => setNewUeForm(p => ({ ...p, [formKey]: { ...p[formKey], nom: e.target.value } }))}
                                                        onKeyDown={e => e.key === 'Enter' && handleCreateUe(sem.id, sem.parcours.length > 0 ? { formKey, parcoursPnId } : {})} />
                                                    <button className="pn-ue-btn pn-ue-btn-save"
                                                        onClick={() => handleCreateUe(sem.id, sem.parcours.length > 0 ? { formKey, parcoursPnId } : {})}>
                                                        <i className="fa-solid fa-check" /> Créer
                                                    </button>
                                                    <button className="pn-ue-btn pn-ue-btn-cancel"
                                                        onClick={() => setNewUeForm(p => { const n = { ...p }; delete n[formKey]; return n; })}>
                                                        <i className="fa-solid fa-xmark" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button className="pn-add-ue-btn"
                                                    onClick={() => setNewUeForm(p => ({ ...p, [formKey]: { code: '', nom: '' } }))}>
                                                    <i className="fa-solid fa-plus" /> Nouvelle UE
                                                </button>
                                            );
                                        })()}
                                    </>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
            </>)}

            {pnTab === 'semestres' && (
                <div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        <button className="prog-btn prog-btn-primary prog-btn-sm"
                            onClick={() => setSemModal({ item: null })}>
                            <i className="fa-solid fa-plus" /> Ajouter un semestre
                        </button>
                    </div>
                    {semErrDelete && (
                        <div className="prog-alert prog-alert-error" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span>{semErrDelete}</span>
                            <button onClick={() => setSemErrDelete(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16 }}>
                                <i className="fa-solid fa-xmark" />
                            </button>
                        </div>
                    )}
                    {semestres.length === 0 ? (
                        <div className="prog-empty prog-empty-sm"><p>Aucun semestre. Cliquez sur Ajouter pour commencer.</p></div>
                    ) : (
                        <table className="prog-table prog-table-sm">
                            <thead>
                                <tr>
                                    <th>Sous-niveau</th>
                                    <th>Semestre</th>
                                    <th>Présentiel (h)</th>
                                    <th>Autonomie (h)</th>
                                    <th>Parcours actifs</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {semestres.map(item => (
                                    <tr key={item.id}>
                                        <td><span className="prog-meta-tag">{item.sous_niveau_code}</span></td>
                                        <td><code className="prog-code-inline">{item.semestre}</code></td>
                                        <td>{item.heures_presentiel}</td>
                                        <td>{item.heures_autonomie}</td>
                                        <td>
                                            {parcoursPn.length === 0 ? (
                                                <span style={{ color: '#94a3b8', fontSize: 12 }}>Aucun parcours défini</span>
                                            ) : (
                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                    {parcoursPn.map(p => {
                                                        const actif = p.semestres?.some(s => s.semestre_config_id === item.id);
                                                        return (
                                                            <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
                                                                <input type="checkbox" checked={!!actif}
                                                                    onChange={e => toggleSemestreParcours(item.id, p.id, e.target.checked)} />
                                                                <span style={{ background: actif ? '#e0e7ff' : '#f1f5f9', color: actif ? '#3730a3' : '#64748b', borderRadius: 4, padding: '1px 6px' }}>
                                                                    {p.code}
                                                                </span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </td>
                                        <td className="prog-col-actions">
                                            <button className="prog-action-btn prog-action-edit"
                                                onClick={() => setSemModal({ item })}>
                                                <i className="fa-solid fa-pen" />
                                            </button>
                                            <button className="prog-action-btn prog-action-delete"
                                                onClick={() => handleSemDelete(item.id)}>
                                                <i className="fa-solid fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* ── Section Parcours du département ── */}
                    <div style={{ marginTop: 28, borderTop: '1px solid #e2e8f0', paddingTop: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#374151' }}>
                                <i className="fa-solid fa-code-branch" style={{ marginRight: 6, color: '#6366f1' }} />
                                Parcours du département
                            </h4>
                            <button className="prog-btn prog-btn-primary prog-btn-sm" style={{ marginLeft: 'auto' }}
                                onClick={() => { setParcoursModal({ item: null }); setParcoursErr(null); }}>
                                <i className="fa-solid fa-plus" /> Nouveau parcours
                            </button>
                        </div>
                        {parcoursPn.length === 0 ? (
                            <p style={{ color: '#94a3b8', fontSize: 13, margin: 0 }}>
                                Aucun parcours défini. Créez des parcours puis activez-les sur chaque semestre concerné.
                            </p>
                        ) : (
                            <table className="prog-table prog-table-sm">
                                <thead>
                                    <tr>
                                        <th>Code</th>
                                        <th>Nom</th>
                                        <th>Semestres actifs</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {parcoursPn.map(p => (
                                        <tr key={p.id}>
                                            <td><code className="prog-code-inline">{p.code}</code></td>
                                            <td>{p.nom}</td>
                                            <td>
                                                {(p.semestres || []).length === 0
                                                    ? <span style={{ color: '#94a3b8', fontSize: 12 }}>Aucun</span>
                                                    : (p.semestres || []).map(s => (
                                                        <span key={s.semestre_config_id} style={{ marginRight: 4, background: '#e0e7ff', color: '#3730a3', borderRadius: 4, padding: '1px 6px', fontSize: 12 }}>
                                                            {s.sous_niveau_code} {s.semestre}
                                                        </span>
                                                    ))
                                                }
                                            </td>
                                            <td className="prog-col-actions">
                                                <button className="prog-action-btn prog-action-edit"
                                                    onClick={() => { setParcoursModal({ item: p }); setParcoursErr(null); }}>
                                                    <i className="fa-solid fa-pen" />
                                                </button>
                                                <button className="prog-action-btn prog-action-delete"
                                                    onClick={() => handleParcoursDelete(p.id)}>
                                                    <i className="fa-solid fa-trash" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* ── Modale Parcours PN ── */}
            {parcoursModal !== null && (
                <div className="prog-overlay" onClick={() => setParcoursModal(null)}>
                    <div className="prog-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
                        <div className="prog-modal-header">
                            <h3>{parcoursModal.item ? 'Modifier le parcours' : 'Nouveau parcours'}</h3>
                            <button className="prog-modal-close" onClick={() => setParcoursModal(null)}><i className="fa-solid fa-xmark" /></button>
                        </div>
                        <div className="prog-modal-body">
                            {parcoursErr && <div className="prog-alert prog-alert-error">{parcoursErr}</div>}
                            <ParcoursForm
                                item={parcoursModal.item}
                                onSave={handleParcoursSave}
                                onCancel={() => setParcoursModal(null)}
                                saving={parcoursSaving}
                            />
                        </div>
                    </div>
                </div>
            )}

            {modalOpen && (
                <ModalPN
                    pn={itemEdit}
                    semestres={semestres}
                    modalites={modalites}
                    departementId={deptId}
                    anneeId={anneeId}
                    onSave={async (newId) => {
                        if (pendingUeId && newId && !itemEdit?.id) {
                            try { await ueAPI.linkPN(pendingUeId, newId); } catch {}
                        }
                        setPendingUeId(null);
                        setModalOpen(false);
                        setItemEdit(null);
                        load();
                    }}
                    onClose={() => { setPendingUeId(null); setModalOpen(false); setItemEdit(null); }}
                />
            )}

            {importOpen && (
                <ImportModal
                    titre="Programme national"
                    onPreview={handlePreview}
                    contextFields={[{
                        key:      'semestre_config_id',
                        label:    'Semestre',
                        required: true,
                        options:  semestres.map(s => ({
                            value: s.id,
                            label: `${s.sous_niveau_code} — ${s.semestre}`
                        }))
                    }]}
                    champs={[
                        { key: 'code',           label: 'Code national',   required: true },
                        { key: 'nom',            label: 'Intitulé',        required: true },
                        { key: 'heures_totales', label: 'Total heures (h)' },
                        { key: 'parcours_id',    label: 'ID Parcours' },
                        { key: 'coefficient',    label: 'Coefficient' },
                        { key: 'description',    label: 'Description' },
                        ...modalites.map(m => ({ key: `vol_${m.code}`, label: `Min ${m.code} (h)` })),
                    ]}
                    onImport={async (rows) => {
                        const res = await importAPI.importer('programme_national', deptId, anneeId, rows);
                        load();
                        return res.data;
                    }}
                    onClose={() => setImportOpen(false)}
                />
            )}

            {/* ── Modale Récupérer tout depuis N-1 ── */}
            {rtOpen && (
                <div className="prog-overlay" onClick={() => !rtExecuting && setRtOpen(false)}>
                    <div className="prog-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
                        <div className="prog-modal-header">
                            <h3><i className="fa-solid fa-clock-rotate-left" style={{ marginRight: 8 }} />Récupérer tout depuis N-1</h3>
                            <button className="prog-modal-close" onClick={() => setRtOpen(false)} disabled={rtExecuting}><i className="fa-solid fa-xmark" /></button>
                        </div>
                        <div className="prog-modal-body">
                            {rtError && <div className="prog-alert prog-alert-error">{rtError}</div>}
                            {rtResult ? (
                                <>
                                    <div className="prog-alert prog-alert-success">
                                        <i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} />
                                        Récupération terminée —{' '}
                                        {rtResult.stats?.semestres > 0 && <span>{rtResult.stats.semestres} semestre{rtResult.stats.semestres > 1 ? 's' : ''} · </span>}
                                        {rtResult.stats?.parcours > 0 && <span>{rtResult.stats.parcours} parcours · </span>}
                                        {rtResult.stats?.famillesOptions > 0 && <span>{rtResult.stats.famillesOptions} famille{rtResult.stats.famillesOptions > 1 ? 's' : ''} d'options · </span>}
                                        {rtResult.stats?.pn > 0 && <span>{rtResult.stats.pn} cours PN · </span>}
                                        {rtResult.stats?.ues > 0 && <span>{rtResult.stats.ues} UE{rtResult.stats.ues > 1 ? 's' : ''} · </span>}
                                        {rtResult.stats?.cours > 0 && <span>{rtResult.stats.cours} cours local{rtResult.stats.cours > 1 ? 'aux' : ''} · </span>}
                                        {rtResult.stats?.enseignants > 0 && <span>{rtResult.stats.enseignants} enseignant{rtResult.stats.enseignants > 1 ? 's' : ''}</span>}
                                        {(rtResult.stats?.semestres || 0) + (rtResult.stats?.parcours || 0) + (rtResult.stats?.famillesOptions || 0) + (rtResult.stats?.pn || 0) + (rtResult.stats?.ues || 0) + (rtResult.stats?.cours || 0) + (rtResult.stats?.enseignants || 0) === 0 && <span>Tout était déjà à jour.</span>}
                                        {rtResult.errors?.length > 0 && (
                                            <div style={{ marginTop: 6, color: '#dc2626', fontSize: 12 }}>
                                                {rtResult.errors.length} avertissement{rtResult.errors.length > 1 ? 's' : ''} : {rtResult.errors.slice(0, 3).join(' · ')}
                                            </div>
                                        )}
                                    </div>
                                    <div className="prog-modal-actions">
                                        <button className="prog-btn prog-btn-primary" onClick={() => setRtOpen(false)}>
                                            <i className="fa-solid fa-check" /> Fermer
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Sélection de l'année source */}
                                    <div className="prog-form-group">
                                        <label>Année source</label>
                                        <select value={rtSource} onChange={e => {
                                            setRtSource(e.target.value);
                                            loadRtPreview(e.target.value);
                                        }}>
                                            <option value="">— Choisir l'année source —</option>
                                            {(annees || [])
                                                .filter(a => a.id !== anneeId)
                                                .sort((a, b) => b.id - a.id)
                                                .map(a => <option key={a.id} value={a.id}>{a.libelle}</option>)
                                            }
                                        </select>
                                    </div>

                                    {/* Aperçu de la source */}
                                    {rtPreview && (
                                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#374151', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                            <span><i className="fa-solid fa-calendar-week" style={{ marginRight: 5, color: '#6366f1' }} /><strong>{rtPreview.nbSemestres}</strong> semestre{rtPreview.nbSemestres !== 1 ? 's' : ''}</span>
                                            <span><i className="fa-solid fa-scroll" style={{ marginRight: 5, color: '#6366f1' }} /><strong>{rtPreview.nbPN}</strong> cours PN</span>
                                            <span><i className="fa-solid fa-layer-group" style={{ marginRight: 5, color: '#6366f1' }} /><strong>{rtPreview.nbUEs}</strong> UE{rtPreview.nbUEs !== 1 ? 's' : ''}</span>
                                            {rtPreview.nbParcours > 0 && (
                                                <span><i className="fa-solid fa-code-branch" style={{ marginRight: 5, color: '#6366f1' }} /><strong>{rtPreview.nbParcours}</strong> parcours</span>
                                            )}
                                            {rtPreview.nbFamillesOptions > 0 && (
                                                <span><i className="fa-solid fa-sitemap" style={{ marginRight: 5, color: '#6366f1' }} /><strong>{rtPreview.nbFamillesOptions}</strong> famille{rtPreview.nbFamillesOptions !== 1 ? 's' : ''} d'options</span>
                                            )}
                                            <span><i className="fa-solid fa-book-open" style={{ marginRight: 5, color: '#6366f1' }} /><strong>{rtPreview.cours?.length ?? 0}</strong> cours local{(rtPreview.cours?.length ?? 0) !== 1 ? 'aux' : ''}</span>
                                        </div>
                                    )}

                                    {/* Options de récupération */}
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 10 }}>Éléments à récupérer</div>

                                        {/* Semestres — toujours */}
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, color: '#6b7280', cursor: 'default' }}>
                                            <input type="checkbox" checked disabled style={{ accentColor: '#94a3b8' }} />
                                            <i className="fa-solid fa-calendar-week" style={{ width: 14 }} />
                                            <span>Semestres <span style={{ fontSize: 11, background: '#e2e8f0', borderRadius: 4, padding: '1px 6px', marginLeft: 4 }}>toujours inclus</span></span>
                                        </label>

                                        {/* Programme national — toujours */}
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, color: '#6b7280', cursor: 'default' }}>
                                            <input type="checkbox" checked disabled style={{ accentColor: '#94a3b8' }} />
                                            <i className="fa-solid fa-scroll" style={{ width: 14 }} />
                                            <span>Programme national, UEs, parcours, familles d'options <span style={{ fontSize: 11, background: '#e2e8f0', borderRadius: 4, padding: '1px 6px', marginLeft: 4 }}>toujours inclus</span></span>
                                        </label>

                                        {/* Cours locaux — optionnel */}
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, color: '#1e293b', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={rtCopyCours}
                                                onChange={e => { setRtCopyCours(e.target.checked); if (!e.target.checked) { setRtCopyVolumes(false); setRtCopyAff(false); } }} />
                                            <i className="fa-solid fa-book-open" style={{ width: 14 }} />
                                            <span>Cours locaux{rtPreview ? ` (${rtPreview.cours?.length ?? 0})` : ''}</span>
                                        </label>

                                        {/* Volumes — optionnel, indented */}
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, color: rtCopyCours ? '#1e293b' : '#94a3b8', cursor: rtCopyCours ? 'pointer' : 'default', paddingLeft: 22 }}>
                                            <input type="checkbox" checked={rtCopyVolumes} disabled={!rtCopyCours}
                                                onChange={e => { setRtCopyVolumes(e.target.checked); if (!e.target.checked) setRtCopyAff(false); }} />
                                            <i className="fa-solid fa-clock" style={{ width: 14 }} />
                                            <span>Volumes d'heures</span>
                                        </label>

                                        {/* Affectations — optionnel, indented */}
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0, fontSize: 13, color: (rtCopyCours && rtCopyVolumes) ? '#1e293b' : '#94a3b8', cursor: (rtCopyCours && rtCopyVolumes) ? 'pointer' : 'default', paddingLeft: 22 }}>
                                            <input type="checkbox" checked={rtCopyAff} disabled={!rtCopyCours || !rtCopyVolumes}
                                                onChange={e => setRtCopyAff(e.target.checked)} />
                                            <i className="fa-solid fa-user-tie" style={{ width: 14 }} />
                                            <span>Affectations enseignants</span>
                                        </label>
                                    </div>

                                    <div className="prog-modal-actions">
                                        <button className="prog-btn prog-btn-secondary" onClick={() => setRtOpen(false)}>Annuler</button>
                                        <button className="prog-btn prog-btn-primary"
                                            disabled={!rtSource || rtExecuting || (rtPreview && rtPreview.nbPN === 0 && rtPreview.nbSemestres === 0)}
                                            onClick={handleRecupererTout}>
                                            {rtExecuting
                                                ? <><i className="fa-solid fa-spinner fa-spin" /> Récupération en cours...</>
                                                : <><i className="fa-solid fa-clock-rotate-left" /> Récupérer tout</>}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {semModal !== null && (
                <ModalRef
                    titre={semModal.item ? 'Modifier le semestre' : 'Nouveau semestre'}
                    item={semModal.item}
                    champs={[
                        { name: 'sous_niveau_id', label: 'Sous-niveau *', required: true, type: 'select',
                          options: sousNiveauxPN.map(sn => ({ value: sn.id, label: sn.code + ' — ' + sn.nom })) },
                        { name: 'semestre', label: 'Code semestre *', required: true, placeholder: 'ex : S1' },
                        { name: 'heures_presentiel', label: 'Heures présentiel', type: 'number', default: '0' },
                        { name: 'heures_autonomie', label: 'Heures autonomie', type: 'number', default: '0' },
                    ]}
                    onSave={handleSemSave}
                    onClose={() => { setSemModal(null); setSemErrSave(null); }}
                    saving={semSaving}
                    error={semErrSave}
                />
            )}
        </div>
    );
}

// ─── Onglet Référentiels (types cours, modalités, semestres) ──
function OngletReferentiels({ config, anneeId }) {
    const deptId = config?.departement?.id;
    const [tab, setTab] = useState('types');
    const [typesCour, setTypesCour]   = useState([]);
    const [modalites, setModalites]   = useState([]);
    const [loading, setLoading]       = useState(true);
    const [modal, setModal]           = useState(null); // { type, item }
    const [saving, setSaving]         = useState(false);
    const [errSave, setErrSave]       = useState(null);
    const [errDelete, setErrDelete]   = useState(null);
    const [importOpen, setImportOpen] = useState(false);

    const load = useCallback(async () => {
        if (!deptId || !anneeId) return;
        setLoading(true);
        try {
            const [rt, rm] = await Promise.all([
                typesCourAPI.getAll(deptId, anneeId),
                modalitesAPI.getAll(deptId),
            ]);
            setTypesCour(rt.data.types_cours || []);
            setModalites(rm.data.modalites || []);
        } finally { setLoading(false); }
    }, [deptId, anneeId]);

    useEffect(() => { load(); }, [load]);

    const handleSave = async (form) => {
        setSaving(true); setErrSave(null);
        const { type, item } = modal;
        try {
            const payload = { departement_id: deptId, annee_scolaire_id: anneeId, ...form };
            if (type === 'types')    { item ? await typesCourAPI.update(item.id, payload) : await typesCourAPI.create(payload); }
            if (type === 'modalites'){ item ? await modalitesAPI.update(item.id, payload) : await modalitesAPI.create(payload); }
            setModal(null); load();
        } catch (err) { setErrSave(err.response?.data?.error || 'Erreur'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (type, id) => {
        if (!window.confirm('Supprimer cet élément ?')) return;
        setErrDelete(null);
        try {
            if (type === 'types')     await typesCourAPI.delete(id, anneeId);
            if (type === 'modalites') await modalitesAPI.delete(id);
            load();
        } catch (err) {
            setErrDelete(err.response?.data?.error || 'Impossible de supprimer cet élément.');
        }
    };

    const CHAMPS = {
        types: [
            { name: 'code',        label: 'Code',        required: true, placeholder: 'ex : RES' },
            { name: 'nom',         label: 'Libellé',     required: true, placeholder: 'ex : Ressource pédagogique' },
            { name: 'couleur',     label: 'Couleur',     type: 'color',  default: '#6C757D' },
            { name: 'ordre',       label: 'Ordre',       type: 'number', default: '0' },
            { name: 'description', label: 'Description', type: 'textarea' },
        ],
        modalites: [
            { name: 'code',            label: 'Code',               required: true, placeholder: 'ex : CM' },
            { name: 'nom',             label: 'Libellé',            required: true, placeholder: 'ex : Cours Magistral' },
            { name: 'coefficient_htd', label: 'Coefficient hTD',    type: 'number', step: '0.001', default: '1', placeholder: 'ex : 1.5' },
        ],
    };

    const tabs = [
        { key: 'types',     label: 'Catégories de cours',    data: typesCour  },
        { key: 'modalites', label: 'Modalités pédagogiques', data: modalites  },
    ];

    const current = tabs.find(t => t.key === tab);

    return (
        <div className="prog-tab-content">
            <div className="prog-ref-tabs">
                {tabs.map(t => (
                    <button key={t.key}
                        className={`prog-ref-tab ${tab === t.key ? 'active' : ''}`}
                        onClick={() => setTab(t.key)}>
                        {t.label} <span className="prog-ref-count">{t.data.length}</span>
                    </button>
                ))}
            </div>

            {errDelete && (
                <div className="prog-alert prog-alert-error" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <i className="fa-solid fa-triangle-exclamation" />
                    <span>{errDelete}</span>
                    <button onClick={() => setErrDelete(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16 }}>
                        <i className="fa-solid fa-xmark" />
                    </button>
                </div>
            )}

            {loading ? (
                <div className="prog-center"><i className="fa-solid fa-spinner fa-spin prog-spinner" /></div>
            ) : (
                <div className="prog-ref-list">
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        <button className="prog-btn prog-btn-secondary prog-btn-sm"
                            onClick={() => setImportOpen(true)}>
                            <i className="fa-solid fa-file-import" /> Importer
                        </button>
                        <button className="prog-btn prog-btn-primary prog-btn-sm"
                            onClick={() => setModal({ type: tab, item: null })}>
                            <i className="fa-solid fa-plus" /> Ajouter
                        </button>
                    </div>

                    {current?.data.length === 0 ? (
                        <div className="prog-empty prog-empty-sm">
                            <p>Aucun élément. Cliquez sur Ajouter pour commencer.</p>
                        </div>
                    ) : tab === 'modalites' ? (
                        /* ── Liste drag & drop modalités ── */
                        <div className="prog-drag-list">
                            <div className="prog-drag-hint">
                                <i className="fa-solid fa-grip-lines" /> Glissez les lignes pour modifier l'ordre d'affichage
                            </div>
                            {modalites.map((item, idx) => (
                                <div key={item.id}
                                    className="prog-drag-row"
                                    draggable
                                    onDragStart={e => { e.dataTransfer.setData('text/plain', String(idx)); }}
                                    onDragOver={e => e.preventDefault()}
                                    onDrop={async e => {
                                        e.preventDefault();
                                        const fromIdx = parseInt(e.dataTransfer.getData('text/plain'));
                                        if (fromIdx === idx) return;
                                        const next = [...modalites];
                                        const [moved] = next.splice(fromIdx, 1);
                                        next.splice(idx, 0, moved);
                                        setModalites(next);
                                        await modalitesAPI.reorder(next.map((m, i) => ({ id: m.id, ordre: i })));
                                    }}>
                                    <span className="prog-drag-handle"><i className="fa-solid fa-grip-vertical" /></span>
                                    <code className="prog-code-inline">{item.code}</code>
                                    <span className="prog-drag-nom">{item.nom}</span>
                                    <span className="prog-drag-coeff">× {item.coefficient_htd} hTD</span>
                                    <label className="prog-modalite-flag" title="Compter dans les heures présentiel">
                                        <input type="checkbox" checked={!!item.is_presentiel}
                                            onChange={async e => {
                                                const val = e.target.checked;
                                                setModalites(prev => prev.map(m => m.id === item.id ? { ...m, is_presentiel: val } : m));
                                                await modalitesAPI.update(item.id, { is_presentiel: val });
                                            }} />
                                        <i className="fa-solid fa-chalkboard-user" /> Présentiel
                                    </label>
                                    <label className="prog-modalite-flag" title="Compter dans les heures autonomie">
                                        <input type="checkbox" checked={!!item.is_autonomie}
                                            onChange={async e => {
                                                const val = e.target.checked;
                                                setModalites(prev => prev.map(m => m.id === item.id ? { ...m, is_autonomie: val } : m));
                                                await modalitesAPI.update(item.id, { is_autonomie: val });
                                            }} />
                                        <i className="fa-solid fa-book-open-reader" /> Autonomie
                                    </label>
                                    <div className="prog-drag-actions">
                                        <button className="prog-action-btn prog-action-edit"
                                            onClick={() => setModal({ type: 'modalites', item })}>
                                            <i className="fa-solid fa-pen" />
                                        </button>
                                        <button className="prog-action-btn prog-action-delete"
                                            onClick={() => handleDelete('modalites', item.id)}>
                                            <i className="fa-solid fa-trash" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : tab === 'semestres' ? (
                        <table className="prog-table prog-table-sm">
                            <thead>
                                <tr>
                                    <th>Code semestre</th>
                                    <th>Sous-niveau</th>
                                    <th>Présentiel (h)</th>
                                    <th>Autonomie (h)</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {current.data.map(item => (
                                    <tr key={item.id}>
                                        <td><code className="prog-code-inline">{item.semestre}</code></td>
                                        <td>{item.sous_niveau_code ? `${item.sous_niveau_code} — ${item.sous_niveau_nom}` : item.sous_niveau_id}</td>
                                        <td>{item.heures_presentiel}</td>
                                        <td>{item.heures_autonomie}</td>
                                        <td className="prog-col-actions">
                                            <button className="prog-action-btn prog-action-edit"
                                                onClick={() => setModal({ type: tab, item })}>
                                                <i className="fa-solid fa-pen" />
                                            </button>
                                            <button className="prog-action-btn prog-action-delete"
                                                onClick={() => handleDelete(tab, item.id)}>
                                                <i className="fa-solid fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <table className="prog-table prog-table-sm">
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Libellé</th>
                                    <th>Couleur</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {current?.data.map(item => (
                                    <tr key={item.id} className={item.actif === false ? 'prog-row-inactive' : ''}>
                                        <td><code className="prog-code-inline">{item.code}</code></td>
                                        <td>{item.nom}</td>
                                        <td>
                                            <span className="prog-color-dot"
                                                style={{ background: item.couleur || '#6C757D' }} />
                                            {item.couleur}
                                        </td>
                                        <td className="prog-col-actions">
                                            <button className="prog-action-btn prog-action-edit"
                                                onClick={() => setModal({ type: tab, item })}>
                                                <i className="fa-solid fa-pen" />
                                            </button>
                                            <button className="prog-action-btn prog-action-delete"
                                                onClick={() => handleDelete(tab, item.id)}>
                                                <i className="fa-solid fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {modal && (
                <ModalRef
                    titre={tabs.find(t => t.key === modal.type)?.label || ''}
                    item={modal.item}
                    champs={CHAMPS[modal.type] || []}
                    onSave={handleSave}
                    onClose={() => { setModal(null); setErrSave(null); }}
                    saving={saving}
                    error={errSave}
                />
            )}

            {importOpen && (
                <ImportModal
                    titre={tabs.find(t => t.key === tab)?.label || 'Référentiels'}
                    champs={
                        tab === 'types'     ? [{ key: 'code', label: 'Code', required: true }, { key: 'nom', label: 'Libellé', required: true }, { key: 'couleur', label: 'Couleur' }, { key: 'ordre', label: 'Ordre' }] :
                        /* modalites */       [{ key: 'code', label: 'Code', required: true }, { key: 'nom', label: 'Libellé', required: true }, { key: 'coefficient_htd', label: 'Coeff hTD' }, { key: 'ordre', label: 'Ordre' }]
                    }
                    onImport={async (rows) => {
                        const importAnneeId = tab === 'types' ? anneeId : null;
                        const res = await importAPI.importer(
                            tab === 'types' ? 'types-cours' : 'modalites',
                            deptId, importAnneeId, rows
                        );
                        load();
                        return res.data;
                    }}
                    onClose={() => setImportOpen(false)}
                />
            )}
        </div>
    );
}

// ─── Maquette pédagogique ─────────────────────────────────────
function OngletMaquette({ config, anneeId, semestres = [], sousNiveaux = [], filtSems = new Set(), setFiltSems }) {
    const deptId = config?.departement?.id;
    const [loading, setLoading]           = useState(true);
    const [maquette, setMaquette]         = useState([]);
    const [modalites, setModalites]       = useState([]);
    const [collapsedSns,  setCollapsedSns]  = useState(new Set());
    const [collapsedSems, setCollapsedSems] = useState(new Set());
    const [collapsedUes,  setCollapsedUes]  = useState(new Set());
    const [editEcts,  setEditEcts]  = useState({});  // { [ueId]: val temp }
    const [editCoeff, setEditCoeff] = useState({});  // { [`${ueId}_${coursId}`]: val temp }
    const [maqSemWidth, setMaqSemWidth] = useState(() => parseInt(localStorage.getItem('maq_sem_width') || '100'));
    const isSingleSem = filtSems.size === 1;
    const dragUe    = useRef(null);
    const dragCours = useRef(null);
    const [dragOverUeId,     setDragOverUeId]     = useState(null);
    const [dragOverCoursKey, setDragOverCoursKey] = useState(null);

    const load = useCallback(async () => {
        if (!deptId || !anneeId) return;
        setLoading(true);
        try {
            const res = await ueAPI.getMaquette(deptId, anneeId);
            setMaquette(res.data.maquette || []);
            setModalites(res.data.modalites || []);
        } catch (e) {
            console.error('Erreur chargement maquette:', e);
        } finally {
            setLoading(false);
        }
    }, [deptId, anneeId]);

    useEffect(() => { load(); }, [load]);

    // Grouper par sous-niveau → semestre en préservant l'ordre SQL
    const snArr = [];
    const snIndex = {};
    const semIndex = {};
    maquette.forEach(ue => {
        const snKey = ue.sn_code || 'autre';
        if (snIndex[snKey] === undefined) {
            snIndex[snKey] = snArr.length;
            snArr.push({ sn_code: ue.sn_code, sn_nom: ue.sn_nom, sn_ordre: ue.sn_ordre, semArr: [] });
        }
        const snObj = snArr[snIndex[snKey]];
        const semKey = `${snKey}__${ue.semestre_config_id}`;
        if (semIndex[semKey] === undefined) {
            semIndex[semKey] = snObj.semArr.length;
            snObj.semArr.push({ semestre_config_id: ue.semestre_config_id, semestre_label: ue.semestre_label, ues: [] });
        }
        snObj.semArr[semIndex[semKey]].ues.push(ue);
    });

    const saveEcts = async (ueId, val) => {
        const num = parseFloat(val);
        if (isNaN(num) || num < 0) return;
        try {
            await ueAPI.update(ueId, { ects: num });
            setMaquette(prev => prev.map(u => u.ue_id === ueId ? { ...u, ects: num } : u));
        } catch (e) { alert('Erreur ECTS : ' + e.message); }
    };

    const saveCoeff = async (ueId, coursId, val) => {
        const num = parseFloat(val);
        if (isNaN(num) || num < 0) return;
        try {
            await ueAPI.saveCoursLocal(ueId, coursId, { coefficient: num });
            await load();
        } catch (e) { alert('Erreur coefficient : ' + e.message); }
    };

    const toggle = (setter, key) => setter(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const handleDropUe = useCallback((semConfigId, toUeId) => {
        const fromUeId = dragUe.current;
        setDragOverUeId(null);
        if (!fromUeId || fromUeId === toUeId) return;
        dragUe.current = null;
        setMaquette(prev => {
            const next = [...prev];
            const fi = next.findIndex(u => u.ue_id === fromUeId);
            const ti = next.findIndex(u => u.ue_id === toUeId);
            if (fi === -1 || ti === -1) return prev;
            const [moved] = next.splice(fi, 1);
            const ni = next.findIndex(u => u.ue_id === toUeId);
            next.splice(ni, 0, moved);
            const ordre = next
                .filter(u => u.semestre_config_id === semConfigId)
                .map((u, i) => ({ id: u.ue_id, ordre: i }));
            ueAPI.reorder(ordre).catch(() => load());
            return next;
        });
    }, [load]);

    const handleDropCours = useCallback((ueId, toPnId) => {
        const fromPnId = dragCours.current;
        setDragOverCoursKey(null);
        if (!fromPnId || fromPnId === toPnId) return;
        dragCours.current = null;
        setMaquette(prev => prev.map(ue => {
            if (ue.ue_id !== ueId) return ue;
            const cours = [...ue.cours];
            const fi = cours.findIndex(c => c.pn_id === fromPnId);
            const ti = cours.findIndex(c => c.pn_id === toPnId);
            if (fi === -1 || ti === -1) return ue;
            const [moved] = cours.splice(fi, 1);
            const ni = cours.findIndex(c => c.pn_id === toPnId);
            cours.splice(ni, 0, moved);
            const ordre = cours
                .filter(c => c.pn_id != null && c.type !== 'missing' && c.type_option !== 'parallele')
                .map((c, i) => ({ pn_id: c.pn_id, ordre: i }));
            ueAPI.reorderPnCours(ueId, ordre).catch(() => load());
            return { ...ue, cours };
        }));
    }, [load]);

    if (loading) return (
        <div className="prog-center" style={{ padding: 60 }}>
            <i className="fa-solid fa-spinner fa-spin prog-spinner" />
            <p>Chargement de la maquette...</p>
        </div>
    );

    if (snArr.length === 0) return (
        <div className="maq-page">
            <div className="maq-empty">
                <i className="fa-solid fa-table-cells-large" />
                <p>Aucune UE définie. Créez d'abord des UEs dans l'onglet Programme national.</p>
            </div>
        </div>
    );

    // Seulement les modalités marquées présentiel ou autonomie dans le référentiel
    const maqModalites = modalites.filter(m => m.is_presentiel || m.is_autonomie);

    // Filtrage par filtSems
    const snToShow = filtSems.size === 0
        ? snArr
        : snArr.filter(sn => sn.semArr.some(sem => filtSems.has(sem.semestre_config_id)));

    return (
        <div className="maq-page">
            {/* ── Toolbar avec filtres ── */}
            <div className="maq-toolbar">
                <FiltreDropdowns sousNiveaux={sousNiveaux} semestres={semestres} filtSems={filtSems} setFiltSems={setFiltSems || (() => {})} />
            </div>
            {snToShow.map(sn => {
                const snCollapsed = collapsedSns.has(sn.sn_code);
                return (
                    <div key={sn.sn_code} className="maq-sn-section">
                        {/* En-tête sous-niveau repliable */}
                        <div className="maq-sn-header" style={{ cursor: 'pointer' }}
                            onClick={() => toggle(setCollapsedSns, sn.sn_code)}>
                            <i className={`fa-solid ${snCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'} maq-sn-chevron`} />
                            <i className="fa-solid fa-graduation-cap" />
                            <span>{sn.sn_code}{sn.sn_nom ? ` — ${sn.sn_nom}` : ''}</span>
                        </div>

                        {/* Semestres côte à côte */}
                        {!snCollapsed && (
                            <div className="maq-sem-cols" style={isSingleSem ? { gridTemplateColumns: `${maqSemWidth}%` } : {}}>
                                {sn.semArr.filter(sem => filtSems.size === 0 || filtSems.has(sem.semestre_config_id)).map(sem => {
                                    const semCollapsed = collapsedSems.has(sem.semestre_config_id);
                                    // Totaux semestre (transversaux comptés une seule fois)
                                    const totalEcts = sem.ues.reduce((s, u) => s + (parseFloat(u.ects) || 0), 0);
                                    const totalParMod = {};
                                    maqModalites.forEach(m => { totalParMod[m.id] = 0; });
                                    const coursDejaComptes = new Set();
                                    sem.ues.forEach(u => u.cours.forEach(c => {
                                        if (!c.heuresParModalite) return;
                                        if (c.is_transversal && coursDejaComptes.has(c.cours_id)) return;
                                        if (c.cours_id) coursDejaComptes.add(c.cours_id);
                                        maqModalites.forEach(m => {
                                            totalParMod[m.id] += parseFloat(c.heuresParModalite[m.id]) || 0;
                                        });
                                    }));
                                    const totalH = maqModalites.reduce((s, m) => s + (totalParMod[m.id] || 0), 0);
                                    // Seulement les modalités avec au moins 1h dans ce semestre
                                    const semModalites = maqModalites.filter(m => (totalParMod[m.id] || 0) > 0);
                                    // Set de suivi des transversaux déjà affichés (1ère UE uniquement)
                                    const transversauxVus = new Set();

                                    return (
                                        <div key={sem.semestre_config_id} className="maq-sem-block">
                                            {/* En-tête semestre repliable */}
                                            <div className="maq-sem-title" style={{ cursor: 'pointer' }}
                                                onClick={() => toggle(setCollapsedSems, sem.semestre_config_id)}>
                                                <i className={`fa-solid ${semCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'} maq-sem-chevron`} />
                                                {sem.semestre_label}
                                                <span className="maq-sem-total-inline">
                                                    {totalH > 0 && <span className="maq-sem-total-h">{totalH}h</span>}
                                                    {totalEcts > 0 && <span className="maq-sem-total-ects">{totalEcts} ECTS</span>}
                                                </span>
                                                <span className="maq-sem-spacer" />
                                                {isSingleSem && (
                                                    <span className="maq-width-control" onClick={e => e.stopPropagation()} title="Largeur de la colonne">
                                                        <i className="fa-solid fa-left-right" style={{ fontSize: 10, color: '#9333ea' }} />
                                                        <input
                                                            type="number" min="20" max="100" step="5"
                                                            className="maq-width-input"
                                                            value={maqSemWidth}
                                                            onChange={e => setMaqSemWidth(parseInt(e.target.value) || 100)}
                                                            onBlur={e => localStorage.setItem('maq_sem_width', e.target.value)}
                                                        />
                                                        <span style={{ fontSize: 11, color: '#7c3aed' }}>%</span>
                                                    </span>
                                                )}
                                            </div>

                                            {!semCollapsed && (
                                                <div className="maq-sem-table-wrap">
                                                    <table className="maq-table">
                                                        <thead>
                                                            <tr>
                                                                <th className="maq-th-ue">UE / Ressource</th>
                                                                <th className="maq-th-nom">Nom</th>
                                                                {semModalites.map(m => (
                                                                    <th key={m.id} className="maq-th-h" title={m.nom}>{m.code}</th>
                                                                ))}
                                                                <th className="maq-th-htot">Total h</th>
                                                                <th className="maq-th-coeff">Coeff</th>
                                                                <th className="maq-th-ects">ECTS</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {sem.ues.map(ue => {
                                                                const ueCollapsed = collapsedUes.has(ue.ue_id);
                                                                const ueEcts      = editEcts[ue.ue_id] !== undefined ? editEcts[ue.ue_id] : ue.ects;
                                                                const localCours = ue.cours.filter(c => c.type === 'local');
                                                                const coursTriés = [...ue.cours].sort((a, b) => {
                                                                    const ao = a.pn_ordre ?? 9999;
                                                                    const bo = b.pn_ordre ?? 9999;
                                                                    if (ao !== bo) return ao - bo;
                                                                    return (a.pn_code || '').localeCompare(b.pn_code || '', undefined, { numeric: true, sensitivity: 'base' });
                                                                });

                                                                return (
                                                                    <React.Fragment key={ue.ue_id}>
                                                                        {/* Ligne UE */}
                                                                        <tr
                                                                            className={`maq-row-ue${dragOverUeId === ue.ue_id ? ' maq-row-drag-over' : ''}`}
                                                                            draggable
                                                                            onDragStart={() => { dragUe.current = ue.ue_id; }}
                                                                            onDragOver={e => { e.preventDefault(); setDragOverUeId(ue.ue_id); }}
                                                                            onDragLeave={() => setDragOverUeId(null)}
                                                                            onDragEnd={() => { dragUe.current = null; setDragOverUeId(null); }}
                                                                            onDrop={() => handleDropUe(sem.semestre_config_id, ue.ue_id)}
                                                                            onClick={() => toggle(setCollapsedUes, ue.ue_id)}
                                                                            style={{ cursor: 'pointer' }}
                                                                        >
                                                                            <td className="maq-cell-ue-code">
                                                                                <i className="fa-solid fa-grip-vertical maq-drag-grip" title="Glisser pour réordonner" />
                                                                                <i className={`fa-solid ${ueCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'} maq-chevron`} />
                                                                                <strong>{ue.ue_code}</strong>
                                                                                {ue.cours.length > 0 && (
                                                                                    <span className="maq-ue-count">{ue.cours.length}</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="maq-cell-ue-nom">{ue.ue_nom}</td>
                                                                            {semModalites.map(m => (
                                                                                <td key={m.id} className="maq-cell-h maq-cell-ue-h" />
                                                                            ))}
                                                                            <td className="maq-cell-htot maq-cell-ue-h" />
                                                                            <td className="maq-cell-coeff maq-cell-ue-h" />
                                                                            <td className="maq-cell-ects" onClick={e => e.stopPropagation()}>
                                                                                <input
                                                                                    type="number" min="0" step="1"
                                                                                    className="maq-ects-input"
                                                                                    value={ueEcts}
                                                                                    onChange={e => setEditEcts(p => ({ ...p, [ue.ue_id]: e.target.value }))}
                                                                                    onKeyDown={e => { if (e.key === 'Enter') { saveEcts(ue.ue_id, e.target.value); setEditEcts(p => { const n = { ...p }; delete n[ue.ue_id]; return n; }); e.target.blur(); } }}
                                                                                    onBlur={e => { saveEcts(ue.ue_id, e.target.value); setEditEcts(p => { const n = { ...p }; delete n[ue.ue_id]; return n; }); }}
                                                                                    title="ECTS (Entrée pour valider)"
                                                                                />
                                                                            </td>
                                                                        </tr>

                                                                        {/* Lignes cours */}
                                                                        {!ueCollapsed && (() => {
                                                                            // Séparer missing, normaux/option+, parallèles
                                                                            const missingItems = coursTriés.filter(c => c.type === 'missing');
                                                                            const coursNormaux = coursTriés.filter(c => c.type !== 'missing' && c.type_option !== 'parallele');
                                                                            const coursPar     = coursTriés.filter(c => c.type !== 'missing' && c.type_option === 'parallele');
                                                                            // Grouper parallèles par pn_id (tous les cours mappés au même PN forment une ligne)
                                                                            const parByPn = {};
                                                                            const parPnOrder = [];
                                                                            coursPar.forEach(c => {
                                                                                const k = String(c.pn_id ?? c.cours_ordre ?? 0);
                                                                                if (!parByPn[k]) { parByPn[k] = []; parPnOrder.push(k); }
                                                                                parByPn[k].push(c);
                                                                            });
                                                                            const lignesPar = parPnOrder.map(k => [k, parByPn[k]]);

                                                                            return (
                                                                                <>
                                                                                    {missingItems.map(c => (
                                                                                        <tr key={`${ue.ue_id}_miss_${c.pn_id}`} className="maq-row-warning">
                                                                                            <td className="maq-cell-code">
                                                                                                <span className="maq-warn-icon" title="Aucun cours local associé">⚠</span>
                                                                                                <code className="prog-code-inline maq-code-missing">{c.pn_code}</code>
                                                                                            </td>
                                                                                            <td className="maq-cell-nom maq-nom-missing" title={c.pn_nom}>{c.pn_nom}</td>
                                                                                            {semModalites.map(m => <td key={m.id} className="maq-cell-h">—</td>)}
                                                                                            <td className="maq-cell-htot">—</td>
                                                                                            <td className="maq-cell-coeff">—</td>
                                                                                            <td className="maq-cell-ects" />
                                                                                        </tr>
                                                                                    ))}
                                                                                    {coursNormaux.map(c => {
                                                                                        const coeffKey = `${ue.ue_id}_${c.cours_id}`;
                                                                                        const coeffVal = editCoeff[coeffKey] !== undefined ? editCoeff[coeffKey] : c.coefficient;
                                                                                        const cParMod    = c.heuresParModalite || {};
                                                                                        const showHeures = !c.is_transversal || !transversauxVus.has(c.pn_id);
                                                                                        if (c.is_transversal) transversauxVus.add(c.pn_id);
                                                                                        const cTotalH    = showHeures ? semModalites.reduce((s, m) => s + (parseFloat(cParMod[m.id]) || 0), 0) : 0;
                                                                                        const nonConf  = c.statut && c.statut !== 'conforme';
                                                                                        return (
                                                                                            <tr key={`${ue.ue_id}_${c.cours_id}`}
                                                                                                className={`maq-row-cours${c.is_transversal ? ' maq-row-transversal' : ''}${nonConf ? ' maq-row-insuffisant' : ''}${dragOverCoursKey === `${ue.ue_id}_${c.pn_id}` ? ' maq-row-drag-over' : ''}`}
                                                                                                draggable
                                                                                                onDragStart={() => { dragCours.current = c.pn_id; }}
                                                                                                onDragOver={e => { e.preventDefault(); setDragOverCoursKey(`${ue.ue_id}_${c.pn_id}`); }}
                                                                                                onDragLeave={() => setDragOverCoursKey(null)}
                                                                                                onDragEnd={() => { dragCours.current = null; setDragOverCoursKey(null); }}
                                                                                                onDrop={() => handleDropCours(ue.ue_id, c.pn_id)}
                                                                                            >
                                                                                                <td className="maq-cell-code">
                                                                                                    <i className="fa-solid fa-grip-vertical maq-drag-grip maq-drag-grip--cours" title="Glisser pour réordonner" />
                                                                                                    {nonConf && <span className="maq-warn-icon" title={`Conformité : ${c.statut}`}>⚠</span>}
                                                                                                    {c.is_transversal && !nonConf && <span className="maq-transversal-icon" title="Transversal">T</span>}
                                                                                                    {c.type_option === 'complementaire' && <span className="maq-badge-plus" title="Option+">⊕</span>}
                                                                                                    <code className="prog-code-inline">{c.cours_code}</code>
                                                                                                </td>
                                                                                                <td className="maq-cell-nom" title={c.cours_nom}>{c.cours_nom}</td>
                                                                                                {semModalites.map(m => {
                                                                                                    const h = showHeures ? (parseFloat(cParMod[m.id]) || 0) : 0;
                                                                                                    return (
                                                                                                        <td key={m.id} className="maq-cell-h">
                                                                                                            {h > 0 ? `${h}h` : ''}
                                                                                                        </td>
                                                                                                    );
                                                                                                })}
                                                                                                <td className="maq-cell-htot">{cTotalH > 0 ? `${cTotalH}h` : ''}</td>
                                                                                                <td className="maq-cell-coeff">
                                                                                                    <input
                                                                                                        type="number" min="0" step="0.5"
                                                                                                        className="maq-coeff-input"
                                                                                                        value={coeffVal}
                                                                                                        onChange={e => setEditCoeff(p => ({ ...p, [coeffKey]: e.target.value }))}
                                                                                                        onKeyDown={e => { if (e.key === 'Enter') { saveCoeff(ue.ue_id, c.cours_id, e.target.value); setEditCoeff(p => { const n = { ...p }; delete n[coeffKey]; return n; }); e.target.blur(); } }}
                                                                                                        onBlur={e => { saveCoeff(ue.ue_id, c.cours_id, e.target.value); setEditCoeff(p => { const n = { ...p }; delete n[coeffKey]; return n; }); }}
                                                                                                        title="Coefficient (Entrée pour valider)"
                                                                                                    />
                                                                                                </td>
                                                                                                <td className="maq-cell-ects" />
                                                                                            </tr>
                                                                                        );
                                                                                    })}
                                                                                    {lignesPar.map(([ordre, ligneItems], ligneIdx) => {
                                                                                        const coeffKeyPar = `${ue.ue_id}_${ligneItems[0]?.cours_id}`;
                                                                                        const coeffValPar = editCoeff[coeffKeyPar] !== undefined
                                                                                            ? editCoeff[coeffKeyPar]
                                                                                            : (ligneItems[0]?.coefficient ?? 1);
                                                                                        return (
                                                                                            <tr key={`par-ligne-${ordre}`} className="maq-row-parallele">
                                                                                                <td className="maq-cell-code" style={{ verticalAlign: 'middle' }}>
                                                                                                    <i className="fa-solid fa-right-left maq-par-icon" title="Option parallèle" />
                                                                                                    {ligneItems[0]?.is_transversal && <span className="maq-transversal-icon" title="Transversal">T</span>}
                                                                                                    <span className="maq-par-ligne-num">Groupe option {ligneIdx + 1}</span>
                                                                                                </td>
                                                                                                <td className="maq-cell-par-familles" colSpan={semModalites.length + 2}>
                                                                                                    <div className="maq-par-familles-row">
                                                                                                        {ligneItems.map(c => {
                                                                                                            const cParMod = c.heuresParModalite || {};
                                                                                                            const cTotalH = semModalites.reduce((s, m) => s + (parseFloat(cParMod[m.id]) || 0), 0);
                                                                                                            return (
                                                                                                                <div key={c.cours_id} className="maq-par-famille-cell">
                                                                                                                    <div className="maq-par-famille-header">
                                                                                                                        <span className="maq-par-famille-nom">{c.famille_option_code || c.famille_option_nom || '?'}</span>
                                                                                                                    </div>
                                                                                                                    <div className="maq-par-famille-body">
                                                                                                                        <code className="prog-code-inline">{c.cours_code}</code>
                                                                                                                        <span className="maq-par-cours-nom">{c.cours_nom}</span>
                                                                                                                        <div className="maq-par-heures">
                                                                                                                            {semModalites.map(m => {
                                                                                                                                const h = parseFloat(cParMod[m.id]) || 0;
                                                                                                                                return h > 0 ? <span key={m.id} className="maq-par-h-chip">{m.code} {h}h</span> : null;
                                                                                                                            })}
                                                                                                                            {cTotalH > 0 && <strong className="maq-par-h-total">{cTotalH}h</strong>}
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            );
                                                                                                        })}
                                                                                                    </div>
                                                                                                </td>
                                                                                                <td className="maq-cell-coeff">
                                                                                                    <input
                                                                                                        type="number" min="0" step="0.5"
                                                                                                        className="maq-coeff-input"
                                                                                                        value={coeffValPar}
                                                                                                        onChange={e => {
                                                                                                            const v = e.target.value;
                                                                                                            setEditCoeff(p => {
                                                                                                                const n = { ...p };
                                                                                                                ligneItems.forEach(c => { n[`${ue.ue_id}_${c.cours_id}`] = v; });
                                                                                                                return n;
                                                                                                            });
                                                                                                        }}
                                                                                                        onBlur={e => {
                                                                                                            const v = e.target.value;
                                                                                                            ligneItems.forEach(c => saveCoeff(ue.ue_id, c.cours_id, v));
                                                                                                            setEditCoeff(p => {
                                                                                                                const n = { ...p };
                                                                                                                ligneItems.forEach(c => { delete n[`${ue.ue_id}_${c.cours_id}`]; });
                                                                                                                return n;
                                                                                                            });
                                                                                                        }}
                                                                                                        onKeyDown={e => { if (e.key === 'Enter') { e.target.blur(); } }}
                                                                                                        title="Coefficient partagé (Entrée pour valider)"
                                                                                                    />
                                                                                                </td>
                                                                                                <td className="maq-cell-ects" />
                                                                                            </tr>
                                                                                        );
                                                                                    })}
                                                                                </>
                                                                            );
                                                                        })()}

                                                                        {/* UE vide */}
                                                                        {!ueCollapsed && ue.cours.length === 0 && (
                                                                            <tr className="maq-row-empty">
                                                                                <td colSpan={4 + semModalites.length} className="maq-cell-empty">
                                                                                    Aucun cours dans cette UE
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </React.Fragment>
                                                                );
                                                            })}

                                                            {/* Ligne total par modalité */}
                                                            <tr className="maq-row-total">
                                                                <td colSpan={2} className="maq-cell-total-label">
                                                                    <strong>Total par modalité</strong>
                                                                </td>
                                                                {semModalites.map(m => (
                                                                    <td key={m.id} className="maq-cell-total-h">
                                                                        <strong>{totalParMod[m.id] > 0 ? `${totalParMod[m.id]}h` : '—'}</strong>
                                                                    </td>
                                                                ))}
                                                                <td className="maq-cell-total-h"><strong>{totalH > 0 ? `${totalH}h` : '—'}</strong></td>
                                                                <td /><td />
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ─── Composant principal ──────────────────────────────────────
const PROGRAMME_TAB_KEY = 'programme_onglet_actif';
const VALID_TABS = ['cours','pn','conformite','maquette','referentiels'];

function Programme({ config, anneeId, annees }) {
    const [onglet, setOngletState] = useState(() => {
        const saved = localStorage.getItem(PROGRAMME_TAB_KEY);
        return VALID_TABS.includes(saved) ? saved : 'pn';
    });
    const setOnglet = (tab) => {
        setOngletState(tab);
        localStorage.setItem(PROGRAMME_TAB_KEY, tab);
    };
    const [semestres, setSemestres] = useState([]);
    const [typesCour, setTypesCour] = useState([]);
    const [modalites, setModalites] = useState([]);
    const [famillesOptions, setFamillesOptions] = useState([]);
    const [enseignants, setEnseignants] = useState([]);
    const [sousNiveaux, setSousNiveaux] = useState([]);
    const [filtSems, setFiltSems] = useState(new Set());
    const [coursSousOnglet, setCoursSousOnglet] = useState('cours'); // 'cours'|'option_plus'|'option_par'|'tout'
    const [loadingRef, setLoadingRef]   = useState(true);
    const [refRefusees, setRefRefusees] = useState([]); // listes non chargées : [{ libelle, module, statut }]
    const initialized = React.useRef(false);

    const deptId = config?.departement?.id;
    const topbarColor = getModuleTopbarColor('programme');

    // Recharger tous les référentiels à chaque changement d'onglet
    // (spinner uniquement au premier chargement, silencieux ensuite).
    // Chaque liste se charge seule : un refus (ex. 403 sur /api/enseignants, qui relève
    // du module Enseignants) n'empêche plus les autres de s'afficher → bandeau `refRefusees`.
    const loadRef = useCallback(async () => {
        if (!deptId || !anneeId) return;
        if (!initialized.current) setLoadingRef(true);
        try {
            const listes = [
                { libelle: 'des types de cours',        appel: () => typesCourAPI.getAll(deptId, anneeId),        poser: d => setTypesCour(d.types_cours || []) },
                { libelle: 'des modalités',             appel: () => modalitesAPI.getAll(deptId),                 poser: d => setModalites(d.modalites || []) },
                { libelle: "des familles d'options",    appel: () => famillesOptionsAPI.getAll(deptId, anneeId),  poser: d => setFamillesOptions(d.familles || []) },
                { libelle: 'des enseignants', module: 'Enseignants',
                  appel: () => enseignantsAPI.getAll(deptId, anneeId),       poser: d => setEnseignants(d.enseignants?.filter(e => e.actif) || []) },
                { libelle: 'des semestres',             appel: () => semestresConfigAPI.getAll(deptId, anneeId),  poser: d => setSemestres(d.semestres || []) },
                { libelle: 'des sous-niveaux',          appel: () => sousNiveauxAPI.getSousNiveaux(deptId),       poser: d => setSousNiveaux(d.sous_niveaux || []) },
            ];
            const resultats = await Promise.allSettled(listes.map(l => l.appel()));
            const refusees = [];
            resultats.forEach((r, i) => {
                if (r.status === 'fulfilled') { listes[i].poser(r.value.data || {}); return; }
                listes[i].poser({});
                refusees.push({ libelle: listes[i].libelle, module: listes[i].module, statut: r.reason?.response?.status });
            });
            setRefRefusees(refusees);
            initialized.current = true;
        } finally { setLoadingRef(false); }
    }, [deptId, anneeId]);

    useEffect(() => { loadRef(); }, [loadRef, onglet]);

    const ONGLETS = [
        { key: 'pn',          label: 'Programme national',  icon: 'fa-scroll' },
        { key: 'cours',       label: 'Cours',               icon: 'fa-book-open' },
        { key: 'conformite',  label: 'Conformité',          icon: 'fa-scale-balanced' },
        { key: 'maquette',    label: 'Maquette',            icon: 'fa-table' },
        { key: 'referentiels',label: 'Référentiels',        icon: 'fa-sliders' },
    ];

    return (
        <div className="prog-page">
            {/* Topbar */}
            <div className="prog-topbar"
                style={{ background: `linear-gradient(135deg, ${topbarColor} 0%, ${topbarColor}cc 100%)` }}>
                <div className="prog-topbar-left">
                    <i className="fa-solid fa-book-open prog-topbar-icon" />
                    <div>
                        <div className="prog-topbar-title">Programme de formation</div>
                        <div className="prog-topbar-sub">
                            {config?.departement?.nom || 'Département'} · {typesCour.length} types · {modalites.length} modalités
                        </div>
                    </div>
                </div>

                {/* Onglets dans le topbar */}
                <div className="prog-topbar-tabs">
                    {ONGLETS.map(o => (
                        <button key={o.key}
                            className={`prog-topbar-tab ${onglet === o.key ? 'active' : ''}`}
                            onClick={() => setOnglet(o.key)}>
                            <i className={`fa-solid ${o.icon}`} /> {o.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Listes refusées ou en échec : l'écran reste utilisable */}
            {!loadingRef && refRefusees.length > 0 && (
                <div className="prog-ref-refusees">
                    {refRefusees.map(r => (
                        <div key={r.libelle} className="prog-alert prog-alert-avert" data-liste-refusee={r.libelle}>
                            <i className="fa-solid fa-triangle-exclamation" />
                            {r.statut === 403 && r.module === 'Enseignants' ? (
                                <span>
                                    La liste des enseignants n'a pas pu être chargée : votre compte n'a pas le droit
                                    « consulter » sur le module Enseignants. Le reste du Programme fonctionne ; seul l'ajout
                                    d'un enseignant à un cours est indisponible. Demandez ce droit à l'administrateur du département.
                                </span>
                            ) : r.statut === 403 ? (
                                <span>La liste {r.libelle} n'a pas pu être chargée : votre compte n'a pas le droit de la consulter.</span>
                            ) : (
                                <span>La liste {r.libelle} n'a pas pu être chargée{r.statut ? ` (erreur ${r.statut})` : ''}. Rechargez la page ; si cela persiste, signalez-le.</span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Contenu */}
            {loadingRef ? (
                <div className="prog-center" style={{ padding: 60 }}>
                    <i className="fa-solid fa-spinner fa-spin prog-spinner" />
                    <p>Chargement des référentiels...</p>
                </div>
            ) : (
                <>
                    {onglet === 'cours'        && <OngletCours config={config} anneeId={anneeId} annees={annees} sousNiveaux={sousNiveaux} semestres={semestres} typesCour={typesCour} modalites={modalites} famillesOptions={famillesOptions} enseignants={enseignants} onFamillesChange={loadRef} filtSems={filtSems} setFiltSems={setFiltSems} sousOnglet={coursSousOnglet} setSousOnglet={setCoursSousOnglet} />}
                    {onglet === 'pn'           && <OngletPN config={config} anneeId={anneeId} annees={annees} modalites={modalites} sousNiveaux={sousNiveaux} semestres={semestres} filtSems={filtSems} setFiltSems={setFiltSems} />}
                    {onglet === 'conformite'   && <OngletConformite config={config} anneeId={anneeId} semestres={semestres} sousNiveaux={sousNiveaux} modalites={modalites} enseignants={enseignants} typesCour={typesCour} famillesOptions={famillesOptions} filtSems={filtSems} setFiltSems={setFiltSems} />}
                    {onglet === 'maquette'     && <OngletMaquette config={config} anneeId={anneeId} semestres={semestres} sousNiveaux={sousNiveaux} filtSems={filtSems} setFiltSems={setFiltSems} />}
                    {onglet === 'referentiels' && <OngletReferentiels config={config} anneeId={anneeId} />}
                </>
            )}
        </div>
    );
}

export default Programme;
